import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axios from 'axios';
import {
  Upload,
  Download,
  Plus,
  Trash2,
  Save,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  FileSpreadsheet,
  Search,
  RefreshCw,
  Filter,
  ChevronDown,
  ChevronUp,
  Building2,
  Users,
  UserPlus,
  Calendar,
  Edit,
  Truck,
  Grid,
  List,
  History,
  ChevronLeft,
  ChevronRight,
  Paperclip,
  Edit3,
  AlertTriangle,
  MessageSquare
} from 'lucide-react';
import api from '../api/config';
import '../styles/MatrizExcelPaperBoatFinal.css';
import '../styles/ModalPaperBoat.css';
import '../styles/SelectDropdownFix.css';
import DocumentPanel from './documents/DocumentPanel';
import DocumentUploadModal from './documents/DocumentUploadModal';
import ModalDocumentsTab from './documents/ModalDocumentsTab';
import ObservationsPanel from './ObservationsPanel';

// Memoized Card Component para optimizar renderizado
const MatrizCard = memo(({
  row,
  clienteNombre,
  proveedorNombre,
  isSelected,
  isNewRow,
  collapsedSections,
  toggleSection,
  onSelectionChange
}) => {
  return (
    <div className={`matriz-card ${isSelected ? 'selected' : ''} ${isNewRow ? 'new-row-highlight' : ''}`}>
      {/* Card Header - ESTILO PAPER BOAT */}
      <div className="card-header">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelectionChange(row.id, e.target.checked);
          }}
        />
        <div className="card-header-info">
          <h3>Item #{row.item}</h3>
          <span className="card-cliente">{clienteNombre}</span>
        </div>
        <span className="card-badge">Orden</span>
      </div>

      {/* Card Body */}
      <div className="card-body">
        {/* Sección: Información del Cliente */}
        <div className="card-section section-cliente">
          <h4
            onClick={() => toggleSection(row.id, 'cliente')}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {collapsedSections[row.id]?.cliente ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronUp size={16} />
            )}
            Información del Cliente
          </h4>
          {!collapsedSections[row.id]?.cliente && (
          <div className="card-grid">
            <div className="card-field">
              <label>Cliente</label>
              <span className="highlight">{clienteNombre}</span>
            </div>
            <div className="card-field">
              <label>Cotización</label>
              <span>{row.cotizacion}</span>
            </div>
            <div className="card-field">
              <label>Orden Compra</label>
              <span>{row.p_order}</span>
            </div>
            <div className="card-field">
              <label>Item 2</label>
              <span>{row.item2}</span>
            </div>
            <div className="card-field full-width">
              <label>Descripción</label>
              <span>{row.descripcion}</span>
            </div>
            <div className="card-field">
              <label>Part Number</label>
              <span>{row.part_number}</span>
            </div>
            <div className="card-field">
              <label>C.I.N.</label>
              <span>{row.cin}</span>
            </div>
            <div className="card-field">
              <label>Fecha Entrega</label>
              <span>{row.delivery_date}</span>
            </div>
            <div className="card-field">
              <label>Entrega Real</label>
              <span>{row.real_delivery}</span>
            </div>
            <div className="card-field">
              <label>Cantidad</label>
              <span>{row.cantidad}</span>
            </div>
            <div className="card-field">
              <label>Unidad</label>
              <span>{row.unidad}</span>
            </div>
          </div>
          )}
        </div>

        {/* Sección: Información Financiera */}
        <div className="card-section section-financiera">
          <h4
            onClick={() => toggleSection(row.id, 'financiera')}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {collapsedSections[row.id]?.financiera ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronUp size={16} />
            )}
            Información Financiera
          </h4>
          {!collapsedSections[row.id]?.financiera && (
          <div className="card-grid">
            <div className="card-field">
              <label>P.U. CLP</label>
              <span className="highlight-success">
                ${parseFloat(row.pu_clp || 0).toLocaleString('es-CL')}
              </span>
            </div>
            <div className="card-field">
              <label>Total Neto</label>
              <span className="highlight-success">
                ${parseFloat(row.total_neto || 0).toLocaleString('es-CL')}
              </span>
            </div>
            <div className="card-field">
              <label>Guía</label>
              <span>{row.guia}</span>
            </div>
            <div className="card-field">
              <label>Factura</label>
              <span>{row.factura}</span>
            </div>
            <div className="card-field">
              <label>Emisión</label>
              <span>{row.emision_fact}</span>
            </div>
            <div className="card-field">
              <label>Vencimiento</label>
              <span>{row.vencimiento}</span>
            </div>
            <div className="card-field">
              <label>Fecha Pago</label>
              <span>{row.fecha_pago}</span>
            </div>
            <div className="card-field">
              <label>Pronto Pago</label>
              <span>{row.pronto_pago}</span>
            </div>
            <div className="card-field">
              <label>Utilidad Factor</label>
              <span>{row.utilidad_factor}</span>
            </div>
          </div>
          )}
        </div>

        {/* Sección: Información del Proveedor */}
        <div className="card-section section-proveedor">
          <h4
            onClick={() => toggleSection(row.id, 'proveedor')}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {collapsedSections[row.id]?.proveedor ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronUp size={16} />
            )}
            Información del Proveedor
          </h4>
          {!collapsedSections[row.id]?.proveedor && (
          <div className="card-grid">
            <div className="card-field">
              <label>Proveedor</label>
              <span className="highlight">{proveedorNombre}</span>
            </div>
            <div className="card-field">
              <label>OC Proveedor</label>
              <span>{row.oc_sobek}</span>
            </div>
            <div className="card-field">
              <label>P.U. Proveedor</label>
              <span className="highlight-warning">
                ${parseFloat(row.pu_proveedor || 0).toLocaleString('es-CL')}
              </span>
            </div>
            <div className="card-field">
              <label>Total Proveedor</label>
              <span className="highlight-warning">
                ${parseFloat(row.total_proveedor || 0).toLocaleString('es-CL')}
              </span>
            </div>
            <div className="card-field">
              <label>Factura Proveedor</label>
              <span>{row.factura_proveedor}</span>
            </div>
            <div className="card-field">
              <label>Emisión</label>
              <span>{row.emision_prov}</span>
            </div>
            <div className="card-field">
              <label>Vencimiento</label>
              <span>{row.vencimiento_prov}</span>
            </div>
            <div className="card-field">
              <label>Fecha Pago</label>
              <span>{row.fecha_pago_prov}</span>
            </div>
          </div>
          )}
        </div>

        {/* Sección: Utilidad */}
        <div className="card-section section-utilidad">
          <h4
            onClick={() => toggleSection(row.id, 'utilidad')}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {collapsedSections[row.id]?.utilidad ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronUp size={16} />
            )}
            Utilidad del Negocio
          </h4>
          {!collapsedSections[row.id]?.utilidad && (
          <div className="card-grid">
            <div className="card-field full-width">
              <label>Utilidad del Negocio</label>
              <span className={`highlight-profit ${(() => {
                if (row.u_del_negocio === null || row.u_del_negocio === undefined) return '';
                const neto = parseFloat(row.total_neto);
                const proveedor = parseFloat(row.total_proveedor);
                if (isNaN(neto) || neto <= 0 || isNaN(proveedor) || proveedor <= 0) return '';
                return parseFloat(row.u_del_negocio) >= 0 ? 'positive' : 'negative';
              })()}`}>
                {(() => {
                  if (row.u_del_negocio === null || row.u_del_negocio === undefined) return '-';
                  const neto = parseFloat(row.total_neto);
                  const proveedor = parseFloat(row.total_proveedor);
                  if (isNaN(neto) || neto <= 0 || isNaN(proveedor) || proveedor <= 0) return '-';
                  return `$${parseFloat(row.u_del_negocio).toLocaleString('es-CL')}`;
                })()}
              </span>
            </div>
            <div className="card-field full-width" style={{ cursor: 'pointer' }}
              onClick={() => setObsPanel({ itemNumber: row.item, itemData: { cliente: getClienteNombre(row.cliente), descripcion: row.descripcion } })}>
              <label>Observaciones</label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MessageSquare size={14} /> {obsCounts[row.item] || 0} comentarios
              </span>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Establecer displayName para debugging
MatrizCard.displayName = 'MatrizCard';

// Mapeo de campos del maestro a tipos de documento
const FIELD_DOC_TYPE_MAP = {
  cotizacion: 'cotizacion',
  p_order: 'orden_compra_cliente',
  oc_sobek: 'orden_compra_proveedor',
  guia: 'guia_despacho',
  factura: 'factura_cliente',
  factura_proveedor: 'factura_proveedor'
};

const MULTI_DOC_TYPES = new Set([
  'guia_despacho', 'factura_cliente', 'factura_proveedor'
]);

const DOC_TYPE_LABELS_SHORT = {
  cotizacion: 'Cotización',
  orden_compra_cliente: 'OC Cliente',
  orden_compra_proveedor: 'OC Proveedor',
  guia_despacho: 'Guía',
  factura_cliente: 'Factura',
  factura_proveedor: 'Fact. Prov.'
};

const MatrizExcelPaperBoatFinal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [activeTab, setActiveTab] = useState('etapa1');
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [searchClient, setSearchClient] = useState('');
  const [searchSupplier, setSearchSupplier] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(250);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [newClientsMap, setNewClientsMap] = useState({});
  const [newSuppliersMap, setNewSuppliersMap] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [overwriteMode, setOverwriteMode] = useState('overwrite'); // 'overwrite', 'skip', 'ask'
  const [importStep, setImportStep] = useState('uploading'); // 'uploading', 'preview', 'confirming', 'completed'
  const [pendingImportData, setPendingImportData] = useState(null);
  const [datosAnteriores, setDatosAnteriores] = useState([]);
  const [activeDateField, setActiveDateField] = useState(null);
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [isRangeMode, setIsRangeMode] = useState(false);
  const [originalDateStart, setOriginalDateStart] = useState('');
  const [originalDateEnd, setOriginalDateEnd] = useState('');
  const [originalIsRangeMode, setOriginalIsRangeMode] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [searchUnit, setSearchUnit] = useState('');
  const [availableUnits, setAvailableUnits] = useState([
    'CU', 'ROLLO', 'MTS', 'UN', 'PZA', 'KG', 'GR', 'TON', 'LT', 'ML', 'GAL',
    'M2', 'M3', 'CM', 'MM', 'CAJA', 'PAQUETE', 'SET', 'PAR',
    'BOLSA', 'LATA', 'BOTELLA', 'BALDE', 'TAMBOR', 'HR', 'DIA', 'MES'
  ]);
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [showItem2Dropdown, setShowItem2Dropdown] = useState(false);
  const [item2Options] = useState(
    Array.from({ length: 50 }, (_, i) => (i + 1) * 10)
  ); // Genera [10, 20, 30, ... 500]

  // Estados para dropdowns en edición inline de la tabla principal
  const [inlineClientDropdown, setInlineClientDropdown] = useState(null);
  const [inlineSupplierDropdown, setInlineSupplierDropdown] = useState(null);
  const [inlineUnitDropdown, setInlineUnitDropdown] = useState(null);
  const [inlineItem2Dropdown, setInlineItem2Dropdown] = useState(null);
  const [inlineItem2Search, setInlineItem2Search] = useState('');
  const [inlineSearchClient, setInlineSearchClient] = useState('');
  const [inlineSearchSupplier, setInlineSearchSupplier] = useState('');
  const [inlineSearchUnit, setInlineSearchUnit] = useState('');
  const [inlineDatePicker, setInlineDatePicker] = useState(null);
  const [inlineDateRangeStart, setInlineDateRangeStart] = useState('');
  const [inlineDateRangeEnd, setInlineDateRangeEnd] = useState('');
  const [inlineIsRangeMode, setInlineIsRangeMode] = useState(false);
  const [inlineDateRange, setInlineDateRange] = useState(null);

  // Estados para sistema de filtros
  const [showFiltersMenu, setShowFiltersMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [availableFilterValues, setAvailableFilterValues] = useState({});
  const [searchFilters, setSearchFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({
    fecha_entrega_start: '',
    fecha_entrega_end: '',
    entrega_real_start: '',
    entrega_real_end: '',
    emision_start: '',
    emision_end: '',
    vencimiento_start: '',
    vencimiento_end: '',
    fecha_pago_start: '',
    fecha_pago_end: ''
  });
  const filtersMenuRef = useRef(null);
  const filterButtonRef = useRef(null);
  const [inlineTempStartDate, setInlineTempStartDate] = useState('');
  const [inlineTempEndDate, setInlineTempEndDate] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [originalClientsMap, setOriginalClientsMap] = useState({});
  const [originalSuppliersMap, setOriginalSuppliersMap] = useState({});
  const [collaborators, setCollaborators] = useState([]);
  const [itemCollaborators, setItemCollaborators] = useState({});
  const [cellLastEditor, setCellLastEditor] = useState({});
  const [showCollaborators, setShowCollaborators] = useState(true);
  const [changesHistory, setChangesHistory] = useState([]);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(-1);
  const [highlightedCell, setHighlightedCell] = useState(null);
  const isNavigatingRef = useRef(false);
  const [isHistoryMode, setIsHistoryMode] = useState(false); // Modo historial activo
  const [currentHistoryChange, setCurrentHistoryChange] = useState(null); // Cambio actual en modo historial
  const [showHistoryBanner, setShowHistoryBanner] = useState(true); // Mostrar banner de modo historial

  // Estados para navegación de datos incompletos
  const [isIncompleteMode, setIsIncompleteMode] = useState(false); // Modo datos incompletos activo
  const [currentIncompleteIndex, setCurrentIncompleteIndex] = useState(-1); // Índice actual en lista de incompletos
  const [incompleteRows, setIncompleteRows] = useState([]); // Lista de filas con datos incompletos
  const incompleteTimerRef = useRef(null); // Timer para evitar race condition al cerrar

  // Estados para controlar visibilidad de badges (con localStorage)
  const [showImporterBadges, setShowImporterBadges] = useState(() => {
    const saved = localStorage.getItem('maestro_show_importer_badges');
    return saved !== null ? JSON.parse(saved) : true; // Por defecto visibles
  });
  const [showEditorBadges, setShowEditorBadges] = useState(() => {
    const saved = localStorage.getItem('maestro_show_editor_badges');
    return saved !== null ? JSON.parse(saved) : true; // Por defecto visibles
  });

  // Optimización: Crear un Set de IDs seleccionados para búsqueda O(1)
  const selectedRowsSet = useMemo(() => new Set(selectedRows), [selectedRows]);

  // Estado para el modo de vista: tabla o cards
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'

  // Filtros de año y mes (aplica a tabla + stats)
  const [statsYear, setStatsYear] = useState(String(new Date().getFullYear()));
  const [statsMonths, setStatsMonths] = useState([]);

  // Estado para secciones colapsadas en cards (por ID de row)
  const [collapsedSections, setCollapsedSections] = useState({});

  // Estado para rastrear la fila recién agregada (para efecto visual)
  const [newRowId, setNewRowId] = useState(null);

  // Estado para el popup de badge de colaborador
  const [badgePopup, setBadgePopup] = useState(null); // { itemNumber, colKey, user, isEditor, position }

  // Estados para observaciones
  const [obsPanel, setObsPanel] = useState(null); // { itemNumber, itemData } or null
  const [obsCounts, setObsCounts] = useState({}); // { item_number: count }

  // Estados para documentos
  const [documentCounts, setDocumentCounts] = useState({}); // { item_number: count }
  const [docCountsByType, setDocCountsByType] = useState({}); // { "1_cotizacion": 2, ... }
  const [docPanelItem, setDocPanelItem] = useState(null); // item number to show panel for
  const [showFieldUpload, setShowFieldUpload] = useState(false); // upload modal from field
  const [fieldUploadDocType, setFieldUploadDocType] = useState(''); // doc type for field upload
  const [modalItemDocs, setModalItemDocs] = useState({}); // { doc_type: [docs] } for current modal item
  const [cellDocPopover, setCellDocPopover] = useState(null); // { itemNumber, docType, position, docs, row }
  const [cellDocLoading, setCellDocLoading] = useState(false);
  const [fieldUploadContext, setFieldUploadContext] = useState(null); // { itemNumber, row, replaceDocIds }
  const [inlineMultiDocEdit, setInlineMultiDocEdit] = useState(null); // { cellId, docs, editingDocId }
  const [inlineMultiDocLoading, setInlineMultiDocLoading] = useState(false);
  const pendingDocUploadsRef = useRef([]); // track doc IDs uploaded this session, cleared on save


  // Función para toggle de secciones - Optimizada con useCallback
  const toggleSection = useCallback((rowId, sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [sectionName]: !prev[rowId]?.[sectionName]
      }
    }));
  }, []);

  // Handler para selección de cards - Optimizada con useCallback
  const handleCardSelection = useCallback((rowId, isChecked) => {
    if (isChecked) {
      setSelectedRows(prev => [...prev, rowId]);
    } else {
      setSelectedRows(prev => prev.filter(id => id !== rowId));
    }
  }, []);

  const fileInputRef = useRef();
  const clientDropdownRef = useRef();
  const supplierDropdownRef = useRef();
  const unitDropdownRef = useRef();
  const item2DropdownRef = useRef();
  const tableWrapperRef = useRef();
  const cardsContainerRef = useRef();

  // Estado para modal de confirmación de navegación
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const hasChangesRef = useRef(false);
  useEffect(() => { hasChangesRef.current = hasChanges; }, [hasChanges]);

  // Interceptar clicks en links del sidebar para bloquear navegación
  useEffect(() => {
    const handleClick = (e) => {
      if (!hasChangesRef.current) return;
      const link = e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('http') || href === location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNavigation(href);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [location.pathname]);

  // Interceptar botón atrás del navegador
  useEffect(() => {
    if (!hasChanges) return;
    const handlePopState = () => {
      window.history.pushState(null, '', location.pathname);
      setPendingNavigation('__back__');
    };
    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasChanges, location.pathname]);

  // Bloquear cierre/recarga de pestaña si hay cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Definición de columnas con etapas
  const etapas = [
    {
      id: 'etapa1',
      nombre: 'ETAPA 1 - CLIENTE',
      columnas: [
        { key: 'item', label: 'ITEM', width: 60 },
        { key: 'cliente', label: 'CLIENTE / CLIENT', width: 150 },
        { key: 'cotizacion', label: 'COTIZACIÓN / QUOTE', width: 100 },
        { key: 'p_order', label: 'ORDEN COMPRA / PURCHASE ORDER', width: 110 },
        { key: 'item2', label: 'ITEM2', width: 60 },
        { key: 'descripcion', label: 'DESCRIPCIÓN / DESCRIPTION', width: 250 },
        { key: 'part_number', label: 'NRO. PARTE / PART NUMBER', width: 110 },
        { key: 'cin', label: 'C.I.N.', width: 80 },
        { key: 'delivery_date', label: 'FECHA ENTREGA / DELIVERY DATE', width: 110 },
        { key: 'real_delivery', label: 'ENTREGA REAL / REAL DELIVERY', width: 110 },
        { key: 'cantidad', label: 'CANTIDAD / QUANTITY', width: 80 },
        { key: 'unidad', label: 'UNIDAD / UNIT', width: 70 },
        { key: 'pu_clp', label: 'P.UNITARIO $CLP / UNIT PRICE', width: 100 },
        { key: 'total_neto', label: 'TOTAL NETO / NET TOTAL', width: 110, calculated: true }
      ]
    },
    {
      id: 'etapa2',
      nombre: 'ETAPA 2 - FACTURACIÓN',
      columnas: [
        { key: 'guia', label: 'GUÍA / GUIDE', width: 80 },
        { key: 'factura', label: 'FACTURA / INVOICE', width: 90 },
        { key: 'emision_fact', label: 'EMISIÓN / ISSUE DATE', width: 110 },
        { key: 'vencimiento', label: 'VENCIMIENTO / DUE DATE', width: 110 },
        { key: 'fecha_pago', label: 'FECHA PAGO / PAYMENT DATE', width: 110 },
        { key: 'pronto_pago', label: 'PRONTO PAGO / EARLY PAY', width: 100 },
        { key: 'utilidad_factor', label: 'UTILIDAD FACTOR / PROFIT FACTOR', width: 120 }
      ]
    },
    {
      id: 'etapa3',
      nombre: 'ETAPA 3 - PROVEEDOR',
      columnas: [
        { key: 'proveedor', label: 'PROVEEDOR / SUPPLIER', width: 150 },
        { key: 'oc_sobek', label: 'OC PROVEEDOR / SUPPLIER PO', width: 90 },
        { key: 'pu_proveedor', label: 'P.U. PROVEEDOR / SUPPLIER PRICE', width: 110 },
        { key: 'total_proveedor', label: 'TOTAL PROVEEDOR / SUPPLIER TOTAL', width: 130, calculated: true },
        { key: 'u_del_negocio', label: 'UTILIDAD NEGOCIO / BUSINESS PROFIT', width: 120, calculated: true },
        { key: 'factura_proveedor', label: 'FACTURA PROV. / SUPPLIER INVOICE', width: 140 },
        { key: 'emision_prov', label: 'EMISIÓN / ISSUE', width: 100 },
        { key: 'vencimiento_prov', label: 'VENCIMIENTO / DUE', width: 110 },
        { key: 'fecha_pago_prov', label: 'FECHA PAGO / PAYMENT DATE', width: 110 }
      ]
    },
    {
      id: 'etapa4',
      nombre: 'ETAPA 4 - OBSERVACIONES',
      columnas: [
        { key: 'observaciones', label: 'OBSERVACIONES / REMARKS', width: 300 }
      ]
    }
  ];

  // Filtrar etapas y columnas según permisos del usuario
  const filteredEtapas = useMemo(() => {
    try {
      const empresaData = localStorage.getItem('empresaActiva');
      if (!empresaData) return etapas;
      const empresa = JSON.parse(empresaData);
      const userRole = empresa?.rolUsuario || empresa?.role;
      // Admin, editor y superadmin ven todo
      if (userRole === 'admin' || userRole === 'editor') return etapas;
      const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
      if (isSuperAdmin) return etapas;

      const maestroConfig = empresa?.permissions?.maestroConfig;
      if (!maestroConfig?.etapas) return etapas;

      return etapas
        .filter(etapa => maestroConfig.etapas[etapa.id]?.enabled !== false)
        .map(etapa => {
          const etapaConfig = maestroConfig.etapas[etapa.id];
          if (!etapaConfig?.columns) return etapa;
          const filteredCols = etapa.columnas.filter(col => etapaConfig.columns[col.key] !== false);
          return { ...etapa, columnas: filteredCols };
        })
        .filter(etapa => etapa.columnas.length > 0);
    } catch {
      return etapas;
    }
  }, []);

  // Función para calcular la altura máxima disponible para el dropdown
  const calculateDropdownHeight = (inputElement) => {
    if (!inputElement) return 250;

    const inputRect = inputElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    // Si hay más espacio arriba y el espacio abajo es muy poco
    if (spaceBelow < 150 && spaceAbove > spaceBelow) {
      // Mostrar el dropdown hacia arriba (no implementado en este caso)
      return Math.min(250, spaceAbove - 20);
    } else {
      // Mostrar el dropdown hacia abajo con altura ajustada
      return Math.min(250, spaceBelow - 20);
    }
  };

  // Ref para evitar llamadas duplicadas en StrictMode
  const dataLoadedRef = useRef(false);

  // Cargar clientes y proveedores al montar el componente
  useEffect(() => {
    // Evitar llamadas duplicadas en React.StrictMode (desarrollo)
    if (dataLoadedRef.current) return;
    dataLoadedRef.current = true;

    loadClientsAndSuppliers();
    loadOrdersFromBackend();
    loadCollaborators();
    loadChangesHistory();
    loadDocumentCounts();
    loadObservationCounts();
  }, []);

  // Scroll a celda desde navegación externa (ej: desde Documentos)
  useEffect(() => {
    if (location.state?.scrollToDoc && data.length > 0) {
      const { itemNumber, fieldKey } = location.state.scrollToDoc;
      const cellId = `${itemNumber}-${fieldKey}`;
      window.history.replaceState({}, '');
      setHighlightedCell(cellId);

      setTimeout(() => {
        const cellElement = document.querySelector(`[data-cell-id="${cellId}"]`);
        if (!cellElement) return;

        cellElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

        // Inyectar animación CSS de pulso persistente
        const styleId = 'doc-scroll-pulse-style';
        if (!document.getElementById(styleId)) {
          const style = document.createElement('style');
          style.id = styleId;
          style.textContent = `
            @keyframes docCellPulse {
              0% { box-shadow: 0 0 0 3px var(--theme-secondary, #000), 0 0 8px rgba(0,0,0,0.15); }
              50% { box-shadow: 0 0 0 5px var(--theme-secondary, #000), 0 0 25px rgba(0,0,0,0.3); }
              100% { box-shadow: 0 0 0 3px var(--theme-secondary, #000), 0 0 8px rgba(0,0,0,0.15); }
            }
          `;
          document.head.appendChild(style);
        }

        cellElement.style.animation = 'docCellPulse 1.5s ease-in-out infinite';
        cellElement.style.zIndex = '10';
        cellElement.style.position = 'relative';

        // Al hacer clic en cualquier parte → quitar animación
        const cleanup = () => {
          cellElement.style.animation = '';
          cellElement.style.zIndex = '';
          cellElement.style.position = '';
          setHighlightedCell(null);
          const styleEl = document.getElementById(styleId);
          if (styleEl) styleEl.remove();
          document.removeEventListener('mousedown', cleanup);
        };

        // Pequeño delay para que el propio clic de navegación no lo cancele
        setTimeout(() => {
          document.addEventListener('mousedown', cleanup);
        }, 200);
      }, 600);
    }
  }, [location.state, data.length]);

  // Limpiar estados al desmontar el componente (al salir del Maestro)
  useEffect(() => {
    return () => {
      // Limpiar modo historial
      setIsHistoryMode(false);
      setCurrentHistoryChange(null);

      // Limpiar modo incompletos
      setIsIncompleteMode(false);
      setCurrentIncompleteIndex(-1);
      setIncompleteRows([]);

      // Clean up documents uploaded but not saved
      if (pendingDocUploadsRef.current.length > 0) {
        api.post('/documents/bulk-delete', { ids: pendingDocUploadsRef.current }).catch(() => {});
        pendingDocUploadsRef.current = [];
      }
    };
  }, []);

  // Handle click outside for unit dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) {
        setShowUnitDropdown(false);
        setSearchUnit('');
      }
    };

    if (showUnitDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUnitDropdown]);

  // Handle click outside for ITEM2 dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (item2DropdownRef.current && !item2DropdownRef.current.contains(event.target)) {
        setShowItem2Dropdown(false);
      }
    };

    if (showItem2Dropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showItem2Dropdown]);

  // Handle click outside for inline dropdowns in main table
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Cerrar dropdowns inline si el clic es fuera
      if (!event.target.closest('.select-dropdown') &&
          !event.target.closest('.cell-input') &&
          !event.target.closest('.select-option')) {
        setInlineClientDropdown(null);
        setInlineSupplierDropdown(null);
        setInlineUnitDropdown(null);
        setInlineItem2Dropdown(null);
        setInlineSearchClient('');
        setInlineSearchSupplier('');
        setInlineSearchUnit('');
        setInlineItem2Search('');
        setInlineDatePicker(null);
      }

      // Cerrar selector de rango de fechas
      if (inlineDateRange && !event.target.closest('.date-range-selector') && !event.target.closest('.cell-input')) {
        setInlineDateRange(null);
        setInlineTempStartDate('');
        setInlineTempEndDate('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inlineDateRange]);

  // Ocultar header cuando modal está abierto
  useEffect(() => {
    const anyModalOpen = showModal || showImportModal;

    if (anyModalOpen) {
      document.body.classList.add('matriz-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('matriz-modal-open');
      document.body.style.overflow = '';
    }

    return () => {
      document.body.classList.remove('matriz-modal-open');
      document.body.style.overflow = '';
    };
  }, [showModal, showImportModal]);

  const loadClientsAndSuppliers = async () => {
    try {
      setLoadingData(true);

      console.log('🔄 Cargando clientes y proveedores desde la API...');

      // Obtener el companyId de la empresa activa
      const empresaData = localStorage.getItem('empresaActiva');
      let companyId = '3'; // Default: SOBEK INGENIEROS SUPPLY

      if (empresaData) {
        try {
          const parsed = JSON.parse(empresaData);
          companyId = String(parsed.id || parsed.empresaId || '3');
        } catch {
          if (!isNaN(empresaData)) {
            companyId = empresaData;
          }
        }
      }

      console.log('📍 Company ID para consulta:', companyId);

      // Cargar clientes desde la API
      try {
        const clientsResponse = await api.get('/business-partners/clients', {
          params: { companyId: companyId, isActive: 'true' }
        });
        console.log('✅ Respuesta de clientes:', clientsResponse.data);

        if (clientsResponse.data?.data) {
          const apiClients = clientsResponse.data.data.map(client => ({
            id: client.id,
            name: client.tradeName || client.legalName || 'Sin nombre',
            rut: client.taxId,
            industry: client.industry
          }));

          console.log(`✅ ${apiClients.length} clientes cargados desde API`);
          setClients(apiClients);
        } else {
          console.log('⚠️ No se encontraron clientes en la respuesta');
          setClients([]);
        }
      } catch (apiError) {
        console.error('❌ Error al cargar clientes desde la API:', apiError);
        console.error('Detalles del error:', apiError.response?.data);
        setClients([]);
      }

      // Cargar proveedores desde la API
      try {
        const suppliersResponse = await api.get('/business-partners/suppliers', {
          params: { companyId: companyId, isActive: 'true' }
        });

        console.log('✅ Respuesta de proveedores:', suppliersResponse.data);

        if (suppliersResponse.data?.data) {
          const apiSuppliers = suppliersResponse.data.data.map(supplier => ({
            id: supplier.id,
            name: supplier.tradeName || supplier.legalName || 'Sin nombre',
            rut: supplier.taxId,
            industry: supplier.industry
          }));

          console.log(`✅ ${apiSuppliers.length} proveedores cargados desde API`);
          setSuppliers(apiSuppliers);
        } else {
          console.log('⚠️ No se encontraron proveedores en la respuesta');
          setSuppliers([]);
        }
      } catch (apiError) {
        console.error('❌ Error al cargar proveedores desde la API:', apiError);
        console.error('Detalles del error:', apiError.response?.data);
        setSuppliers([]);
      }

    } finally {
      setLoadingData(false);
    }
  };

  // Cargar colaboradores desde el historial
  const loadCollaborators = async () => {
    try {
      const response = await api.get('/matriz/maestro-supply/collaborators');
      console.log('👥 Colaboradores cargados:', response.data);

      setCollaborators(response.data.collaborators || []);
      setItemCollaborators(response.data.itemCollaborators || {});
      setCellLastEditor(response.data.cellLastEditor || {});
    } catch (error) {
      console.error('Error al cargar colaboradores:', error);
    }
  };

  // Cargar historial de cambios para navegación
  const loadChangesHistory = async () => {
    try {
      const response = await api.get('/matriz/maestro-supply/changes-history');
      console.log('📜 Historial de cambios cargado:', response.data);

      setChangesHistory(response.data.changes || []);
    } catch (error) {
      console.error('Error al cargar historial de cambios:', error);
    }
  };

  // Navegar al cambio anterior (← va hacia atrás en el tiempo)
  const navigateToPreviousChange = () => {
    if (isNavigatingRef.current) {
      console.log('⏸️ Ya se está navegando, esperando...');
      return;
    }

    if (currentChangeIndex > 0) {
      const newIndex = currentChangeIndex - 1;
      setCurrentChangeIndex(newIndex);
      setIsHistoryMode(true);
      setCurrentHistoryChange(changesHistory[newIndex]);
      navigateToChange(changesHistory[newIndex]);
    } else if (currentChangeIndex === -1 && changesHistory.length > 0) {
      // Si no hay cambio seleccionado, ir al último (más reciente)
      const lastIndex = changesHistory.length - 1;
      setCurrentChangeIndex(lastIndex);
      setIsHistoryMode(true);
      setCurrentHistoryChange(changesHistory[lastIndex]);
      navigateToChange(changesHistory[lastIndex]);
    }
  };

  // Navegar al cambio siguiente (→ va hacia adelante en el tiempo)
  const navigateToNextChange = () => {
    if (isNavigatingRef.current) {
      console.log('⏸️ Ya se está navegando, esperando...');
      return;
    }

    if (currentChangeIndex < changesHistory.length - 1 && currentChangeIndex !== -1) {
      const newIndex = currentChangeIndex + 1;
      setCurrentChangeIndex(newIndex);
      setIsHistoryMode(true);
      setCurrentHistoryChange(changesHistory[newIndex]);
      navigateToChange(changesHistory[newIndex]);
    }
  };

  // Salir del modo historial y volver al presente
  const exitHistoryMode = () => {
    setIsHistoryMode(false);
    setCurrentHistoryChange(null);
    setCurrentChangeIndex(-1);
    setHighlightedCell(null);

    // Limpiar indicador flotante
    const existingIndicator = document.getElementById('cell-highlight-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }
  };

  // ============ FUNCIONES PARA NAVEGACIÓN DE DATOS INCOMPLETOS ============

  // Verificar si una fila está incompleta
  const isRowIncomplete = (row) => {
    return getMissingFields(row).length > 0;
  };

  // Obtener campos faltantes de una fila
  const getMissingFields = (row) => {
    const missing = [];

    // Solo campos numéricos que determinan los cálculos de totales
    const cantidad = parseFloat(row.cantidad);
    const pu_clp = parseFloat(row.pu_clp);
    const pu_proveedor = parseFloat(row.pu_proveedor);

    if (isNaN(cantidad) || cantidad <= 0) missing.push({ key: 'cantidad', label: 'Cantidad' });
    if (isNaN(pu_clp) || pu_clp <= 0) missing.push({ key: 'pu_clp', label: 'PU CLP' });
    if (isNaN(pu_proveedor) || pu_proveedor <= 0) missing.push({ key: 'pu_proveedor', label: 'PU Proveedor' });

    return missing;
  };

  // Construir lista plana de TODAS las celdas incompletas (recalcula desde data actual)
  const getAllIncompleteCells = () => {
    const cells = [];
    data.forEach(row => {
      const missing = getMissingFields(row);
      missing.forEach(field => {
        cells.push({ row, field });
      });
    });
    return cells;
  };

  // Iniciar modo de navegación de datos incompletos
  // Helper: abrir celda en edición y darle foco
  const openCellForEditing = (row, field) => {
    const cellId = `${row.id}-${field.key}`;
    setEditingCell(cellId);
    setTimeout(() => {
      const input = document.querySelector(`[data-cell-id="${row.item}-${field.key}"] input, [data-cell-id="${row.item}-${field.key}"] select`);
      if (input) input.focus();
    }, 100);
  };

  const startIncompleteMode = () => {
    const allCells = getAllIncompleteCells();

    if (allCells.length === 0) {
      console.log('✅ No hay datos incompletos');
      return;
    }

    // Guardar filas incompletas para el indicador de conteo
    const incomplete = data.filter(row => isRowIncomplete(row));
    setIncompleteRows(incomplete);
    setCurrentIncompleteIndex(0);
    setIsIncompleteMode(true);

    // Navegar a la primera celda incompleta y abrirla para edición
    navigateToIncompleteCell(allCells, 0);
    setTimeout(() => openCellForEditing(allCells[0].row, allCells[0].field), 150);
  };

  // Navegar a la celda incompleta anterior
  const navigateToPreviousIncomplete = () => {
    const allCells = getAllIncompleteCells();
    if (allCells.length === 0) { exitIncompleteMode(); return; }
    const newIndex = currentIncompleteIndex > 0 ? currentIncompleteIndex - 1 : 0;
    setCurrentIncompleteIndex(newIndex);
    navigateToIncompleteCell(allCells, newIndex);
    setTimeout(() => openCellForEditing(allCells[newIndex].row, allCells[newIndex].field), 150);
  };

  // Navegar a la celda incompleta siguiente
  const navigateToNextIncomplete = () => {
    const allCells = getAllIncompleteCells();
    if (allCells.length === 0) { exitIncompleteMode(); return; }
    const newIndex = currentIncompleteIndex < allCells.length - 1 ? currentIncompleteIndex + 1 : currentIncompleteIndex;
    setCurrentIncompleteIndex(newIndex);
    navigateToIncompleteCell(allCells, newIndex);
    setTimeout(() => openCellForEditing(allCells[newIndex].row, allCells[newIndex].field), 150);
  };

  // Salir del modo de datos incompletos
  const exitIncompleteMode = () => {
    if (incompleteTimerRef.current) {
      clearTimeout(incompleteTimerRef.current);
      incompleteTimerRef.current = null;
    }
    setIsIncompleteMode(false);
    setCurrentIncompleteIndex(-1);
    setIncompleteRows([]);
    setHighlightedCell(null);

    const existingIndicator = document.getElementById('incomplete-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }
  };

  // Navegar a una celda incompleta específica por índice
  const navigateToIncompleteCell = (allCells, index) => {
    if (!allCells[index]) return;

    const { row, field } = allCells[index];
    const allMissing = getMissingFields(row);
    const cellId = `${row.item}-${field.key}`;

    // Actualizar filas incompletas
    const incomplete = data.filter(r => isRowIncomplete(r));
    setIncompleteRows(incomplete);

    setHighlightedCell(cellId);

    if (incompleteTimerRef.current) {
      clearTimeout(incompleteTimerRef.current);
    }
    incompleteTimerRef.current = setTimeout(() => {
      incompleteTimerRef.current = null;
      const cellElement = document.querySelector(`[data-cell-id="${cellId}"]`);
      if (cellElement) {
        const existingIndicator = document.getElementById('incomplete-indicator');
        if (existingIndicator) existingIndicator.remove();

        const indicator = document.createElement('div');
        indicator.id = 'incomplete-indicator';

        const missingFieldsList = allMissing.map(f => f.label).join(', ');

        indicator.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 12px; height: 12px; border-radius: 50%; background: #d97706; animation: pulse 1.5s ease-in-out infinite;"></div>
            <span>Item ${row.item} - Faltan: ${missingFieldsList} (${index + 1}/${allCells.length} celdas)</span>
          </div>
        `;
        indicator.style.cssText = `position: fixed; top: 80px; right: 20px; background: #fef3c7; color: #92400e; padding: 12px 20px; border-radius: 12px; z-index: 9998; font-weight: 700; box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3); border: 3px solid #d97706; font-family: "Space Grotesk", sans-serif; font-size: 14px;`;
        document.body.appendChild(indicator);

        cellElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      }
    }, 400);
  };

  // ============ FIN FUNCIONES DATOS INCOMPLETOS ============

  // Navegar a un cambio específico
  const navigateToChange = (change) => {
    if (!change) {
      console.warn('⚠️ Cambio inválido o vacío');
      return;
    }

    // Marcar que se está navegando
    isNavigatingRef.current = true;

    console.log('🔍 Navegando a cambio:', change);

    // Encontrar la fila correspondiente al item
    const rowIndex = filteredData.findIndex(row => row.item === change.itemNumber);

    if (rowIndex === -1) {
      console.warn('⚠️ No se encontró la fila para el item:', change.itemNumber, '(posiblemente filtrado)');
      // Mostrar mensaje temporal al usuario
      const tempMessage = document.createElement('div');
      tempMessage.textContent = `⚠️ El item ${change.itemNumber} está filtrado. Limpia los filtros para verlo.`;
      tempMessage.style.cssText = 'position: fixed; top: 100px; left: 50%; transform: translateX(-50%); background: #fbbf24; color: #000; padding: 12px 24px; border-radius: 8px; z-index: 9999; font-weight: 600; box-shadow: 0 4px 6px rgba(0,0,0,0.1);';
      document.body.appendChild(tempMessage);
      setTimeout(() => {
        document.body.removeChild(tempMessage);
        isNavigatingRef.current = false;
      }, 3000);
      return;
    }

    const row = filteredData[rowIndex];
    const cellId = `${row.item}-${change.fieldKey}`;

    console.log('✅ Celda encontrada:', cellId);

    // Marcar celda como destacada
    setHighlightedCell(cellId);

    // No quitar highlight automáticamente si estamos en modo historial
    // El usuario navegará manualmente o saldrá del modo historial

    // Auto-scroll a la celda con un delay para asegurar render
    setTimeout(() => {
      const cellElement = document.querySelector(`[data-cell-id="${cellId}"]`);
      if (cellElement) {
        console.log('📍 Haciendo scroll a celda:', cellId);

        // Agregar indicador flotante que se mantiene visible
        const existingIndicator = document.getElementById('cell-highlight-indicator');
        if (existingIndicator) {
          existingIndicator.remove();
        }

        const indicator = document.createElement('div');
        indicator.id = 'cell-highlight-indicator';

        // Obtener colores del tema
        const themeSecondary = getComputedStyle(document.documentElement).getPropertyValue('--theme-secondary').trim() || '#000';
        const themePrimary = getComputedStyle(document.documentElement).getPropertyValue('--theme-primary').trim() || '#c4fffc';

        indicator.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 12px; height: 12px; border-radius: 50%; background: ${themeSecondary}; animation: pulse 1.5s ease-in-out infinite;"></div>
            <span>${change.fieldLabel}: Item ${change.itemNumber}</span>
          </div>
        `;
        indicator.style.cssText = `position: fixed; top: 80px; right: 20px; background: ${themePrimary}; color: ${themeSecondary}; padding: 12px 20px; border-radius: 12px; z-index: 9998; font-weight: 700; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); border: 3px solid ${themeSecondary}; font-family: "Space Grotesk", sans-serif; font-size: 14px;`;
        document.body.appendChild(indicator);

        cellElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
        console.log('✅ Scroll completado');
      } else {
        console.warn('⚠️ No se encontró el elemento DOM de la celda:', cellId);
      }
      // Desmarcar navegación después del scroll
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 800);
    }, 400);
  };

  // Toggle para mostrar/ocultar badges de importadores
  const toggleImporterBadges = () => {
    const newValue = !showImporterBadges;
    setShowImporterBadges(newValue);
    localStorage.setItem('maestro_show_importer_badges', JSON.stringify(newValue));
  };

  // Toggle para mostrar/ocultar badges de editores
  const toggleEditorBadges = () => {
    const newValue = !showEditorBadges;
    setShowEditorBadges(newValue);
    localStorage.setItem('maestro_show_editor_badges', JSON.stringify(newValue));
  };

  // Cargar órdenes desde el backend
  const loadOrdersFromBackend = async () => {
    try {
      setLoadingData(true);
      const response = await api.get('/matriz/maestro-supply');
      console.log('Orders loaded from backend:', response.data);

      // La respuesta de maestro-supply es directamente un array
      if (Array.isArray(response.data)) {
        if (response.data.length > 0) {
          console.log(`Loading ${response.data.length} orders from database`);
          // Mapear los campos del backend a los campos del frontend
          const mappedData = response.data.map(row => ({
            id: row.id,
            item: row.item,
            cliente: row.cliente,
            cotizacion: row.cotizacion,
            p_order: row.p_order,  // Cambiado de orden_compra a p_order
            item2: row.item2,
            descripcion: row.descripcion,
            part_number: row.part_number,  // Cambiado de parte_numero a part_number
            cin: row.cin,
            delivery_date: row.delivery_date,  // Cambiado de fecha_entrega a delivery_date
            real_delivery: row.real_delivery,  // Cambiado de entrega_real a real_delivery
            cantidad: row.cantidad,
            unidad: row.unidad,
            pu_clp: row.pu_clp,  // Cambiado de p_unitario_clp a pu_clp
            total_neto: row.total_neto,
            guia: row.guia,  // Cambiado de guia_despacho a guia
            factura: row.factura,
            emision_fact: row.emision_fact,  // Cambiado de fecha_emision a emision_fact
            vencimiento: row.vencimiento,  // Cambiado de fecha_vencimiento a vencimiento
            fecha_pago: row.fecha_pago,
            pronto_pago: row.pronto_pago,
            utilidad_factor: row.utilidad_factor,
            proveedor: row.proveedor,
            oc_sobek: row.oc_sobek,
            pu_proveedor: row.pu_proveedor,  // Cambiado de p_unitario_proveedor a pu_proveedor
            total_proveedor: row.total_proveedor,
            u_del_negocio: row.u_del_negocio,  // Cambiado de utilidad_del_negocio a u_del_negocio
            factura_proveedor: row.factura_proveedor,
            emision_prov: row.emision_prov,  // Cambiado de fecha_emision_proveedor a emision_prov
            vencimiento_prov: row.vencimiento_prov,  // Cambiado de fecha_vencimiento_proveedor a vencimiento_prov
            fecha_pago_prov: row.fecha_pago_prov,  // Cambiado de fecha_pago_proveedor a fecha_pago_prov
            observaciones: row.observaciones
          }));
          setData(mappedData);
        } else {
          console.log('No orders in database');
          // No cargar datos de ejemplo, dejar la tabla vacía
          setData([]);
        }
      } else {
        console.log('Invalid response format');
        // No cargar datos de ejemplo, dejar la tabla vacía
        setData([]);
      }
    } catch (error) {
      console.error('Error loading orders from backend:', error);
      // En caso de error, tampoco cargar datos de ejemplo
      setData([]);
    } finally {
      setLoadingData(false);
    }
  };

  // Cargar documentos agrupados por tipo para un ítem (usado en el modal)
  const loadModalItemDocs = async (itemNumber) => {
    if (!itemNumber) return;
    try {
      const response = await api.get(`/documents/item/${itemNumber}`);
      const docs = response.data || [];
      const byType = {};
      docs.forEach(doc => {
        if (!byType[doc.doc_type]) byType[doc.doc_type] = [];
        byType[doc.doc_type].push(doc);
      });
      setModalItemDocs(byType);
    } catch (error) {
      console.error('Error loading modal item docs:', error);
      setModalItemDocs({});
    }
  };

  // Abrir popover de documentos en una celda del maestro
  const openCellDocPopover = async (itemNumber, docType, position, row) => {
    setCellDocLoading(true);
    setCellDocPopover({ itemNumber, docType, position, docs: [], row });
    try {
      const response = await api.get(`/documents/item/${itemNumber}`);
      const allDocs = response.data || [];
      const filtered = allDocs.filter(d => d.doc_type === docType);
      setCellDocPopover(prev => prev ? { ...prev, docs: filtered } : null);
    } catch (error) {
      console.error('Error loading cell docs:', error);
    } finally {
      setCellDocLoading(false);
    }
  };

  const handleCellDocDelete = async (docId) => {
    if (!confirm('¿Eliminar este documento?')) return;
    try {
      await api.delete(`/documents/${docId}`);
      const popover = cellDocPopover;
      const remainingDocs = popover ? popover.docs.filter(d => d.id !== docId) : [];
      setCellDocPopover(prev => prev ? {
        ...prev,
        docs: remainingDocs
      } : null);
      loadDocumentCounts();

      // Para multi-doc, actualizar la celda del maestro con el nuevo display
      if (popover && MULTI_DOC_TYPES.has(popover.docType)) {
        const fieldKey = Object.entries(FIELD_DOC_TYPE_MAP).find(([k, v]) => v === popover.docType)?.[0];
        if (fieldKey) {
          let displayValue = '';
          if (remainingDocs.length === 1) {
            displayValue = remainingDocs[0].doc_reference || remainingDocs[0].original_name || '1 doc';
          } else if (remainingDocs.length > 1) {
            displayValue = `${remainingDocs.length} docs`;
          }
          setData(prev => prev.map(r =>
            r.item === popover.itemNumber ? { ...r, [fieldKey]: displayValue } : r
          ));
          api.patch('/documents/sync-maestro-field', {
            item_number: popover.itemNumber,
            field_key: fieldKey,
            value: displayValue
          }).catch(err => console.error('Error syncing maestro:', err));
        }
      }
    } catch (error) {
      alert('Error al eliminar documento');
    }
  };

  const handleCellDocUploadSuccess = ({ docReference, docType, docId } = {}) => {
    // Track uploaded doc for cleanup if user leaves without saving
    if (docId) {
      pendingDocUploadsRef.current = [...pendingDocUploadsRef.current, docId];
    }
    setHasChanges(true);
    const ctx = fieldUploadContext;
    const isMulti = MULTI_DOC_TYPES.has(docType);

    if (isMulti) {
      // Multi-doc: NO borrar docs anteriores, calcular display value
      loadDocumentCounts().then(() => {
        // Después de recargar conteos, actualizar la celda del maestro
        const fieldKey = Object.entries(FIELD_DOC_TYPE_MAP).find(([k, v]) => v === docType)?.[0];
        if (fieldKey && ctx?.row) {
          // Obtener docs actuales del item para calcular display
          api.get(`/documents/item/${ctx.row.item}`).then(res => {
            const docs = (res.data || []).filter(d => d.doc_type === docType);
            let displayValue = '';
            if (docs.length === 1) {
              displayValue = docs[0].doc_reference || docs[0].original_name || '1 doc';
            } else if (docs.length > 1) {
              displayValue = `${docs.length} docs`;
            }
            setData(prev => prev.map(r =>
              r.item === ctx.row.item ? { ...r, [fieldKey]: displayValue } : r
            ));
            api.patch('/documents/sync-maestro-field', {
              item_number: ctx.row.item,
              field_key: fieldKey,
              value: displayValue
            }).catch(err => console.error('Error syncing maestro:', err));
          }).catch(err => console.error('Error fetching docs for display:', err));
        }
      });
    } else {
      // Single-doc: comportamiento original (reemplazar)
      if (docReference && ctx?.row) {
        const fieldKey = Object.entries(FIELD_DOC_TYPE_MAP).find(([k, v]) => v === docType)?.[0];
        if (fieldKey) {
          setData(prev => prev.map(r =>
            r.item === ctx.row.item ? { ...r, [fieldKey]: docReference } : r
          ));
          api.patch('/documents/sync-maestro-field', {
            item_number: ctx.row.item,
            field_key: fieldKey,
            value: docReference
          }).catch(err => console.error('Error syncing maestro:', err));
        }
      }
      const deleteOld = ctx?.replaceDocIds?.length > 0
        ? Promise.all(ctx.replaceDocIds.map(id =>
            api.delete(`/documents/${id}`).catch(() => {})
          ))
        : Promise.resolve();
      deleteOld.then(() => loadDocumentCounts());
    }
  };

  // Cargar conteo de documentos por ítem
  const loadDocumentCounts = async () => {
    try {
      const [countsRes, byTypeRes] = await Promise.all([
        api.get('/documents/counts'),
        api.get('/documents/counts-by-type')
      ]);
      setDocumentCounts(countsRes.data || {});
      setDocCountsByType(byTypeRes.data || {});
    } catch (error) {
      console.error('Error loading document counts:', error);
    }
  };

  // Cargar conteo de observaciones por ítem
  const loadObservationCounts = async () => {
    try {
      const res = await api.get('/observations/counts/all');
      setObsCounts(res.data || {});
    } catch (error) {
      console.error('Error loading observation counts:', error);
    }
  };

  // Helper function para obtener nombre de cliente (mapea ID a nombre)
  const getClienteNombre = (clienteValue) => {
    if (!clienteValue) return '';

    // Si ya es un string (nombre), devolverlo directamente
    if (typeof clienteValue === 'string' && isNaN(clienteValue)) {
      return clienteValue;
    }

    // Si es un ID, buscar el nombre en la lista de clientes
    const cliente = clients.find(c => c.id === parseInt(clienteValue));
    if (cliente) {
      return cliente.name || cliente.tradeName || cliente.legalName || clienteValue;
    }

    return clienteValue;
  };

  // Helper function para obtener nombre de proveedor (mapea ID a nombre)
  const getProveedorNombre = (proveedorValue) => {
    if (!proveedorValue) return '';

    // Si ya es un string (nombre), devolverlo directamente
    if (typeof proveedorValue === 'string' && isNaN(proveedorValue)) {
      return proveedorValue;
    }

    // Si es un ID, buscar el nombre en la lista de proveedores
    const proveedor = suppliers.find(s => s.id === parseInt(proveedorValue));
    if (proveedor) {
      return proveedor.name || proveedor.tradeName || proveedor.legalName || proveedorValue;
    }

    return proveedorValue;
  };

  // Datos de ejemplo
  const loadInitialData = () => {
    const ejemploData = [
      {
        id: 1,
        item: 1,
        cliente: 'ANTOFAGASTA',
        cotizacion: '6086',
        p_order: '4510235570',
        item2: 10,
        descripcion: 'TECLE PALANCA 3 TLC',
        part_number: 'NA',
        cin: 'NA',
        delivery_date: '30-10-2025',
        real_delivery: '',
        cantidad: 7,
        unidad: 'CU',
        pu_clp: 684694,
        total_neto: 4792858,
        guia: '',
        factura: '',
        emision_fact: '',
        vencimiento: '',
        fecha_pago: '',
        pronto_pago: '',
        utilidad_factor: '',
        proveedor: 'JAM LTDA',
        oc_sobek: '3138',
        pu_proveedor: 570578,
        total_proveedor: 3994046,
        u_del_negocio: 798812,
        factura_proveedor: '118-125',
        emision_prov: '09-06-2025 / 09-07-2025',
        vencimiento_prov: '09-07-2025 / 07-08-2025',
        fecha_pago_prov: '04-07-2025 / 08-08-2025',
        observaciones: 'OBSERVACIONES'
      },
      {
        id: 2,
        item: 2,
        cliente: 'ANTOFAGASTA',
        cotizacion: '6086',
        p_order: '4510235570',
        item2: 20,
        descripcion: 'TECLE CADENA 15 TLC',
        part_number: 'NA',
        cin: 'NA',
        delivery_date: '30-10-2025',
        real_delivery: '',
        cantidad: 5,
        unidad: 'CU',
        pu_clp: 470631,
        total_neto: 2353155,
        guia: '',
        factura: '',
        emision_fact: '',
        vencimiento: '',
        fecha_pago: '',
        pronto_pago: '',
        utilidad_factor: '',
        proveedor: 'JAM LTDA',
        oc_sobek: '3138',
        pu_proveedor: 392192,
        total_proveedor: 1960960,
        u_del_negocio: 392195,
        factura_proveedor: '118-125',
        emision_prov: '09-06-2025 / 09-07-2025',
        vencimiento_prov: '09-07-2025 / 07-08-2025',
        fecha_pago_prov: '04-07-2025 / 08-08-2025',
        observaciones: ''
      },
      {
        id: 3,
        item: 3,
        cliente: 'ANTOFAGASTA',
        cotizacion: '6086',
        p_order: '4510235570',
        item2: 30,
        descripcion: 'TECLE PALANCA 6 TLC',
        part_number: 'NA',
        cin: 'NA',
        delivery_date: '30-10-2025',
        real_delivery: '',
        cantidad: 4,
        unidad: 'CU',
        pu_clp: 1163367,
        total_neto: 4653468,
        guia: '',
        factura: '',
        emision_fact: '',
        vencimiento: '',
        fecha_pago: '',
        pronto_pago: '',
        utilidad_factor: '',
        proveedor: 'JAM LTDA',
        oc_sobek: '3138',
        pu_proveedor: 969473,
        total_proveedor: 3877892,
        u_del_negocio: 775576,
        factura_proveedor: '118-125',
        emision_prov: '09-06-2025 / 09-07-2025',
        vencimiento_prov: '09-07-2025 / 07-08-2025',
        fecha_pago_prov: '04-07-2025 / 08-08-2025',
        observaciones: ''
      },
      {
        id: 4,
        item: 4,
        cliente: 'ANTOFAGASTA',
        cotizacion: '6086',
        p_order: '4510235570',
        item2: 40,
        descripcion: 'TECLE CADENA 3 TLC',
        part_number: 'NA',
        cin: 'NA',
        delivery_date: '30-10-2025',
        real_delivery: '',
        cantidad: 4,
        unidad: 'CU',
        pu_clp: 555921,
        total_neto: 2223684,
        guia: '',
        factura: '',
        emision_fact: '',
        vencimiento: '',
        fecha_pago: '',
        pronto_pago: '',
        utilidad_factor: '',
        proveedor: 'JAM LTDA',
        oc_sobek: '3138',
        pu_proveedor: 463267,
        total_proveedor: 1853068,
        u_del_negocio: 370616,
        factura_proveedor: '118-125',
        emision_prov: '09-06-2025 / 09-07-2025',
        vencimiento_prov: '09-07-2025 / 07-08-2025',
        fecha_pago_prov: '04-07-2025 / 08-08-2025',
        observaciones: ''
      },
      {
        id: 5,
        item: 5,
        cliente: 'ANTOFAGASTA',
        cotizacion: '6086',
        p_order: '4510235570',
        item2: 50,
        descripcion: 'GRILLETE 1',
        part_number: 'NA',
        cin: 'NA',
        delivery_date: '30-10-2025',
        real_delivery: '',
        cantidad: 30,
        unidad: 'CU',
        pu_clp: 7321,
        total_neto: 219630,
        guia: '',
        factura: '',
        emision_fact: '',
        vencimiento: '',
        fecha_pago: '',
        pronto_pago: '',
        utilidad_factor: '',
        proveedor: 'JAM LTDA',
        oc_sobek: '3138',
        pu_proveedor: 6101,
        total_proveedor: 183030,
        u_del_negocio: 36600,
        factura_proveedor: '118-125',
        emision_prov: '09-06-2025 / 09-07-2025',
        vencimiento_prov: '09-07-2025 / 07-08-2025',
        fecha_pago_prov: '04-07-2025 / 08-08-2025',
        observaciones: ''
      }
    ];

    setData(ejemploData);
  };

  // Función para confirmar la importación real
  const confirmImport = async () => {
    console.log('🔴🔴🔴 ===== INICIO CONFIRMACIÓN DE IMPORTACIÓN ===== 🔴🔴🔴');
    console.log('Función confirmImport ejecutada');

    try {
      setImportStep('confirming');
      setImportProgress(0);
      setImportStatus('Procesando importación...');

      const token = localStorage.getItem('token');
      let empresaData = localStorage.getItem('empresaActiva');
      let empresaId = '3';

      if (empresaData) {
        try {
          const parsed = JSON.parse(empresaData);
          empresaId = String(parsed.id || parsed.empresaId || '3');
        } catch {
          if (!isNaN(empresaData)) {
            empresaId = String(empresaData);
          }
        }
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-empresa-id': empresaId,
        'x-company-id': empresaId
      };

      // Primero crear/mapear clientes y proveedores si hay nuevos
      if (Object.keys(newClientsMap).length > 0 || Object.keys(newSuppliersMap).length > 0) {
        setImportProgress(20);
        setImportStatus('Creando nuevos clientes y proveedores...');

        console.log('🔍 DEBUG - newClientsMap completo:', newClientsMap);
        console.log('🔍 DEBUG - newSuppliersMap completo:', newSuppliersMap);

        const newClients = [];
        const clientMappings = {};

        for (const [clientName, mappedId] of Object.entries(newClientsMap)) {
          console.log(`🔍 Procesando cliente: "${clientName}" con mappedId:`, mappedId, 'tipo:', typeof mappedId);
          if (mappedId === null) {
            newClients.push(clientName);
            console.log(`✅ Cliente "${clientName}" será creado`);
          } else {
            clientMappings[clientName] = mappedId;
            console.log(`🔗 Cliente "${clientName}" será mapeado a ID ${mappedId}`);
          }
        }

        const newSuppliers = [];
        const supplierMappings = {};

        for (const [supplierName, mappedId] of Object.entries(newSuppliersMap)) {
          console.log(`🔍 Procesando proveedor: "${supplierName}" con mappedId:`, mappedId, 'tipo:', typeof mappedId);
          if (mappedId === null) {
            newSuppliers.push(supplierName);
            console.log(`✅ Proveedor "${supplierName}" será creado`);
          } else {
            supplierMappings[supplierName] = mappedId;
            console.log(`🔗 Proveedor "${supplierName}" será mapeado a ID ${mappedId}`);
          }
        }

        console.log('📋 Clientes a crear:', newClients);
        console.log('📋 Proveedores a crear:', newSuppliers);

        // Crear nuevos clientes
        if (newClients.length > 0) {
          console.log('🚀 Enviando solicitud para crear clientes:', newClients);
          console.log('🚀 URL:', '/api/entities/clients/bulk');
          console.log('🚀 Headers:', headers);

          try {
            const clientResponse = await axios.post(
              '/api/entities/clients/bulk',
              { clients: newClients },
              { headers: { ...headers, 'Content-Type': 'application/json' } }
            );
            console.log('✅ Respuesta de clientes:', clientResponse.data);
            console.log(`   📊 Total procesados: ${clientResponse.data.total}`);
            console.log(`   ✨ Nuevos creados: ${clientResponse.data.created}`);
            console.log(`   ♻️ Ya existían: ${clientResponse.data.existing}`);
            if (clientResponse.data.errors > 0) {
              console.warn(`   ⚠️ Errores: ${clientResponse.data.errors}`, clientResponse.data.errorDetails);
            }
          } catch (error) {
            console.error('❌ Error al crear clientes:', error);
            console.error('❌ Respuesta del error:', error.response?.data);
            throw error;
          }
        } else {
          console.log('ℹ️ No hay clientes nuevos para crear');
        }

        // Crear nuevos proveedores
        if (newSuppliers.length > 0) {
          console.log('🚀 Enviando solicitud para crear proveedores:', newSuppliers);
          console.log('🚀 URL:', '/api/entities/suppliers/bulk');
          console.log('🚀 Headers:', headers);

          try {
            const supplierResponse = await axios.post(
              '/api/entities/suppliers/bulk',
              { suppliers: newSuppliers },
              { headers: { ...headers, 'Content-Type': 'application/json' } }
            );
            console.log('✅ Respuesta de proveedores:', supplierResponse.data);
            console.log(`   📊 Total procesados: ${supplierResponse.data.total}`);
            console.log(`   ✨ Nuevos creados: ${supplierResponse.data.created}`);
            console.log(`   ♻️ Ya existían: ${supplierResponse.data.existing}`);
            if (supplierResponse.data.errors > 0) {
              console.warn(`   ⚠️ Errores: ${supplierResponse.data.errors}`, supplierResponse.data.errorDetails);
            }
          } catch (error) {
            console.error('❌ Error al crear proveedores:', error);
            console.error('❌ Respuesta del error:', error.response?.data);
            throw error;
          }
        } else {
          console.log('ℹ️ No hay proveedores nuevos para crear');
        }

        // IMPORTANTE: Recargar clientes y proveedores después de crearlos
        console.log('🔄 Recargando lista de clientes y proveedores...');
        await loadClientsAndSuppliers();
        console.log('✅ Listas actualizadas');
      }

      // Ahora importar los datos realmente
      setImportProgress(60);
      setImportStatus('Importando datos al sistema...');

      const formData = new FormData();
      formData.append('file', pendingImportData.file);
      formData.append('overwriteMode', overwriteMode);

      // Enviar los mappings de clientes y proveedores al backend
      if (Object.keys(newClientsMap).length > 0) {
        formData.append('clientMappings', JSON.stringify(newClientsMap));
      }
      if (Object.keys(newSuppliersMap).length > 0) {
        formData.append('supplierMappings', JSON.stringify(newSuppliersMap));
      }

      const response = await axios.post(
        `/api/matriz/maestro-supply/import`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
            'x-empresa-id': empresaId,
            'x-company-id': empresaId
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setImportProgress(60 + (percentCompleted * 0.3));
          }
        }
      );

      setImportProgress(100);
      setImportStatus('¡Importación completada exitosamente!');
      setImportStep('completed');

      // Recargar datos inmediatamente
      console.log('🔄 Recargando datos del backend...');
      await loadOrdersFromBackend();
      await loadClientsAndSuppliers();
      console.log('✅ Datos recargados exitosamente');

      // Cerrar modal después de 1 segundo
      setTimeout(() => {
        setShowImportModal(false);
        setImportResults(null);
        setShowPreview(false);
        setDuplicateItems([]);
        setNewClientsMap({});
        setNewSuppliersMap({});
        setPendingImportData(null);
        setImportStep('uploading');
        setDatosAnteriores([]);
      }, 1000);

    } catch (error) {
      console.error('Error confirmando importación:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error desconocido';
      setImportStatus(`Error: ${errorMsg}`);
      setImportStep('preview'); // Volver a preview para reintentar
    }
  };

  // Función para parsear números con formato del Excel (formato inglés)
  const parseChileanNumber = (value) => {
    if (!value || value === 'NA' || value === 'N/A') return 0;

    // Si ya es un número, retornarlo
    if (typeof value === 'number') return value;

    // Convertir a string y limpiar
    let str = value.toString();

    // Remover símbolo de peso y espacios
    str = str.replace(/[$\s]/g, '');

    // IMPORTANTE: El Excel usa formato inglés con comas como separadores de miles
    // Ejemplo: $684,694 significa 684694 pesos
    // Remover comas (separadores de miles)
    str = str.replace(/,/g, '');

    // Si hay punto, es decimal (formato inglés)
    return parseFloat(str) || 0;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Mostrar modal de importación
    setShowImportModal(true);
    setImportProgress(0);
    setImportStatus('Cargando clientes y proveedores...');
    setImportResults(null);
    setImportStep('uploading');
    setPendingImportData(null);

    // IMPORTANTE: Recargar clientes y proveedores antes de procesar el archivo
    // Esto asegura que la lista incluya cualquier cliente/proveedor creado en importaciones anteriores
    console.log('🔄 Recargando clientes y proveedores antes de importar...');
    await loadClientsAndSuppliers();
    console.log('✅ Listas actualizadas, ahora procesando archivo...');

    setImportStatus('Leyendo archivo...');

    // Primero leer el archivo para preview
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        setImportProgress(20);
        setImportStatus('Procesando datos del Excel...');

        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json(ws, { raw: false });

        setImportProgress(40);
        setImportStatus(`Analizando ${jsonData.length} registros...`);

        // Preparar preview de datos (nota: las columnas tienen espacios)
        const preview = jsonData.slice(0, 10).map(row => ({
          item: row['ITEM'] || row.ITEM,
          cliente: row['CLIENTE'] || row.CLIENTE,
          descripcion: row['DESCRIPCION'] || row.DESCRIPCION,
          cantidad: parseChileanNumber(row['CANTIDAD'] || row.CANTIDAD),
          pu_clp: parseChileanNumber(row[' P.U. $CLP '] || row['P.U. $CLP'] || row['P.U.$CLP'] || row['PU CLP']),
          total_neto: parseChileanNumber(row[' TOTAL NETO '] || row['TOTAL NETO'] || row['TOTAL NETO ']),
          proveedor: row['PROVEEDOR'] || row.PROVEEDOR
        }));

        // Detectar items duplicados con los datos existentes
        const existingItems = data.map(d => d.item);
        const importItems = jsonData.map(row => row['ITEM'] || row.ITEM).filter(Boolean);
        const duplicates = importItems.filter(item => existingItems.includes(item));
        const uniqueDuplicates = [...new Set(duplicates)];

        if (uniqueDuplicates.length > 0) {
          setDuplicateItems(uniqueDuplicates);
          setImportStatus(`Detectados ${uniqueDuplicates.length} items duplicados`);
        }

        setPreviewData(preview);
        setShowPreview(true);

        // Guardar el archivo para uso posterior
        setPendingImportData({ file, preview });
        setImportStep('preview');
        setImportProgress(50);
        setImportStatus('Vista previa lista. Revise los datos antes de confirmar.');

        // Primero obtener solo un preview del backend
        const formData = new FormData();
        formData.append('file', file);
        formData.append('preview', 'true');
        formData.append('overwriteMode', overwriteMode);

        const token = localStorage.getItem('token');
        let empresaData = localStorage.getItem('empresaActiva');
        let empresaId = '3';

        if (empresaData) {
          try {
            const parsed = JSON.parse(empresaData);
            empresaId = String(parsed.id || parsed.empresaId || '3');
          } catch {
            if (!isNaN(empresaData)) {
              empresaId = String(empresaData);
            }
          }
        }

        console.log('📤 Enviando solicitud de preview al backend...');
        const previewResponse = await axios.post(
          `/api/matriz/maestro-supply/import`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`,
              'x-empresa-id': empresaId,
              'x-company-id': empresaId
            }
          }
        );

        console.log('📊 Respuesta del backend (preview):', previewResponse.data);
        console.log('🔍 Nuevos clientes detectados:', previewResponse.data.summary?.nuevosClientes);
        console.log('🔍 Nuevos proveedores detectados:', previewResponse.data.summary?.nuevosProveedores);
        console.log('📋 Total registros:', previewResponse.data.summary?.totalRegistros);
        console.log('📋 Registros nuevos:', previewResponse.data.summary?.registrosNuevos);

        if (previewResponse.data?.success) {
          setImportResults(previewResponse.data);

          // Si hay nuevos clientes/proveedores, preparar mapeo con detección automática
          if (previewResponse.data.summary?.nuevosClientes?.length > 0) {
            const clientMap = {};
            previewResponse.data.summary.nuevosClientes.forEach(newClient => {
              // Buscar cliente existente con nombre exacto
              const existingClient = clients.find(c =>
                c.name && c.name.toUpperCase().trim() === newClient.toUpperCase().trim()
              );

              if (existingClient) {
                // Si encuentra un match exacto, pre-seleccionarlo (guardar ID, no nombre)
                clientMap[newClient] = existingClient.id;
                console.log(`🔗 Cliente "${newClient}" automáticamente mapeado a ID ${existingClient.id} (${existingClient.name})`);
              } else {
                // Si no hay match, dejar como "crear nuevo"
                clientMap[newClient] = null;
                console.log(`➕ Cliente "${newClient}" marcado para crear nuevo`);
              }
            });
            console.log('📋 Inicializando newClientsMap con:', clientMap);
            setNewClientsMap(clientMap);
            setOriginalClientsMap({...clientMap}); // Guardar copia del estado original
          } else {
            console.log('ℹ️ No hay nuevos clientes detectados');
            setNewClientsMap({});
            setOriginalClientsMap({});
          }

          if (previewResponse.data.summary?.nuevosProveedores?.length > 0) {
            const supplierMap = {};
            previewResponse.data.summary.nuevosProveedores.forEach(newSupplier => {
              // Buscar proveedor existente con nombre exacto
              const existingSupplier = suppliers.find(s =>
                s.name && s.name.toUpperCase().trim() === newSupplier.toUpperCase().trim()
              );

              if (existingSupplier) {
                // Si encuentra un match exacto, pre-seleccionarlo (guardar ID, no nombre)
                supplierMap[newSupplier] = existingSupplier.id;
                console.log(`🔗 Proveedor "${newSupplier}" automáticamente mapeado a ID ${existingSupplier.id} (${existingSupplier.name})`);
              } else {
                // Si no hay match, dejar como "crear nuevo"
                supplierMap[newSupplier] = null;
                console.log(`➕ Proveedor "${newSupplier}" marcado para crear nuevo`);
              }
            });
            console.log('📋 Inicializando newSuppliersMap con:', supplierMap);
            setNewSuppliersMap(supplierMap);
            setOriginalSuppliersMap({...supplierMap}); // Guardar copia del estado original
          } else {
            console.log('ℹ️ No hay nuevos proveedores detectados');
            setNewSuppliersMap({});
            setOriginalSuppliersMap({});
          }

          // Guardar duplicados y datos anteriores
          if (previewResponse.data.duplicados) {
            setDuplicateItems(previewResponse.data.duplicados);
          }

          // Mostrar datos que serán sobrescritos
          if (previewResponse.data.summary?.datosAnteriores) {
            console.log('Datos que serán sobrescritos:', previewResponse.data.summary.datosAnteriores);
            setDatosAnteriores(previewResponse.data.summary.datosAnteriores);
          }
        }

        setImportProgress(100);
        setImportStatus('Vista previa completada. Revise y confirme la importación.');

      } catch (error) {
        console.error('Error al importar:', error);
        setImportStatus('Error en la importación');
        setImportResults({
          error: error.response?.data?.message || error.message || 'Error desconocido'
        });
      }
    };

    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const empresaId = localStorage.getItem('empresaActiva') || 1;

      const response = await axios.get(
        `/api/matriz/maestro-supply`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-empresa-id': empresaId
          }
        }
      );

      if (response.data && response.data.length > 0) {
        setData(response.data);
      }
    } catch (error) {
      console.log('Usando datos de ejemplo');
    }
  };

  const exportToExcel = () => {
    // Definir todas las columnas en el orden exacto del Excel maestro
    const exportData = data.map(row => ({
      // ETAPA 1 - CLIENTE
      'ITEM': row.item || '',
      'CLIENTE': getClienteNombre(row.cliente) || '',
      'COTIZACION': row.cotizacion || '',
      'P. ORDER': row.p_order || '',
      'ITEM2': row.item2 || '',
      'DESCRIPCION': row.descripcion || '',
      'PART NUMBER': row.part_number || '',
      'C.I.N.': row.cin || '',
      'DELIVERY DATE': row.delivery_date || '',
      'REAL DELIVERY': row.real_delivery || '',
      'CANTIDAD': row.cantidad || '',
      'UNIDAD': row.unidad || '',
      'P.U. $CLP': row.pu_clp || '',
      'TOTAL NETO': row.total_neto || '',

      // ETAPA 2 - FACTURACIÓN
      'GUIA': row.guia || '',
      'FACTURA': row.factura || '',
      'EMISION FACT.': row.emision_fact || '',
      'VENCIMIENTO': row.vencimiento || '',
      'FECHA DE PAGO': row.fecha_pago || '',
      'PRONTO PAGO': row.pronto_pago || '',
      'UTILIDAD FACTOR': row.utilidad_factor || '',

      // ETAPA 3 - PROVEEDOR
      'PROVEEDOR': getProveedorNombre(row.proveedor) || '',
      'OC PROVEEDOR': row.oc_sobek || '',
      'PU PROVEEDOR': row.pu_proveedor || '',
      'TOTAL PROVEEDOR': row.total_proveedor || '',
      'U. DEL NEGOCIO': row.u_del_negocio || '',
      'FACTURA PROVEEDOR': row.factura_proveedor || '',
      'EMISION': row.emision_prov || '',
      'VENCIMIENTO_1': row.vencimiento_prov || '',
      'FECHA DE PAGO_1': row.fecha_pago_prov || '',

      // ETAPA 4 - OBSERVACIONES
      'OBSERVACIONES': row.observaciones || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Configurar anchos de columnas para mejor visualización
    const colWidths = [
      { wch: 8 },  // ITEM
      { wch: 30 }, // CLIENTE
      { wch: 15 }, // COTIZACION
      { wch: 15 }, // P. ORDER
      { wch: 8 },  // ITEM2
      { wch: 40 }, // DESCRIPCION
      { wch: 15 }, // PART NUMBER
      { wch: 15 }, // C.I.N.
      { wch: 12 }, // DELIVERY DATE
      { wch: 12 }, // REAL DELIVERY
      { wch: 10 }, // CANTIDAD
      { wch: 8 },  // UNIDAD
      { wch: 12 }, // P.U. $CLP
      { wch: 15 }, // TOTAL NETO
      { wch: 12 }, // GUIA
      { wch: 12 }, // FACTURA
      { wch: 12 }, // EMISION FACT.
      { wch: 12 }, // VENCIMIENTO
      { wch: 12 }, // FECHA DE PAGO
      { wch: 12 }, // PRONTO PAGO
      { wch: 12 }, // UTILIDAD FACTOR
      { wch: 30 }, // PROVEEDOR
      { wch: 15 }, // OC PROVEEDOR
      { wch: 12 }, // PU PROVEEDOR
      { wch: 15 }, // TOTAL PROVEEDOR
      { wch: 12 }, // U. DEL NEGOCIO
      { wch: 15 }, // FACTURA PROVEEDOR
      { wch: 12 }, // EMISION
      { wch: 12 }, // VENCIMIENTO_1
      { wch: 12 }, // FECHA DE PAGO_1
      { wch: 40 }, // OBSERVACIONES
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MAESTRO SUPPLY');
    XLSX.writeFile(wb, `Maestro_Supply_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Optimizado con useCallback para evitar recrear la función en cada render
  const addNewRow = useCallback(() => {
    // Generar ID temporal único para la nueva fila
    const tempId = `new_${Date.now()}`;
    const newRow = { id: tempId, item: data.length + 1, isNew: true };
    etapas.forEach(etapa => {
      etapa.columnas.forEach(col => {
        if (!newRow[col.key]) newRow[col.key] = '';
      });
    });
    setData(prevData => [...prevData, newRow]); // Usar función callback
    setHasChanges(true);
    setNewRowId(tempId); // Marcar como nueva fila para efecto visual

    // Hacer scroll al final después de agregar la fila
    setTimeout(() => {
      // Determinar qué contenedor hacer scroll según el modo de vista
      const scrollContainer = viewMode === 'cards' ? cardsContainerRef.current : tableWrapperRef.current;

      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth'
        });
      }

      // En móvil, también hacer scroll en el layout-content principal
      const layoutContent = document.querySelector('.layout-content');
      if (layoutContent) {
        layoutContent.scrollTo({
          top: layoutContent.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 300); // Aumentar timeout para asegurar que el DOM se actualice
  }, [data.length, viewMode]); // Depende de la longitud y del modo de vista

  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      // Filtrar solo los IDs de registros que ya existen en BD
      const existingIds = selectedRows.filter(id => id && typeof id === 'number');

      if (existingIds.length > 0) {
        // Eliminar del backend
        await api.delete('/matriz/maestro-supply/bulk', {
          data: { ids: existingIds }
        });
      }

      // Eliminar del estado local
      setData(data.filter(row => !selectedRows.includes(row.id)));
      setSelectedRows([]);
      setHasChanges(true);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting rows:', error);
      alert('Error al eliminar registros');
      setShowDeleteConfirm(false);
    }
  };

  const handleCellEdit = (rowId, field, value) => {
    console.log('📝 handleCellEdit - rowId:', rowId, 'field:', field, 'value:', value);

    // Quitar el efecto visual si es una fila nueva que se está editando
    if (rowId === newRowId) {
      setNewRowId(null);
    }

    const newData = data.map(row => {
      if (row.id === rowId) {
        const updatedRow = { ...row, [field]: value };
        console.log('✏️ Fila actualizada:', updatedRow);

        // Cálculos automáticos
        if (field === 'cantidad' || field === 'pu_clp') {
          updatedRow.total_neto = (parseFloat(updatedRow.cantidad) || 0) * (parseFloat(updatedRow.pu_clp) || 0);
        }
        if (field === 'cantidad' || field === 'pu_proveedor') {
          updatedRow.total_proveedor = (parseFloat(updatedRow.cantidad) || 0) * (parseFloat(updatedRow.pu_proveedor) || 0);
        }
        if (['cantidad', 'pu_clp', 'pu_proveedor'].includes(field)) {
          updatedRow.u_del_negocio = calculateUtilidad(updatedRow.total_neto, updatedRow.total_proveedor);
        }

        return updatedRow;
      }
      return row;
    });

    setData(newData);
    setHasChanges(true);
    setEditingCell(null);
    setInlineMultiDocEdit(null);

    // Si se editó un campo de documento, sincronizar la referencia en los docs asociados
    // (solo para tipos single-doc; multi-doc tiene refs individuales por doc)
    if (FIELD_DOC_TYPE_MAP[field] && !MULTI_DOC_TYPES.has(FIELD_DOC_TYPE_MAP[field])) {
      const row = newData.find(r => r.id === rowId);
      if (row) {
        api.patch('/documents/update-reference', {
          item_number: row.item,
          doc_type: FIELD_DOC_TYPE_MAP[field],
          new_reference: value || ''
        }).catch(err => console.error('Error syncing doc reference:', err));
      }
    }

    // Si estamos en modo datos incompletos, avanzar a la siguiente celda faltante
    if (isIncompleteMode) {
      // Recalcular celdas incompletas usando newData (data aún no se actualizó en el state)
      const remainingCells = [];
      newData.forEach(r => {
        const missing = getMissingFields(r);
        missing.forEach(f => remainingCells.push({ row: r, field: f }));
      });

      setTimeout(() => {
        if (remainingCells.length === 0) {
          exitIncompleteMode();
        } else {
          const nextIndex = Math.min(currentIncompleteIndex, remainingCells.length - 1);
          setCurrentIncompleteIndex(nextIndex);
          navigateToIncompleteCell(remainingCells, nextIndex);
          setTimeout(() => openCellForEditing(remainingCells[nextIndex].row, remainingCells[nextIndex].field), 100);
        }
      }, 50);
    }
  };

  const formatValue = (value, key, row = null) => {
    if (!value) return '';

    // Mapear IDs de clientes a nombres
    if (key === 'cliente') {
      return getClienteNombre(value);
    }

    // Mapear IDs de proveedores a nombres
    if (key === 'proveedor') {
      return getProveedorNombre(value);
    }

    // Formatear fechas de yyyy-mm-dd a dd/mm/yyyy
    if (['delivery_date', 'real_delivery', 'emision_fact', 'vencimiento', 'fecha_pago', 'emision_prov', 'vencimiento_prov', 'fecha_pago_prov'].includes(key)) {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-');
        return `${day}/${month}/${year}`;
      }
      return value;
    }

    if (['pu_clp', 'total_neto', 'pu_proveedor', 'total_proveedor', 'u_del_negocio'].includes(key)) {
      const num = parseFloat(value);

      // Si el valor es null, undefined, NaN o <= 0, mostrar guion
      if (value === null || value === undefined || isNaN(num) || num <= 0) {
        return '-';
      }

      // Si es u_del_negocio, verificar que los datos necesarios estén presentes
      if (key === 'u_del_negocio' && row) {
        const neto = parseFloat(row.total_neto);
        const proveedor = parseFloat(row.total_proveedor);
        // Si alguno de los dos es inválido o <= 0, no mostrar utilidad
        if (isNaN(neto) || neto <= 0 || isNaN(proveedor) || proveedor <= 0) {
          return '-';
        }
      }

      return `$${num.toLocaleString('es-CL')}`;
    }

    if (['cantidad', 'item', 'item2', 'utilidad_factor'].includes(key)) {
      const num = parseFloat(value) || 0;
      // Si el número es un entero, mostrarlo sin decimales
      return num % 1 === 0 ? parseInt(num) : num;
    }

    return value;
  };

  // Calcular utilidad del negocio solo si ambos valores son válidos
  const calculateUtilidad = (total_neto, total_proveedor) => {
    const neto = parseFloat(total_neto);
    const proveedor = parseFloat(total_proveedor);

    // Solo calcular si ambos valores son válidos (> 0)
    if (isNaN(neto) || neto <= 0 || isNaN(proveedor) || proveedor <= 0) {
      return null; // Retornar null si faltan datos
    }

    return neto - proveedor;
  };

  const saveData = async () => {
    try {
      // Función auxiliar para limpiar y normalizar fechas
      const cleanDate = (dateValue, fieldName = '') => {
        if (!dateValue || dateValue === '' || dateValue === 'Invalid date' || dateValue === 'undefined') {
          return null;
        }

        // Si es un rango de fechas (dd-mm-yyyy - dd-mm-yyyy), retornarlo tal cual
        if (typeof dateValue === 'string' && dateValue.includes(' - ')) {
          console.log(`📅 cleanDate: ${fieldName} es un rango, retornando tal cual:`, dateValue);
          return dateValue;
        }

        // Si ya está en formato yyyy-mm-dd, retornar tal cual
        if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
          return dateValue;
        }

        // Si está en formato dd-mm-yyyy o dd/mm/yyyy, convertir a yyyy-mm-dd
        if (typeof dateValue === 'string' && /^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateValue)) {
          const parts = dateValue.split(/[-\/]/);
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];
          return `${year}-${month}-${day}`;
        }

        // Para otros formatos, intentar parsear con Date
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) {
          console.log(`⚠️ cleanDate: ${fieldName} tiene fecha inválida:`, dateValue);
          return null;
        }

        // Convertir a yyyy-mm-dd en zona horaria local (sin cambios de día)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Log para debug de fechas
      if (data[0]) {
        console.log('🔍 PRIMER ROW - delivery_date:', data[0].delivery_date, 'real_delivery:', data[0].real_delivery);
      }

      // Mapear los datos del frontend al formato del backend
      const mappedData = data.map(row => ({
        item: row.item,
        cliente: row.cliente,
        cotizacion: row.cotizacion,
        p_order: row.p_order,
        item2: row.item2,
        descripcion: row.descripcion,
        part_number: row.part_number,
        cin: row.cin,
        delivery_date: cleanDate(row.delivery_date, 'delivery_date'),
        real_delivery: cleanDate(row.real_delivery, 'real_delivery'),
        cantidad: row.cantidad,
        unidad: row.unidad,
        pu_clp: row.pu_clp,
        total_neto: row.total_neto,
        guia: row.guia,
        factura: row.factura,
        emision_fact: cleanDate(row.emision_fact, 'emision_fact'),
        vencimiento: cleanDate(row.vencimiento, 'vencimiento'),
        fecha_pago: cleanDate(row.fecha_pago, 'fecha_pago'),
        pronto_pago: row.pronto_pago,
        utilidad_factor: row.utilidad_factor,
        proveedor: row.proveedor,
        oc_sobek: row.oc_sobek,
        pu_proveedor: row.pu_proveedor,
        total_proveedor: row.total_proveedor,
        u_del_negocio: row.u_del_negocio,
        factura_proveedor: row.factura_proveedor,
        emision_prov: cleanDate(row.emision_prov, 'emision_prov'),
        vencimiento_prov: cleanDate(row.vencimiento_prov, 'vencimiento_prov'),
        fecha_pago_prov: cleanDate(row.fecha_pago_prov, 'fecha_pago_prov'),
        observaciones: row.observaciones
      }));

      // Usar el endpoint de actualización masiva
      const response = await api.post('/matriz/maestro-supply/bulk', {
        data: mappedData
      });

      if (response.data?.message) {
        setHasChanges(false);
        pendingDocUploadsRef.current = []; // docs are now permanent
        setSuccessMessage('Datos guardados exitosamente');
        setShowSuccessModal(true);
        // Recargar los datos desde el backend
        await loadOrdersFromBackend();
        // Esperar a que el activity logger registre la actividad (es asíncrono en el backend)
        await new Promise(resolve => setTimeout(resolve, 500));
        // Recargar colaboradores y historial para mostrar badges actualizados
        await Promise.all([loadCollaborators(), loadChangesHistory()]);
      }
    } catch (error) {
      console.error('Error saving data:', error);
      alert('❌ Error al guardar los datos');
    }
  };

  // Optimización: Extraer valores únicos solo cuando showFiltersMenu está abierto
  useEffect(() => {
    // Solo calcular si el menú está abierto
    if (!showFiltersMenu) return;

    const extractUniqueValues = () => {
      const uniqueValues = {};

      const filterableFields = [
        { key: 'cliente', label: 'Cliente' },
        { key: 'proveedor', label: 'Proveedor' },
        { key: 'unidad', label: 'Unidad' },
        { key: 'item', label: 'Item' },
        { key: 'cotizacion', label: 'Cotización' },
        { key: 'p_order', label: 'P. Order' },
        { key: 'item2', label: 'Item2' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'nro_parte', label: 'Nro. Parte' },
        { key: 'cin', label: 'C.I.N.' },
        { key: 'guia', label: 'Guía' },
        { key: 'factura', label: 'Factura' },
        { key: 'oc_sobek', label: 'OC Proveedor' },
        { key: 'factura_proveedor', label: 'Factura Proveedor' },
        { key: 'observaciones', label: 'Observaciones' }
      ];

      filterableFields.forEach(field => {
        let values;

        if (field.key === 'cliente') {
          // Mapear IDs de clientes a nombres
          values = [...new Set(
            data
              .map(row => row[field.key])
              .filter(val => val && val !== '')
              .map(val => getClienteNombre(val))
          )].sort();
        } else if (field.key === 'proveedor') {
          // Mapear IDs de proveedores a nombres
          values = [...new Set(
            data
              .map(row => row[field.key])
              .filter(val => val && val !== '')
              .map(val => getProveedorNombre(val))
          )].sort();
        } else {
          // Para otros campos, usar valores directos
          values = [...new Set(
            data
              .map(row => row[field.key])
              .filter(val => val && val !== '')
          )].sort();
        }

        uniqueValues[field.key] = values;
      });

      setAvailableFilterValues(uniqueValues);
    };

    if (data.length > 0) {
      extractUniqueValues();
    }
  }, [data, showFiltersMenu, clients, suppliers]); // Solo recalcular cuando cambie data, showFiltersMenu o las listas

  // Click outside para cerrar el menú de filtros
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Verificar si el click fue fuera del menú Y del botón de filtros
      const isOutsideMenu = filtersMenuRef.current && !filtersMenuRef.current.contains(event.target);
      const isOutsideButton = filterButtonRef.current && !filterButtonRef.current.contains(event.target);

      if (isOutsideMenu && isOutsideButton) {
        setShowFiltersMenu(false);
      }
    };

    if (showFiltersMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFiltersMenu]);

  // Aplicar filtros - Optimizado con useCallback
  const applyFilters = useCallback((dataToFilter) => {
    let filtered = dataToFilter;

    // Filtros por checkboxes
    Object.keys(activeFilters).forEach(filterKey => {
      const filterValues = activeFilters[filterKey];
      if (filterValues && filterValues.length > 0) {
        filtered = filtered.filter(row => {
          let valueToCompare = row[filterKey];

          // Mapear IDs a nombres para comparación
          if (filterKey === 'cliente') {
            valueToCompare = getClienteNombre(row[filterKey]);
          } else if (filterKey === 'proveedor') {
            valueToCompare = getProveedorNombre(row[filterKey]);
          }

          return filterValues.includes(valueToCompare);
        });
      }
    });

    // Filtros por fecha
    const parseDateString = (dateStr) => {
      if (!dateStr || dateStr === 'dd/mm/aaaa' || dateStr === '') return null;

      // Si es formato dd/mm/yyyy
      if (dateStr.includes('/')) {
        const [day, month, year] = dateStr.split('/');
        if (day && month && year) {
          return new Date(year, month - 1, day);
        }
      }
      // Si es formato DD-MM-YYYY (2 dígitos día)
      else if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('-');
        return new Date(year, month - 1, day);
      }
      // Si es formato yyyy-mm-dd
      else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return new Date(dateStr);
      }

      return null;
    };

    // Filtro Fecha Entrega
    if (dateFilters.fecha_entrega_start || dateFilters.fecha_entrega_end) {
      filtered = filtered.filter(row => {
        const rowDate = parseDateString(row.fecha_entrega);
        if (!rowDate) return false;

        const startDate = dateFilters.fecha_entrega_start ? new Date(dateFilters.fecha_entrega_start) : null;
        const endDate = dateFilters.fecha_entrega_end ? new Date(dateFilters.fecha_entrega_end) : null;

        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;

        return true;
      });
    }

    // Filtro Entrega Real
    if (dateFilters.entrega_real_start || dateFilters.entrega_real_end) {
      filtered = filtered.filter(row => {
        const rowDate = parseDateString(row.entrega_real);
        if (!rowDate) return false;

        const startDate = dateFilters.entrega_real_start ? new Date(dateFilters.entrega_real_start) : null;
        const endDate = dateFilters.entrega_real_end ? new Date(dateFilters.entrega_real_end) : null;

        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;

        return true;
      });
    }

    // Filtro Emisión
    if (dateFilters.emision_start || dateFilters.emision_end) {
      filtered = filtered.filter(row => {
        const rowDate = parseDateString(row.emision);
        if (!rowDate) return false;

        const startDate = dateFilters.emision_start ? new Date(dateFilters.emision_start) : null;
        const endDate = dateFilters.emision_end ? new Date(dateFilters.emision_end) : null;

        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;

        return true;
      });
    }

    // Filtro Vencimiento
    if (dateFilters.vencimiento_start || dateFilters.vencimiento_end) {
      filtered = filtered.filter(row => {
        const rowDate = parseDateString(row.vencimiento);
        if (!rowDate) return false;

        const startDate = dateFilters.vencimiento_start ? new Date(dateFilters.vencimiento_start) : null;
        const endDate = dateFilters.vencimiento_end ? new Date(dateFilters.vencimiento_end) : null;

        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;

        return true;
      });
    }

    // Filtro Fecha Pago
    if (dateFilters.fecha_pago_start || dateFilters.fecha_pago_end) {
      filtered = filtered.filter(row => {
        const rowDate = parseDateString(row.fecha_pago);
        if (!rowDate) return false;

        const startDate = dateFilters.fecha_pago_start ? new Date(dateFilters.fecha_pago_start) : null;
        const endDate = dateFilters.fecha_pago_end ? new Date(dateFilters.fecha_pago_end) : null;

        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;

        return true;
      });
    }

    return filtered;
  }, [activeFilters, dateFilters, clients, suppliers]); // Dependencias: solo recalcular cuando cambien los filtros o las listas

  // Helper para convertir cualquier formato de fecha a YYYY-MM-DD (para input type="date")
  const toInputDate = useCallback((val) => {
    if (!val || typeof val !== 'string') return '';
    // Ya es YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
    // DD-MM-YYYY o DD/MM/YYYY → YYYY-MM-DD
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(val)) {
      const parts = val.split(/[-\/]/);
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return '';
  }, []);

  // Helper para parsear fechas en cualquier formato (DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD)
  const parseFlexibleDate = useCallback((val) => {
    if (!val || typeof val !== 'string') return null;
    // DD-MM-YYYY o DD/MM/YYYY
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(val)) {
      const parts = val.split(/[-\/]/);
      const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      return isNaN(d.getTime()) ? null : d;
    }
    // YYYY-MM-DD
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }, []);

  // Available years from delivery_date (computed from raw data, before filters)
  const statsAvailableYears = useMemo(() => {
    const years = new Set();
    data.forEach(row => {
      if (row.delivery_date) {
        const d = parseFlexibleDate(row.delivery_date);
        if (d) years.add(d.getFullYear());
      }
    });
    return [...years].sort((a, b) => b - a);
  }, [data, parseFlexibleDate]);

  const toggleStatsMonth = (m) => {
    setStatsMonths(prev =>
      prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
    );
  };

  // Optimización: Usar useMemo para filteredData para evitar recalcular en cada render
  const filteredData = useMemo(() => {
    let result = data;

    // Aplicar filtro de año/mes (delivery_date)
    if (statsYear || statsMonths.length > 0) {
      result = result.filter(row => {
        // Filas sin delivery_date siempre pasan el filtro (nuevas o sin fecha asignada)
        if (!row.delivery_date) return true;
        const d = parseFlexibleDate(row.delivery_date);
        if (!d) return true; // Si no se puede parsear, mostrar la fila
        if (statsYear && d.getFullYear() !== parseInt(statsYear)) return false;
        if (statsMonths.length > 0 && !statsMonths.includes(d.getMonth() + 1)) return false;
        return true;
      });
    }

    // Aplicar búsqueda de texto
    if (searchText) {
      result = result.filter(row =>
        Object.values(row).some(val =>
          String(val).toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    // Aplicar filtros
    result = applyFilters(result);

    return result;
  }, [data, searchText, activeFilters, dateFilters, statsYear, statsMonths, parseFlexibleDate]);

  const MONTH_NAMES_MAESTRO = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  return (
    <div className="matriz-page">
      {/* Banner de Modo Historial - Franja LED Minimalista */}
      {isHistoryMode && showHistoryBanner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(90deg, var(--theme-secondary, #000) 0%, var(--theme-primary, #c4fffc) 50%, var(--theme-secondary, #000) 100%)',
          padding: '6px 16px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          fontFamily: "'Space Grotesk', sans-serif",
          animation: 'ledPulse 2s ease-in-out infinite'
        }}>
          <style>
            {`
              @keyframes ledPulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.85; }
              }
            `}
          </style>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '12px',
            fontWeight: '700',
            color: 'var(--theme-secondary, #000)',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            <span style={{ fontSize: '16px' }}>⏳</span>
            MODO HISTORIAL • SOLO VISUALIZACIÓN
          </div>
          <button
            onClick={() => setShowHistoryBanner(false)}
            style={{
              position: 'absolute',
              right: '16px',
              background: 'transparent',
              border: 'none',
              color: 'var(--theme-secondary, #000)',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
            title="Ocultar banner"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Maestro</h1>
          <p style={{ fontWeight: 'bold', fontSize: '15px' }}>Gestión de órdenes de compra y proveedores</p>
        </div>
        <div className="header-actions">
          <button onClick={exportToExcel} className="btn btn-secondary">
            <Download size={16} />
            Exportar
          </button>
          <button
            onClick={addNewRow}
            className="btn btn-primary"
            disabled={loadingData}
            style={{ opacity: loadingData ? 0.5 : 1, cursor: loadingData ? 'not-allowed' : 'pointer' }}
          >
            <Plus size={16} />
            Nueva Fila
          </button>
        </div>
      </div>

      {/* Filtros de Año/Mes */}
      <div className="maestro-date-filters">
        <div className="maestro-date-filter-group">
          <Calendar size={16} />
          <span className="maestro-filter-label">Año:</span>
          <div className="maestro-year-pills">
            <button
              className={`maestro-year-pill ${statsYear === '' ? 'active' : ''}`}
              onClick={() => setStatsYear('')}
            >
              Todos
            </button>
            {statsAvailableYears.map(year => (
              <button
                key={year}
                className={`maestro-year-pill ${statsYear === String(year) ? 'active' : ''}`}
                onClick={() => setStatsYear(String(year))}
              >
                {year}
              </button>
            ))}
          </div>
          <span className="maestro-filter-separator">|</span>
          <span className="maestro-filter-label">Mes:</span>
          <div className="maestro-month-pills">
            {MONTH_NAMES_MAESTRO.map((name, i) => (
              <button
                key={i + 1}
                className={`maestro-month-pill ${statsMonths.includes(i + 1) ? 'active' : ''}`}
                onClick={() => toggleStatsMonth(i + 1)}
              >
                {name}
              </button>
            ))}
          </div>
          {statsMonths.length > 0 && (
            <button className="maestro-clear-months-btn" onClick={() => setStatsMonths([])}>
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards - Resumen General */}
      <div className="maestro-stats-cards">
        <div className="maestro-stat-card">
          <h3>{filteredData.length}</h3>
          <p>Total Órdenes</p>
        </div>
        <div className="maestro-stat-card">
          <h3>
            ${(() => {
              const isRowIncomplete = (row) => {
                const cantidad = parseFloat(row.cantidad);
                const pu_clp = parseFloat(row.pu_clp);
                const pu_proveedor = parseFloat(row.pu_proveedor);

                return isNaN(cantidad) || cantidad <= 0 ||
                       isNaN(pu_clp) || pu_clp <= 0 ||
                       isNaN(pu_proveedor) || pu_proveedor <= 0;
              };

              const total = filteredData.reduce((sum, row) => {
                if (isRowIncomplete(row)) return sum;
                const val = parseFloat(row.total_neto);
                return sum + (isNaN(val) ? 0 : val);
              }, 0);
              return total.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            })()}
          </h3>
          <p>Volumen Ventas</p>
        </div>
        <div className="maestro-stat-card">
          <h3>
            ${(() => {
              const isRowIncomplete = (row) => {
                const cantidad = parseFloat(row.cantidad);
                const pu_clp = parseFloat(row.pu_clp);
                const pu_proveedor = parseFloat(row.pu_proveedor);

                return isNaN(cantidad) || cantidad <= 0 ||
                       isNaN(pu_clp) || pu_clp <= 0 ||
                       isNaN(pu_proveedor) || pu_proveedor <= 0;
              };

              const total = filteredData.reduce((sum, row) => {
                if (isRowIncomplete(row)) return sum;
                const val = parseFloat(row.total_proveedor);
                return sum + (isNaN(val) ? 0 : val);
              }, 0);
              return total.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            })()}
          </h3>
          <p>Costo Total</p>
        </div>
        <div className="maestro-stat-card">
          <h3 style={{ color: '#10b981' }}>
            ${(() => {
              const isRowIncomplete = (row) => {
                const cantidad = parseFloat(row.cantidad);
                const pu_clp = parseFloat(row.pu_clp);
                const pu_proveedor = parseFloat(row.pu_proveedor);

                return isNaN(cantidad) || cantidad <= 0 ||
                       isNaN(pu_clp) || pu_clp <= 0 ||
                       isNaN(pu_proveedor) || pu_proveedor <= 0;
              };

              const totalNeto = filteredData.reduce((sum, row) => {
                if (isRowIncomplete(row)) return sum;
                const val = parseFloat(row.total_neto);
                return sum + (isNaN(val) ? 0 : val);
              }, 0);
              const totalCosto = filteredData.reduce((sum, row) => {
                if (isRowIncomplete(row)) return sum;
                const val = parseFloat(row.total_proveedor);
                return sum + (isNaN(val) ? 0 : val);
              }, 0);
              const utilidad = totalNeto - totalCosto;
              return utilidad.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            })()}
          </h3>
          <p>Utilidad Total</p>
        </div>
        <div
          className="maestro-stat-card"
          onClick={() => {
            const incompleteCount = data.filter(row => isRowIncomplete(row)).length;
            if (incompleteCount > 0) {
              startIncompleteMode();
            }
          }}
          style={{
            background: (() => {
              const incompleteCount = filteredData.filter(row => isRowIncomplete(row)).length;
              return incompleteCount > 0 ? '#fef3c7' : undefined;
            })(),
            cursor: (() => {
              const incompleteCount = filteredData.filter(row => isRowIncomplete(row)).length;
              return incompleteCount > 0 ? 'pointer' : 'default';
            })(),
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            const incompleteCount = filteredData.filter(row => isRowIncomplete(row)).length;
            if (incompleteCount > 0) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(217, 119, 6, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '';
          }}
        >
          {(() => {
            const incompleteCells = [];
            filteredData.forEach(row => {
              const missing = getMissingFields(row);
              missing.forEach(f => incompleteCells.push({ row, field: f }));
            });
            const totalCells = incompleteCells.length;
            const totalRows = filteredData.filter(row => isRowIncomplete(row)).length;
            return (
              <>
                <h3 style={{ color: totalCells > 0 ? '#d97706' : undefined }}>
                  {totalCells}
                </h3>
                <p style={{ color: totalCells > 0 ? '#92400e' : undefined }}>
                  {totalCells > 0 ? `Datos Incompletos (${totalRows} filas)` : 'Datos Incompletos'}
                </p>
              </>
            );
          })()}
        </div>
      </div>

      {/* Collaborators Section - solo mostrar cuando hay datos guardados del backend */}
      {collaborators.length > 0 && showCollaborators && data.some(row => !row.isNew) && (
        <div style={{
          background: 'white',
          border: '2px solid var(--theme-secondary, #000)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
            <Users size={24} style={{ color: 'var(--theme-secondary, #000)' }} />
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '700',
                color: 'var(--theme-secondary, #000)',
                marginBottom: '4px',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                Colaboradores ({collaborators.length})
              </h3>
              <p style={{ fontSize: '13px', color: '#6b7280' }}>
                Usuarios que han trabajado en este Maestro
              </p>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            {collaborators.slice(0, 5).map((collab, idx) => (
              <div
                key={collab.email}
                title={`${collab.name} (${collab.email})\n${collab.activitiesCount} actividades\nClick para ver en Equipo`}
                onClick={() => navigate('/equipo', { state: { highlightUserEmail: collab.email } })}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'var(--theme-primary, #c4fffc)',
                  border: '2px solid var(--theme-secondary, #000)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'var(--theme-secondary, #000)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                  zIndex: collaborators.length - idx
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.15)';
                  e.currentTarget.style.zIndex = '100';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.zIndex = String(collaborators.length - idx);
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {collab.initials}
              </div>
            ))}

            {collaborators.length > 5 && (
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'var(--theme-secondary, #000)',
                border: '2px solid var(--theme-secondary, #000)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '700',
                color: 'var(--theme-primary, #c4fffc)',
                cursor: 'pointer'
              }}>
                +{collaborators.length - 5}
              </div>
            )}

            <button
              onClick={() => setShowCollaborators(false)}
              style={{
                marginLeft: '12px',
                padding: '8px 12px',
                background: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                color: '#6b7280',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--theme-secondary, #000)';
                e.currentTarget.style.color = 'var(--theme-secondary, #000)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              Ocultar
            </button>
          </div>
        </div>
      )}

      {/* Badge Visibility Controls */}
      <div style={{
        background: 'white',
        border: '2px solid var(--theme-secondary, #000)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '14px',
          fontWeight: '600',
          fontFamily: "'Space Grotesk', sans-serif",
          color: 'var(--theme-secondary, #000)'
        }}>
          👁️ Mostrar en celdas:
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={toggleEditorBadges}
            style={{
              padding: '8px 16px',
              background: showEditorBadges ? 'var(--theme-secondary, #000)' : '#f3f4f6',
              color: showEditorBadges ? 'var(--theme-primary, #c4fffc)' : '#6b7280',
              border: '2px solid var(--theme-secondary, #000)',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: "'Space Grotesk', sans-serif"
            }}
          >
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: 'var(--theme-secondary, #000)',
              border: '1px solid white'
            }} />
            Editores
          </button>

          <button
            onClick={toggleImporterBadges}
            style={{
              padding: '8px 16px',
              background: showImporterBadges ? 'var(--theme-primary, #c4fffc)' : '#f3f4f6',
              color: showImporterBadges ? 'var(--theme-secondary, #000)' : '#6b7280',
              border: '2px solid var(--theme-secondary, #000)',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: "'Space Grotesk', sans-serif"
            }}
          >
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: 'var(--theme-primary, #c4fffc)',
              border: '1px solid var(--theme-secondary, #000)'
            }} />
            Importadores
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="search-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Buscar por cliente, producto..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* View Toggle - SUPER VISIBLE */}
        <div style={{
          display: 'flex',
          gap: '8px',
          background: '#000',
          padding: '8px',
          borderRadius: '12px',
          border: '3px solid #000',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
          flexShrink: 0
        }}>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '12px 20px',
              background: viewMode === 'table' ? 'var(--theme-primary, #c4fffc)' : 'white',
              border: `2px solid ${viewMode === 'table' ? 'var(--theme-primary, #c4fffc)' : '#000'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: viewMode === 'table' ? '800' : '700',
              fontSize: '14px',
              fontFamily: "'Space Grotesk', sans-serif"
            }}
            onMouseOver={(e) => {
              if (viewMode !== 'table') {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.2)';
              }
            }}
            onMouseOut={(e) => {
              if (viewMode !== 'table') {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            <List size={20} />
            <span>Tabla</span>
          </button>
          <button
            onClick={() => setViewMode('cards')}
            style={{
              padding: '12px 20px',
              background: viewMode === 'cards' ? 'var(--theme-primary, #c4fffc)' : 'white',
              border: `2px solid ${viewMode === 'cards' ? 'var(--theme-primary, #c4fffc)' : '#000'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: viewMode === 'cards' ? '800' : '700',
              fontSize: '14px',
              fontFamily: "'Space Grotesk', sans-serif"
            }}
            onMouseOver={(e) => {
              if (viewMode !== 'cards') {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.2)';
              }
            }}
            onMouseOut={(e) => {
              if (viewMode !== 'cards') {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            <Grid size={20} />
            <span>Cards</span>
          </button>
        </div>

        <div className="filter-actions" style={{ position: 'relative' }}>
          <button
            ref={filterButtonRef}
            className="filter-btn"
            onClick={() => setShowFiltersMenu(!showFiltersMenu)}
            style={{
              backgroundColor: Object.keys(activeFilters).some(key => activeFilters[key]?.length > 0)
                ? 'var(--theme-primary, #c4fffc)'
                : 'white',
              color: '#000'
            }}
          >
            <Filter size={16} />
            Filtros
            {Object.keys(activeFilters).filter(key => activeFilters[key]?.length > 0).length > 0 && (
              <span style={{
                marginLeft: '8px',
                backgroundColor: '#000',
                color: 'var(--theme-primary, #c4fffc)',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '700'
              }}>
                {Object.keys(activeFilters).filter(key => activeFilters[key]?.length > 0).length}
              </span>
            )}
          </button>

          {/* Menú de filtros */}
          {showFiltersMenu && (
            <div
              ref={filtersMenuRef}
              style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              backgroundColor: 'white',
              border: '2px solid #000',
              borderRadius: '12px',
              padding: '16px',
              minWidth: '320px',
              maxWidth: '450px',
              maxHeight: '80vh',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid #e5e7eb',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 10
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Filtros</h3>
                {(Object.keys(activeFilters).some(key => activeFilters[key]?.length > 0) ||
                  Object.keys(searchFilters).some(key => searchFilters[key]) ||
                  Object.values(dateFilters).some(val => val)) && (
                  <button
                    onClick={() => {
                      setActiveFilters({});
                      setSearchFilters({});
                      setDateFilters({
                        fecha_entrega_start: '',
                        fecha_entrega_end: '',
                        entrega_real_start: '',
                        entrega_real_end: '',
                        emision_start: '',
                        emision_end: '',
                        vencimiento_start: '',
                        vencimiento_end: '',
                        fecha_pago_start: '',
                        fecha_pago_end: ''
                      });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Limpiar todos
                  </button>
                )}
              </div>

              {/* Filtro por Cliente */}
              {availableFilterValues.cliente?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Cliente
                  </label>
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.cliente.map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.cliente?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.cliente) newFilters.cliente = [];

                            if (e.target.checked) {
                              newFilters.cliente = [...newFilters.cliente, value];
                            } else {
                              newFilters.cliente = newFilters.cliente.filter(v => v !== value);
                            }

                            if (newFilters.cliente.length === 0) {
                              delete newFilters.cliente;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Proveedor */}
              {availableFilterValues.proveedor?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Proveedor
                  </label>
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.proveedor.map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.proveedor?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.proveedor) newFilters.proveedor = [];

                            if (e.target.checked) {
                              newFilters.proveedor = [...newFilters.proveedor, value];
                            } else {
                              newFilters.proveedor = newFilters.proveedor.filter(v => v !== value);
                            }

                            if (newFilters.proveedor.length === 0) {
                              delete newFilters.proveedor;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Unidad */}
              {availableFilterValues.unidad?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Unidad
                  </label>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px'
                  }}>
                    {availableFilterValues.unidad.map(value => (
                      <label key={value} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        backgroundColor: activeFilters.unidad?.includes(value) ? 'var(--theme-primary, #c4fffc)' : 'white',
                        color: '#000',
                        fontWeight: activeFilters.unidad?.includes(value) ? '700' : '500'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.unidad?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.unidad) newFilters.unidad = [];

                            if (e.target.checked) {
                              newFilters.unidad = [...newFilters.unidad, value];
                            } else {
                              newFilters.unidad = newFilters.unidad.filter(v => v !== value);
                            }

                            if (newFilters.unidad.length === 0) {
                              delete newFilters.unidad;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ display: 'none' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Item */}
              {availableFilterValues.item?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Item
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar item..."
                    value={searchFilters.item || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        item: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.item
                      .filter(value => !searchFilters.item ||
                        value.toString().toLowerCase().includes(searchFilters.item.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.item?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.item) newFilters.item = [];

                            if (e.target.checked) {
                              newFilters.item = [...newFilters.item, value];
                            } else {
                              newFilters.item = newFilters.item.filter(v => v !== value);
                            }

                            if (newFilters.item.length === 0) {
                              delete newFilters.item;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Cotización */}
              {availableFilterValues.cotizacion?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Cotización
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar cotización..."
                    value={searchFilters.cotizacion || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        cotizacion: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.cotizacion
                      .filter(value => !searchFilters.cotizacion ||
                        value.toString().toLowerCase().includes(searchFilters.cotizacion.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.cotizacion?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.cotizacion) newFilters.cotizacion = [];

                            if (e.target.checked) {
                              newFilters.cotizacion = [...newFilters.cotizacion, value];
                            } else {
                              newFilters.cotizacion = newFilters.cotizacion.filter(v => v !== value);
                            }

                            if (newFilters.cotizacion.length === 0) {
                              delete newFilters.cotizacion;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por P. Order */}
              {availableFilterValues.p_order?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    P. Order
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar P. Order..."
                    value={searchFilters.p_order || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        p_order: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.p_order
                      .filter(value => !searchFilters.p_order ||
                        value.toString().toLowerCase().includes(searchFilters.p_order.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.p_order?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.p_order) newFilters.p_order = [];

                            if (e.target.checked) {
                              newFilters.p_order = [...newFilters.p_order, value];
                            } else {
                              newFilters.p_order = newFilters.p_order.filter(v => v !== value);
                            }

                            if (newFilters.p_order.length === 0) {
                              delete newFilters.p_order;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Item2 */}
              {availableFilterValues.item2?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Item2
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar item2..."
                    value={searchFilters.item2 || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        item2: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.item2
                      .filter(value => !searchFilters.item2 ||
                        value.toString().toLowerCase().includes(searchFilters.item2.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.item2?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.item2) newFilters.item2 = [];

                            if (e.target.checked) {
                              newFilters.item2 = [...newFilters.item2, value];
                            } else {
                              newFilters.item2 = newFilters.item2.filter(v => v !== value);
                            }

                            if (newFilters.item2.length === 0) {
                              delete newFilters.item2;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Descripción */}
              {availableFilterValues.descripcion?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Descripción
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar descripción..."
                    value={searchFilters.descripcion || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        descripcion: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.descripcion
                      .filter(value => !searchFilters.descripcion ||
                        value.toString().toLowerCase().includes(searchFilters.descripcion.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.descripcion?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.descripcion) newFilters.descripcion = [];

                            if (e.target.checked) {
                              newFilters.descripcion = [...newFilters.descripcion, value];
                            } else {
                              newFilters.descripcion = newFilters.descripcion.filter(v => v !== value);
                            }

                            if (newFilters.descripcion.length === 0) {
                              delete newFilters.descripcion;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Nro. Parte */}
              {availableFilterValues.nro_parte?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Nro. Parte
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar nro. parte..."
                    value={searchFilters.nro_parte || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        nro_parte: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.nro_parte
                      .filter(value => !searchFilters.nro_parte ||
                        value.toString().toLowerCase().includes(searchFilters.nro_parte.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.nro_parte?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.nro_parte) newFilters.nro_parte = [];

                            if (e.target.checked) {
                              newFilters.nro_parte = [...newFilters.nro_parte, value];
                            } else {
                              newFilters.nro_parte = newFilters.nro_parte.filter(v => v !== value);
                            }

                            if (newFilters.nro_parte.length === 0) {
                              delete newFilters.nro_parte;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por C.I.N. */}
              {availableFilterValues.cin?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    C.I.N.
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar C.I.N...."
                    value={searchFilters.cin || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        cin: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.cin
                      .filter(value => !searchFilters.cin ||
                        value.toString().toLowerCase().includes(searchFilters.cin.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.cin?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.cin) newFilters.cin = [];

                            if (e.target.checked) {
                              newFilters.cin = [...newFilters.cin, value];
                            } else {
                              newFilters.cin = newFilters.cin.filter(v => v !== value);
                            }

                            if (newFilters.cin.length === 0) {
                              delete newFilters.cin;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Guía */}
              {availableFilterValues.guia?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Guía
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar guía..."
                    value={searchFilters.guia || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        guia: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.guia
                      .filter(value => !searchFilters.guia ||
                        value.toString().toLowerCase().includes(searchFilters.guia.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.guia?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.guia) newFilters.guia = [];

                            if (e.target.checked) {
                              newFilters.guia = [...newFilters.guia, value];
                            } else {
                              newFilters.guia = newFilters.guia.filter(v => v !== value);
                            }

                            if (newFilters.guia.length === 0) {
                              delete newFilters.guia;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Factura */}
              {availableFilterValues.factura?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Factura
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar factura..."
                    value={searchFilters.factura || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        factura: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.factura
                      .filter(value => !searchFilters.factura ||
                        value.toString().toLowerCase().includes(searchFilters.factura.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.factura?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.factura) newFilters.factura = [];

                            if (e.target.checked) {
                              newFilters.factura = [...newFilters.factura, value];
                            } else {
                              newFilters.factura = newFilters.factura.filter(v => v !== value);
                            }

                            if (newFilters.factura.length === 0) {
                              delete newFilters.factura;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por OC Proveedor */}
              {availableFilterValues.oc_sobek?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    OC Proveedor
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar OC Proveedor..."
                    value={searchFilters.oc_sobek || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        oc_sobek: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.oc_sobek
                      .filter(value => !searchFilters.oc_sobek ||
                        value.toString().toLowerCase().includes(searchFilters.oc_sobek.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.oc_sobek?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.oc_sobek) newFilters.oc_sobek = [];

                            if (e.target.checked) {
                              newFilters.oc_sobek = [...newFilters.oc_sobek, value];
                            } else {
                              newFilters.oc_sobek = newFilters.oc_sobek.filter(v => v !== value);
                            }

                            if (newFilters.oc_sobek.length === 0) {
                              delete newFilters.oc_sobek;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Factura Proveedor */}
              {availableFilterValues.factura_proveedor?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Factura Proveedor
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar factura proveedor..."
                    value={searchFilters.factura_proveedor || ''}
                    onChange={(e) => {
                      setSearchFilters({
                        ...searchFilters,
                        factura_proveedor: e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.factura_proveedor
                      .filter(value => !searchFilters.factura_proveedor ||
                        value.toString().toLowerCase().includes(searchFilters.factura_proveedor.toLowerCase()))
                      .map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.factura_proveedor?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.factura_proveedor) newFilters.factura_proveedor = [];

                            if (e.target.checked) {
                              newFilters.factura_proveedor = [...newFilters.factura_proveedor, value];
                            } else {
                              newFilters.factura_proveedor = newFilters.factura_proveedor.filter(v => v !== value);
                            }

                            if (newFilters.factura_proveedor.length === 0) {
                              delete newFilters.factura_proveedor;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro por Observaciones */}
              {availableFilterValues.observaciones?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Observaciones
                  </label>
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {availableFilterValues.observaciones.map(value => (
                      <label key={value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={activeFilters.observaciones?.includes(value) || false}
                          onChange={(e) => {
                            const newFilters = { ...activeFilters };
                            if (!newFilters.observaciones) newFilters.observaciones = [];

                            if (e.target.checked) {
                              newFilters.observaciones = [...newFilters.observaciones, value];
                            } else {
                              newFilters.observaciones = newFilters.observaciones.filter(v => v !== value);
                            }

                            if (newFilters.observaciones.length === 0) {
                              delete newFilters.observaciones;
                            }

                            setActiveFilters(newFilters);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtros por Fechas */}
              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '2px solid #e5e7eb' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700' }}>Filtros por Fecha</h4>

                {/* Fecha Entrega */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Fecha Entrega
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={dateFilters.fecha_entrega_start}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          fecha_entrega_start: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>a</span>
                    <input
                      type="date"
                      value={dateFilters.fecha_entrega_end}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          fecha_entrega_end: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>

                {/* Entrega Real */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Entrega Real
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={dateFilters.entrega_real_start}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          entrega_real_start: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>a</span>
                    <input
                      type="date"
                      value={dateFilters.entrega_real_end}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          entrega_real_end: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>

                {/* Emisión */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Emisión
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={dateFilters.emision_start}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          emision_start: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>a</span>
                    <input
                      type="date"
                      value={dateFilters.emision_end}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          emision_end: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>

                {/* Vencimiento */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Vencimiento
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={dateFilters.vencimiento_start}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          vencimiento_start: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>a</span>
                    <input
                      type="date"
                      value={dateFilters.vencimiento_end}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          vencimiento_end: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>

                {/* Fecha Pago */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Fecha Pago
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={dateFilters.fecha_pago_start}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          fecha_pago_start: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>a</span>
                    <input
                      type="date"
                      value={dateFilters.fecha_pago_end}
                      onChange={(e) => {
                        setDateFilters({
                          ...dateFilters,
                          fecha_pago_end: e.target.value
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-ghost"
            disabled={loadingData}
            style={{ opacity: loadingData ? 0.5 : 1, cursor: loadingData ? 'not-allowed' : 'pointer' }}
          >
            <Upload size={16} />
            Importar
          </button>

          {selectedRows.length > 0 && (
            <button
              onClick={deleteSelectedRows}
              className="btn btn-ghost delete"
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                fontWeight: 'bold',
                animation: 'pulse 1s infinite'
              }}
            >
              <Trash2 size={16} />
              Eliminar ({selectedRows.length})
            </button>
          )}

          {hasChanges && (
            <button onClick={saveData} className="btn btn-primary">
              <Save size={16} />
              Guardar
            </button>
          )}
        </div>
      </div>

      {/* Historial de Cambios - Compacto - solo mostrar cuando hay datos guardados */}
      {changesHistory.length > 0 && data.some(row => !row.isNew) && (
        <div style={{
          background: 'white',
          border: '2px solid var(--theme-secondary, #000)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          {/* Título e info compacta */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flex: '1 1 auto'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'var(--theme-primary, #c4fffc)',
              border: '2px solid var(--theme-secondary, #000)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <History size={16} style={{ color: 'var(--theme-secondary, #000)' }} />
            </div>

            {currentChangeIndex !== -1 && changesHistory[currentChangeIndex] ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
                fontSize: '13px',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                <span style={{
                  fontWeight: '700',
                  color: 'var(--theme-secondary, #000)'
                }}>
                  Cambio {currentChangeIndex + 1}/{changesHistory.length}:
                </span>
                <span style={{
                  padding: '2px 8px',
                  background: 'var(--theme-secondary, #000)',
                  color: 'var(--theme-primary, #c4fffc)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '700'
                }}>
                  Item {changesHistory[currentChangeIndex].itemNumber}
                </span>
                <span style={{
                  padding: '2px 8px',
                  background: 'var(--theme-primary, #c4fffc)',
                  color: 'var(--theme-secondary, #000)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '700',
                  border: '1px solid var(--theme-secondary, #000)'
                }}>
                  {changesHistory[currentChangeIndex].fieldLabel}
                </span>
                <span style={{ color: '#666666', fontWeight: '600' }}>
                  {changesHistory[currentChangeIndex].oldValue ? formatValue(changesHistory[currentChangeIndex].oldValue, changesHistory[currentChangeIndex].fieldKey) : '(vacío)'}
                </span>
                <span style={{ color: 'var(--theme-secondary, #000)' }}>→</span>
                <span style={{ color: 'var(--theme-secondary, #000)', fontWeight: '700' }}>
                  {changesHistory[currentChangeIndex].newValue ? formatValue(changesHistory[currentChangeIndex].newValue, changesHistory[currentChangeIndex].fieldKey) : '(vacío)'}
                </span>
              </div>
            ) : (
              <span style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                {changesHistory.length} cambios registrados
              </span>
            )}
          </div>

          {/* Controles compactos */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <button
              onClick={navigateToPreviousChange}
              disabled={currentChangeIndex === 0}
              title="Anterior (más antiguo)"
              style={{
                padding: '6px 10px',
                background: currentChangeIndex === 0 ? '#f3f4f6' : 'var(--theme-secondary, #000)',
                color: currentChangeIndex === 0 ? '#9ca3af' : 'var(--theme-primary, #c4fffc)',
                border: '2px solid var(--theme-secondary, #000)',
                borderRadius: '6px',
                cursor: currentChangeIndex === 0 ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s, color 0.2s',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <button
              onClick={navigateToNextChange}
              disabled={currentChangeIndex === -1 || currentChangeIndex >= changesHistory.length - 1}
              title="Siguiente (más reciente)"
              style={{
                padding: '6px 10px',
                background: (currentChangeIndex === -1 || currentChangeIndex >= changesHistory.length - 1) ? '#f3f4f6' : 'var(--theme-secondary, #000)',
                color: (currentChangeIndex === -1 || currentChangeIndex >= changesHistory.length - 1) ? '#9ca3af' : 'var(--theme-primary, #c4fffc)',
                border: '2px solid var(--theme-secondary, #000)',
                borderRadius: '6px',
                cursor: (currentChangeIndex === -1 || currentChangeIndex >= changesHistory.length - 1) ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s, color 0.2s',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronRight size={16} />
            </button>

            {isHistoryMode && (
              <button
                onClick={exitHistoryMode}
                title="Volver al presente"
                style={{
                  padding: '6px 12px',
                  background: 'var(--theme-primary, #c4fffc)',
                  color: 'var(--theme-secondary, #000)',
                  border: '2px solid var(--theme-secondary, #000)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '11px',
                  fontWeight: '700'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--theme-secondary, #000)';
                  e.target.style.color = 'var(--theme-primary, #c4fffc)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'var(--theme-primary, #c4fffc)';
                  e.target.style.color = 'var(--theme-secondary, #000)';
                }}
              >
                <span>⏭️</span>
                Presente
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navegación de Datos Incompletos - Compacto */}
      {isIncompleteMode && incompleteRows.length > 0 && (
        <div style={{
          background: '#fef3c7',
          border: '3px solid #d97706',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
          boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)'
        }}>
          {/* Título e info compacta */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flex: '1 1 auto'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#d97706',
              border: '2px solid #92400e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <span style={{ fontSize: '16px' }}>⚠️</span>
            </div>

            {(() => {
              const allCells = getAllIncompleteCells();
              const currentCell = allCells[currentIncompleteIndex];
              return currentCell ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                  fontSize: '13px',
                  fontFamily: "'Space Grotesk', sans-serif"
                }}>
                  <span style={{
                    fontWeight: '700',
                    color: '#92400e'
                  }}>
                    {currentIncompleteIndex + 1}/{allCells.length} celdas:
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    background: '#92400e',
                    color: '#fef3c7',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '700'
                  }}>
                    Item {currentCell.row.item}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    background: '#d97706',
                    color: '#fff',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '700'
                  }}>
                    {currentCell.field.label}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    color: '#92400e'
                  }}>
                    ({incompleteRows.length} filas)
                  </span>
                </div>
              ) : (
                <span style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#92400e',
                  fontFamily: "'Space Grotesk', sans-serif"
                }}>
                  {allCells.length} celdas incompletas en {incompleteRows.length} filas
                </span>
              );
            })()}
          </div>

          {/* Controles compactos */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <button
              onClick={navigateToPreviousIncomplete}
              disabled={currentIncompleteIndex <= 0}
              title="Anterior"
              style={{
                padding: '6px 10px',
                background: currentIncompleteIndex <= 0 ? '#fef3c7' : '#92400e',
                color: currentIncompleteIndex <= 0 ? '#d97706' : '#fef3c7',
                border: '2px solid #92400e',
                borderRadius: '6px',
                cursor: currentIncompleteIndex <= 0 ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s, color 0.2s',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <button
              onClick={navigateToNextIncomplete}
              disabled={currentIncompleteIndex === -1 || currentIncompleteIndex >= getAllIncompleteCells().length - 1}
              title="Siguiente"
              style={{
                padding: '6px 10px',
                background: (currentIncompleteIndex === -1 || currentIncompleteIndex >= getAllIncompleteCells().length - 1) ? '#fef3c7' : '#92400e',
                color: (currentIncompleteIndex === -1 || currentIncompleteIndex >= getAllIncompleteCells().length - 1) ? '#d97706' : '#fef3c7',
                border: '2px solid #92400e',
                borderRadius: '6px',
                cursor: (currentIncompleteIndex === -1 || currentIncompleteIndex >= getAllIncompleteCells().length - 1) ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s, color 0.2s',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronRight size={16} />
            </button>

            <button
              onClick={exitIncompleteMode}
              style={{
                padding: '6px 16px',
                background: '#d97706',
                color: '#fff',
                border: '2px solid #92400e',
                borderRadius: '6px',
                cursor: 'pointer',
                marginLeft: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '11px',
                fontWeight: '700'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#92400e';
                e.target.style.color = '#fef3c7';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#d97706';
                e.target.style.color = '#fff';
              }}
            >
              <span>✓</span>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Table or Cards View */}
      {viewMode === 'table' ? (
        /* Table Container */
        <div className="table-container" style={{
          height: filteredData.length === 0 ? 'auto' : undefined
        }}>
        <div className="table-wrapper" ref={tableWrapperRef} style={{
          overflow: 'auto',
          height: filteredData.length === 0 ? 'auto' : '100%',
          width: '100%',
          minHeight: filteredData.length === 0 ? '0' : undefined
        }}>
          <div className="table-scroll" style={{
            width: 'max-content',
            '--zoom-level': zoomLevel / 100
          }}>
          <table className="data-table">
            {/* Etapas Headers */}
            <thead>
              <tr className="etapas-row">
                <th className="fixed-header-cell" style={{backgroundColor: '#6b7280', border: '2px solid #000', width: '60px', minWidth: '60px', position: 'sticky', left: '0', zIndex: 22, top: '0'}}></th>
                <th className="fixed-header-cell" style={{backgroundColor: '#6b7280', border: '2px solid #000', width: '32px', minWidth: '32px', position: 'sticky', left: '60px', zIndex: 22, top: '0'}}></th>
                <th style={{backgroundColor: '#6b7280', border: '2px solid #000', width: '38px', minWidth: '38px', position: 'sticky', left: '92px', zIndex: 21, top: '0'}}></th>
                <th style={{backgroundColor: '#6b7280', border: '2px solid #000', width: '36px', minWidth: '36px', position: 'sticky', left: '130px', zIndex: 21, top: '0'}}></th>
                {filteredEtapas.map(etapa => (
                  <th
                    key={etapa.id}
                    colSpan={etapa.columnas.length}
                    style={{
                      background: etapa.id === 'etapa1' ? '#7FFFD4' :
                                 etapa.id === 'etapa2' ? '#FF69B4' :
                                 etapa.id === 'etapa3' ? '#FFA500' :
                                 etapa.id === 'etapa4' ? '#BA55D3' : '#fff',
                      color: '#000',
                      fontSize: '14px',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      padding: '12px',
                      textAlign: 'center',
                      border: '2px solid #000',
                      textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                      position: 'sticky',
                      top: '0',
                      zIndex: 20
                    }}
                  >
                    {etapa.nombre}
                  </th>
                ))}
              </tr>
              {/* Column Headers */}
              <tr className="columns-row">
                <th className="fixed-header-cell" style={{backgroundColor: '#6b7280', textAlign: 'center', border: '2px solid #000', width: '60px', minWidth: '60px', position: 'sticky', left: '0', zIndex: 17, top: '40px'}}>
                  <Users size={16} style={{ color: 'white' }} />
                </th>
                <th className="fixed-header-cell" style={{backgroundColor: '#6b7280', textAlign: 'center', border: '2px solid #000', width: '32px', minWidth: '32px', position: 'sticky', left: '60px', zIndex: 17, top: '40px'}}>
                  <input
                    type="checkbox"
                    checked={selectedRows.length === filteredData.length && filteredData.length > 0}
                    onChange={(e) => {
                      e.stopPropagation();
                      // Optimización: pre-calcular los IDs una sola vez
                      if (e.target.checked) {
                        // Usar un bucle simple es más rápido que map para arrays grandes
                        const ids = new Array(filteredData.length);
                        for (let i = 0; i < filteredData.length; i++) {
                          ids[i] = filteredData[i].id;
                        }
                        setSelectedRows(ids);
                      } else {
                        setSelectedRows([]);
                      }
                    }}
                    title="Seleccionar todo"
                    style={{ cursor: 'pointer', background: 'white', border: '2px solid #000' }}
                  />
                </th>
                <th style={{backgroundColor: '#6b7280', fontWeight: 'bold', textAlign: 'center', border: '2px solid #000', width: '38px', minWidth: '38px', color: 'white', position: 'sticky', left: '92px', zIndex: 16, top: '40px'}}>#</th>
                <th style={{backgroundColor: '#6b7280', textAlign: 'center', border: '2px solid #000', width: '36px', minWidth: '36px', position: 'sticky', left: '130px', zIndex: 16, top: '40px'}} title="Documentos adjuntos">
                  <Paperclip size={14} style={{ color: 'white' }} />
                </th>
                {filteredEtapas.map(etapa =>
                  etapa.columnas.map(col => (
                    <th
                      key={col.key}
                      style={{
                        minWidth: col.width,
                        backgroundColor: etapa.id === 'etapa1' ? '#E0FFF4' :
                                       etapa.id === 'etapa2' ? '#FFE4E1' :
                                       etapa.id === 'etapa3' ? '#FFEFD5' :
                                       etapa.id === 'etapa4' ? '#F0E6FF' : '#f9fafb',
                        color: '#000',
                        fontWeight: '600',
                        border: '1px solid #000',
                        padding: '10px 8px',
                        fontSize: '12px',
                        position: 'sticky',
                        top: '40px',
                        zIndex: 15
                      }}
                    >
                      {col.label}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, rowIndex) => {
                const isSelected = selectedRowsSet.has(row.id);
                const isNewRow = row.id === newRowId;
                return (
                <tr
                  key={row.id}
                  className={`${isSelected ? 'selected' : ''} ${isNewRow ? 'new-row-highlight' : ''}`}
                  style={{
                    backgroundColor: isSelected ? '#fee2e2' : '',
                    position: 'relative',
                    zIndex: editingCell && editingCell.startsWith(row.id + '-') ? 13 : 1
                  }}
                >
                  {/* Avatar Column */}
                  <td
                    className="avatar-column"
                    style={{
                      backgroundColor: isSelected ? '#fca5a5' : '#f3f4f6',
                      textAlign: 'center',
                      border: '2px solid #000',
                      width: '60px',
                      minWidth: '60px',
                      padding: '8px 4px',
                      position: 'sticky',
                      left: '0',
                      zIndex: 12,
                      overflow: 'visible'
                    }}
                  >
                    {itemCollaborators[row.item] && itemCollaborators[row.item].length > 0 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        height: '32px'
                      }}>
                        {itemCollaborators[row.item].slice(0, 3).map((user, idx) => (
                          <div
                            key={user.email}
                            title={`${user.name} (${user.email})`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setBadgePopup({
                                itemNumber: row.item,
                                colKey: null,
                                user: user,
                                isEditor: !user.isImporter,
                                editorData: null,
                                position: {
                                  x: rect.left + rect.width / 2,
                                  y: rect.top + rect.height + 5
                                }
                              });
                            }}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'var(--theme-primary, #c4fffc)',
                              border: '2px solid var(--theme-secondary, #000)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '11px',
                              fontWeight: '700',
                              color: 'var(--theme-secondary, #000)',
                              cursor: 'pointer',
                              position: 'absolute',
                              left: `${idx * 14}px`,
                              zIndex: itemCollaborators[row.item].length - idx,
                              transition: 'all 0.2s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.15)';
                              e.currentTarget.style.zIndex = '100';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.zIndex = String(itemCollaborators[row.item].length - idx);
                            }}
                          >
                            {user.initials}
                          </div>
                        ))}
                        {itemCollaborators[row.item].length > 3 && (
                          <div
                            title={`+${itemCollaborators[row.item].length - 3} más`}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'var(--theme-secondary, #000)',
                              border: '2px solid var(--theme-secondary, #000)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: '700',
                              color: 'var(--theme-primary, #c4fffc)',
                              cursor: 'pointer',
                              position: 'absolute',
                              left: '42px',
                              zIndex: 0,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }}
                          >
                            +{itemCollaborators[row.item].length - 3}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td
                    className="checkbox-column"
                    style={{
                      backgroundColor: isSelected ? '#fca5a5' : '#f3f4f6',
                      textAlign: 'center',
                      border: '2px solid #000',
                      width: '32px',
                      minWidth: '32px',
                      padding: '8px',
                      position: 'sticky',
                      left: '60px',
                      zIndex: 12
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRowsSet.has(row.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newSelected = new Set(selectedRows);
                        if (e.target.checked) {
                          newSelected.add(row.id);
                        } else {
                          newSelected.delete(row.id);
                        }
                        setSelectedRows(Array.from(newSelected));
                      }}
                      style={{ cursor: 'pointer', background: 'white', border: '2px solid #000' }}
                    />
                  </td>
                  <td
                    className="number-column"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Click simple para abrir modal
                      setModalData({
                        ...row,
                        cliente: getClienteNombre(row.cliente),
                        proveedor: getProveedorNombre(row.proveedor)
                      });
                      setShowModal(true);
                      setActiveTab(filteredEtapas[0]?.id || 'etapa1');
                      loadModalItemDocs(row.item);
                    }}
                    style={{
                      backgroundColor: selectedRows.includes(row.id) ? '#fca5a5' : '#f3f4f6',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      border: '2px solid #000',
                      width: '38px',
                      minWidth: '38px',
                      fontSize: '14px',
                      color: '#1f2937',
                      cursor: 'pointer',
                      position: 'sticky',
                      left: '92px',
                      zIndex: 11
                    }}
                    title="Clic para editar en modal"
                  >
                    {rowIndex + 1}
                  </td>
                  {/* Clip / Documents Column */}
                  <td
                    className="clip-column"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (row.isNew) {
                        alert('Debes guardar la fila antes de adjuntar documentos. Guarda los cambios primero.');
                        return;
                      }
                      setDocPanelItem(row.item);
                    }}
                    style={{
                      backgroundColor: isSelected ? '#fca5a5' : '#f3f4f6',
                      textAlign: 'center',
                      border: '2px solid #000',
                      width: '36px',
                      minWidth: '36px',
                      padding: '4px',
                      position: 'sticky',
                      left: '130px',
                      zIndex: 11,
                      cursor: 'pointer'
                    }}
                    title={documentCounts[row.item] ? `${documentCounts[row.item]} documento(s) adjunto(s)` : 'Sin documentos - clic para agregar'}
                  >
                    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Paperclip size={15} style={{ color: documentCounts[row.item] ? 'var(--theme-secondary, #000)' : '#d1d5db' }} />
                      {documentCounts[row.item] > 0 && (
                        <span style={{
                          position: 'absolute', top: '-6px', right: '-8px',
                          background: 'var(--theme-secondary, #000)', color: 'var(--theme-primary, #c4fffc)',
                          borderRadius: '50%', width: '15px', height: '15px',
                          fontSize: '9px', fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          lineHeight: 1
                        }}>
                          {documentCounts[row.item]}
                        </span>
                      )}
                    </div>
                  </td>
                  {filteredEtapas.map(etapa =>
                    etapa.columnas.map(col => {
                      const cellId = `${row.id}-${col.key}`;
                      const isEditing = editingCell === cellId;

                      // Badge de celda: solo mostrar si alguien editó ESTA celda específica
                      // No mostrar en filas nuevas sin guardar ni en celdas sin ediciones reales
                      const lastEditor = row.isNew ? null : cellLastEditor[`${row.item}-${col.key}`];
                      const cellBadgeUser = lastEditor || null;

                      // Construir título
                      let cellTitle = '';
                      if (col.calculated) {
                        cellTitle = "Campo calculado automáticamente (no editable)";
                      } else if (lastEditor) {
                        const editDate = new Date(lastEditor.lastChange).toLocaleString('es-CL', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                        cellTitle = lastEditor.oldValue
                          ? `Último cambio: ${lastEditor.name}\n${editDate}\n${lastEditor.oldValue} → ${lastEditor.newValue}\n\nDoble clic para editar`
                          : `Creado por: ${lastEditor.name}\n${editDate}\n\nDoble clic para editar`;
                      } else {
                        cellTitle = "Doble clic para editar en modal";
                      }

                      // Identificador de celda para navegación
                      const cellNavigationId = `${row.item}-${col.key}`;
                      const isHighlighted = highlightedCell === cellNavigationId;

                      // Colores más saturados y visibles para cada etapa
                      const bgColor = etapa.id === 'etapa1' ? '#B8FFF9' :
                                     etapa.id === 'etapa2' ? '#FFB3BA' :
                                     etapa.id === 'etapa3' ? '#FFD4A3' :
                                     etapa.id === 'etapa4' ? '#DDB3FF' : '#ffffff';

                      return (
                        <td
                          key={col.key}
                          data-cell-id={cellNavigationId}
                          className={`data-cell ${col.calculated ? 'calculated' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                          style={{
                            backgroundColor: isHighlighted ? '#00fff5' : (col.calculated ? '#f0f0f0' : bgColor),
                            border: isHighlighted ? '4px solid #000' : '1px solid #000',
                            boxShadow: isHighlighted ? '0 0 20px rgba(0, 255, 245, 0.8), inset 0 0 30px rgba(0, 255, 245, 0.4), 0 0 0 4px rgba(0, 0, 0, 0.2)' : 'none',
                            padding: '10px 16px',
                            fontSize: '13px',
                            color: col.calculated ? '#059669' : '#374151',
                            fontWeight: col.calculated ? '700' : (isHighlighted ? '700' : 'normal'),
                            cursor: col.calculated || isHistoryMode ? 'not-allowed' : 'pointer',
                            userSelect: 'none',
                            position: 'relative',
                            overflow: 'visible',
                            zIndex: isEditing ? 14 : (isHighlighted ? 13 : 1),
                            opacity: col.calculated ? 0.85 : 1,
                            transition: 'all 0.3s ease',
                            transform: isHighlighted ? 'scale(1.08)' : 'scale(1)'
                          }}
                          title={cellTitle}
                          onClick={(e) => {
                            e.stopPropagation();
                            // No permitir edición de campos calculados
                            if (col.calculated) {
                              e.preventDefault();
                              return;
                            }
                            // No permitir edición en modo historial
                            if (isHistoryMode) {
                              e.preventDefault();
                              return;
                            }
                            // Observaciones: abrir panel de comentarios en lugar de editar inline
                            if (col.key === 'observaciones') {
                              setObsPanel({
                                itemNumber: row.item,
                                itemData: {
                                  cliente: getClienteNombre(row.cliente),
                                  descripcion: row.descripcion
                                }
                              });
                              return;
                            }
                            // Un solo clic para edición inline solo si no es calculado
                            if (!e.detail || e.detail === 1) {
                              setEditingCell(cellId);
                            }
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            // No permitir abrir modal en modo historial
                            if (isHistoryMode) {
                              return;
                            }
                            // Abrir modal en la etapa correspondiente
                            console.log('Abriendo modal en etapa:', etapa.id);
                            setModalData({
                              ...row,
                              cliente: getClienteNombre(row.cliente),
                              proveedor: getProveedorNombre(row.proveedor)
                            });
                            setActiveTab(etapa.id);
                            setShowModal(true);
                            setEditingCell(null); // Cerrar edición inline si está abierta
                            loadModalItemDocs(row.item);
                          }}
                        >
                          {/* Badge del último editor o importador en esquina superior derecha */}
                          {cellBadgeUser && !isEditing && !col.calculated &&
                            (lastEditor && showEditorBadges) && (
                            <div
                              title={`Editado por ${lastEditor.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                setBadgePopup({
                                  itemNumber: row.item,
                                  colKey: col.key,
                                  user: cellBadgeUser,
                                  isEditor: !!lastEditor,
                                  editorData: lastEditor,
                                  position: {
                                    x: rect.left + rect.width / 2,
                                    y: rect.top + rect.height + 5
                                  }
                                });
                              }}
                              style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                background: 'var(--theme-secondary, #000)',
                                border: '1px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '8px',
                                fontWeight: '700',
                                color: 'var(--theme-primary, #c4fffc)',
                                zIndex: 2,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                cursor: 'pointer'
                              }}
                            >
                              {cellBadgeUser.initials}
                            </div>
                          )}
                          {isEditing && !col.calculated ? (
                            // Renderizar campo especial según el tipo (solo si no es calculado)
                            col.key === 'cliente' ? (
                              <div style={{ position: 'relative' }}>
                                <input
                                  type="text"
                                  className="cell-input"
                                  id={`input-${cellId}`}
                                  value={getClienteNombre(row[col.key]) || 'Seleccionar cliente...'}
                                  readOnly
                                  autoFocus
                                  onClick={() => {
                                    setInlineClientDropdown(cellId);
                                  }}
                                  onFocus={() => {
                                    setInlineClientDropdown(cellId);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      setEditingCell(null);
                                      setInlineClientDropdown(null);
                                      setInlineSearchClient('');
                                    }
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    backgroundColor: '#f9fafb'
                                  }}
                                />
                                {inlineClientDropdown === cellId && (() => {
                                  const input = document.getElementById(`input-${cellId}`);
                                  const rect = input?.getBoundingClientRect();
                                  return (
                                    <div className="select-dropdown" style={{
                                      position: 'fixed',
                                      top: rect ? `${rect.bottom + 2}px` : 'auto',
                                      left: rect ? `${rect.left}px` : 'auto',
                                      width: rect ? `${Math.max(rect.width, 250)}px` : '250px',
                                      zIndex: 2147483647,
                                      backgroundColor: 'white',
                                      border: '2px solid var(--theme-secondary, #000)',
                                      borderRadius: '4px',
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}>
                                    {/* Campo de búsqueda dentro del dropdown */}
                                    <div style={{ padding: '8px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: 'white' }}>
                                      <input
                                        type="text"
                                        value={inlineSearchClient || ''}
                                        onChange={(e) => setInlineSearchClient(e.target.value)}
                                        placeholder="Buscar cliente..."
                                        autoFocus
                                        style={{
                                          width: '100%',
                                          padding: '6px 8px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '4px',
                                          fontSize: '13px',
                                          outline: 'none',
                                          textAlign: 'center'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                      <div
                                        className="select-option"
                                        onClick={() => window.open('/clientes?openModal=true', '_blank')}
                                        style={{
                                          background: 'var(--theme-primary, #c4fffc)',
                                          borderBottom: '2px solid var(--theme-secondary, #000)',
                                          padding: '10px 12px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}
                                      >
                                        <Plus size={16} style={{ flexShrink: 0 }} />
                                        <strong style={{ fontSize: '13px' }}>+ Nuevo Cliente</strong>
                                      </div>
                                      {clients
                                        .filter(client => {
                                          const search = (inlineSearchClient || '').toLowerCase();
                                          return !search ||
                                            (client.name || '').toLowerCase().includes(search) ||
                                            (client.rut || '').toLowerCase().includes(search);
                                        })
                                        .slice(0, 50)
                                        .map(client => (
                                          <div
                                            key={client.id}
                                            className="select-option"
                                            onClick={() => {
                                              handleCellEdit(row.id, col.key, client.name);
                                              setInlineClientDropdown(null);
                                              setInlineSearchClient('');
                                            }}
                                            style={{ padding: '8px', cursor: 'pointer' }}
                                          >
                                            <Building2 size={14} style={{ display: 'inline', marginRight: '8px' }} />
                                            {client.name}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                  );
                                })()}
                              </div>
                            ) : col.key === 'proveedor' ? (
                              <div style={{ position: 'relative' }}>
                                <input
                                  type="text"
                                  className="cell-input"
                                  id={`input-${cellId}`}
                                  value={getProveedorNombre(row[col.key]) || 'Seleccionar proveedor...'}
                                  readOnly
                                  autoFocus
                                  onClick={() => {
                                    setInlineSupplierDropdown(cellId);
                                  }}
                                  onFocus={() => {
                                    setInlineSupplierDropdown(cellId);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      setEditingCell(null);
                                      setInlineSupplierDropdown(null);
                                      setInlineSearchSupplier('');
                                    }
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    backgroundColor: '#f9fafb'
                                  }}
                                />
                                {inlineSupplierDropdown === cellId && (() => {
                                  const input = document.getElementById(`input-${cellId}`);
                                  const rect = input?.getBoundingClientRect();
                                  return (
                                    <div className="select-dropdown" style={{
                                      position: 'fixed',
                                      top: rect ? `${rect.bottom + 2}px` : 'auto',
                                      left: rect ? `${rect.left}px` : 'auto',
                                      width: rect ? `${Math.max(rect.width, 250)}px` : '250px',
                                      zIndex: 2147483647,
                                      backgroundColor: 'white',
                                      border: '2px solid var(--theme-secondary, #000)',
                                      borderRadius: '4px',
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}>
                                    {/* Campo de búsqueda dentro del dropdown */}
                                    <div style={{ padding: '8px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: 'white' }}>
                                      <input
                                        type="text"
                                        value={inlineSearchSupplier || ''}
                                        onChange={(e) => setInlineSearchSupplier(e.target.value)}
                                        placeholder="Buscar proveedor..."
                                        autoFocus
                                        style={{
                                          width: '100%',
                                          padding: '6px 8px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '4px',
                                          fontSize: '13px',
                                          outline: 'none',
                                          textAlign: 'center'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                      <div
                                        className="select-option"
                                        onClick={() => window.open('/proveedores?openModal=true', '_blank')}
                                        style={{
                                          background: 'var(--theme-primary, #c4fffc)',
                                          borderBottom: '2px solid var(--theme-secondary, #000)',
                                          padding: '10px 12px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}
                                      >
                                        <Plus size={16} style={{ flexShrink: 0 }} />
                                        <strong style={{ fontSize: '13px' }}>+ Nuevo Proveedor</strong>
                                      </div>
                                      {suppliers
                                        .filter(supplier => {
                                          const search = (inlineSearchSupplier || '').toLowerCase();
                                          return !search ||
                                            (supplier.name || '').toLowerCase().includes(search) ||
                                            (supplier.rut || '').toLowerCase().includes(search);
                                        })
                                        .slice(0, 50)
                                        .map(supplier => (
                                          <div
                                            key={supplier.id}
                                            className="select-option"
                                            onClick={() => {
                                              handleCellEdit(row.id, col.key, supplier.name);
                                              setInlineSupplierDropdown(null);
                                              setInlineSearchSupplier('');
                                            }}
                                            style={{ padding: '8px', cursor: 'pointer' }}
                                          >
                                            <Truck size={14} style={{ display: 'inline', marginRight: '8px' }} />
                                            {supplier.name}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                  );
                                })()}
                              </div>
                            ) : col.key === 'unidad' ? (
                              <div style={{ position: 'relative' }}>
                                <input
                                  type="text"
                                  className="cell-input"
                                  id={`input-${cellId}`}
                                  value={inlineSearchUnit || row[col.key] || ''}
                                  placeholder="Seleccionar unidad..."
                                  autoFocus
                                  onChange={(e) => {
                                    setInlineSearchUnit(e.target.value.toUpperCase());
                                    setInlineUnitDropdown(cellId);
                                  }}
                                  onFocus={() => {
                                    setInlineUnitDropdown(cellId);
                                    setInlineSearchUnit('');
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !inlineUnitDropdown) {
                                      handleCellEdit(row.id, col.key, e.target.value);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingCell(null);
                                      setInlineUnitDropdown(null);
                                    }
                                  }}
                                />
                                {inlineUnitDropdown === cellId && (() => {
                                  const input = document.getElementById(`input-${cellId}`);
                                  const rect = input?.getBoundingClientRect();
                                  return (
                                    <div className="select-dropdown" style={{
                                      position: 'fixed',
                                      top: rect ? `${rect.bottom + 2}px` : 'auto',
                                      left: rect ? `${rect.left}px` : 'auto',
                                      width: rect ? `${Math.max(rect.width, 150)}px` : '150px',
                                      zIndex: 2147483647, // Maximum z-index value
                                      maxHeight: '250px',
                                      overflowY: 'auto',
                                      backgroundColor: 'white',
                                      border: '2px solid var(--theme-secondary, #000)',
                                      borderRadius: '4px',
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}>
                                    {availableUnits
                                      .filter(unit => unit.toLowerCase().includes((inlineSearchUnit || '').toLowerCase()))
                                      .map(unit => (
                                        <div
                                          key={unit}
                                          className="select-option"
                                          onClick={() => {
                                            handleCellEdit(row.id, col.key, unit);
                                            setInlineUnitDropdown(null);
                                            setInlineSearchUnit('');
                                          }}
                                          style={{ padding: '8px', cursor: 'pointer' }}
                                        >
                                          {unit}
                                        </div>
                                      ))}
                                  </div>
                                  );
                                })()}
                              </div>
                            ) : col.key === 'item2' ? (
                              // Dropdown para ITEM2 con entrada manual
                              <div style={{ position: 'relative' }}>
                                <input
                                  type="text"
                                  className="cell-input"
                                  id={`input-${cellId}`}
                                  value={row[col.key] || ''}
                                  placeholder="Escribe o selecciona..."
                                  autoFocus
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^\d]/g, '');
                                    const updatedRow = { ...row, [col.key]: value };
                                    const index = filteredData.findIndex(r => r.id === row.id);
                                    const newData = [...filteredData];
                                    newData[index] = updatedRow;
                                    setData(data.map(r => r.id === row.id ? updatedRow : r));
                                  }}
                                  onFocus={() => setInlineItem2Dropdown(cellId)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleCellEdit(row.id, col.key, e.target.value);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingCell(null);
                                      setInlineItem2Dropdown(null);
                                    }
                                  }}
                                />
                                {inlineItem2Dropdown === cellId && (() => {
                                  const input = document.getElementById(`input-${cellId}`);
                                  const rect = input?.getBoundingClientRect();
                                  return (
                                    <div className="select-dropdown" style={{
                                      position: 'fixed',
                                      top: rect ? `${rect.bottom + 2}px` : 'auto',
                                      left: rect ? `${rect.left}px` : 'auto',
                                      width: rect ? `${Math.max(rect.width, 200)}px` : '200px',
                                      zIndex: 2147483647,
                                      maxHeight: '250px',
                                      overflowY: 'auto',
                                      backgroundColor: 'white',
                                      border: '2px solid var(--theme-secondary, #000)',
                                      borderRadius: '4px',
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}>
                                      {/* Mensaje principal */}
                                      <div style={{
                                        padding: '12px 16px',
                                        background: 'var(--theme-secondary, #000000)',
                                        color: '#ffffff',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        textAlign: 'center',
                                        borderBottom: '2px solid var(--theme-primary, #c4fffc)',
                                        fontFamily: "'Space Grotesk', sans-serif",
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10
                                      }}>
                                        💡 Puedes escribir manualmente o seleccionar
                                      </div>
                                      {/* Lista de valores */}
                                      {item2Options.map(value => (
                                        <div
                                          key={value}
                                          className="select-option"
                                          onClick={() => {
                                            handleCellEdit(row.id, col.key, value.toString());
                                            setInlineItem2Dropdown(null);
                                          }}
                                          style={{
                                            padding: '10px 16px',
                                            borderBottom: '1px solid #eee',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            fontSize: '15px',
                                            fontWeight: '500',
                                            transition: 'all 0.2s ease'
                                          }}
                                        >
                                          {value}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : ['part_number', 'cin'].includes(col.key) ? (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <input
                                  type="text"
                                  className="cell-input"
                                  defaultValue={row[col.key]}
                                  disabled={row[col.key] === 'No Aplica'}
                                  autoFocus
                                  style={{
                                    flex: 1,
                                    backgroundColor: row[col.key] === 'No Aplica' ? '#f5f5f5' : 'white',
                                    opacity: row[col.key] === 'No Aplica' ? 0.7 : 1
                                  }}
                                  onBlur={(e) => handleCellEdit(row.id, col.key, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleCellEdit(row.id, col.key, e.target.value);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingCell(null);
                                    }
                                  }}
                                />
                                <button
                                  onClick={() => {
                                    const newValue = row[col.key] === 'No Aplica' ? '' : 'No Aplica';
                                    handleCellEdit(row.id, col.key, newValue);
                                  }}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '11px',
                                    background: row[col.key] === 'No Aplica'
                                      ? 'var(--theme-primary, #c4fffc)'
                                      : 'var(--theme-secondary, #000)',
                                    color: row[col.key] === 'No Aplica'
                                      ? 'var(--theme-secondary, #000)'
                                      : 'var(--theme-primary, #c4fffc)',
                                    border: '1px solid var(--theme-secondary, #000)',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {row[col.key] === 'No Aplica' ? '✓ NA' : 'NA'}
                                </button>
                              </div>
                            ) : ['delivery_date', 'real_delivery'].includes(col.key) ? (
                              // Selector de fecha simple
                              <input
                                type="date"
                                className="cell-input"
                                id={`input-${cellId}`}
                                defaultValue={(() => {
                                  const val = row[col.key];
                                  if (!val) return '';
                                  // Si ya está en formato yyyy-mm-dd, retornar tal cual
                                  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
                                    return val;
                                  }
                                  // Si está en formato dd-mm-yyyy, convertir a yyyy-mm-dd
                                  if (/^\d{2}-\d{2}-\d{4}$/.test(val)) {
                                    const parts = val.split('-');
                                    return `${parts[2]}-${parts[1]}-${parts[0]}`;
                                  }
                                  return val;
                                })()}
                                ref={(input) => {
                                  // Auto-abrir el selector de fecha al entrar en modo edición
                                  if (input && input.showPicker) {
                                    setTimeout(() => {
                                      try {
                                        input.showPicker();
                                      } catch (e) {
                                        // Algunos navegadores no soportan showPicker
                                        input.click();
                                      }
                                    }, 50);
                                  }
                                }}
                                autoFocus
                                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                onBlur={(e) => {
                                  const newVal = e.target.value || '';
                                  handleCellEdit(row.id, col.key, newVal);
                                  // Si es delivery_date, ajustar filtro de año para no perder la fila
                                  if (col.key === 'delivery_date' && newVal && statsYear) {
                                    const newYear = new Date(newVal).getFullYear();
                                    if (!isNaN(newYear) && String(newYear) !== statsYear) {
                                      setStatsYear(String(newYear));
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const val = e.target.value || '';
                                    handleCellEdit(row.id, col.key, val);
                                    if (col.key === 'delivery_date' && val && statsYear) {
                                      const newYear = new Date(val).getFullYear();
                                      if (!isNaN(newYear) && String(newYear) !== statsYear) {
                                        setStatsYear(String(newYear));
                                      }
                                    }
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                style={{
                                  backgroundColor: '#ffffff',
                                  cursor: 'pointer'
                                }}
                              />
                            ) : ['pu_clp', 'pu_proveedor'].includes(col.key) ? (
                              // Campo de precio con formato de moneda
                              <input
                                type="text"
                                className="cell-input"
                                defaultValue={row[col.key] ? Number(row[col.key]).toLocaleString('es-CL') : ''}
                                placeholder="0"
                                autoFocus
                                onBlur={(e) => {
                                  const value = e.target.value.replace(/[^\d]/g, '');
                                  const numValue = parseInt(value) || 0;
                                  handleCellEdit(row.id, col.key, numValue);

                                  // Actualizar cálculos
                                  const updatedRow = { ...row, [col.key]: numValue };
                                  if (col.key === 'pu_clp') {
                                    updatedRow.total_neto = (parseFloat(updatedRow.cantidad) || 0) * numValue;
                                  }
                                  if (col.key === 'pu_proveedor') {
                                    updatedRow.total_proveedor = (parseFloat(updatedRow.cantidad) || 0) * numValue;
                                  }
                                  updatedRow.u_del_negocio = calculateUtilidad(updatedRow.total_neto, updatedRow.total_proveedor);
                                  setData(data.map(r => r.id === row.id ? updatedRow : r));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const value = e.target.value.replace(/[^\d]/g, '');
                                    const numValue = parseInt(value) || 0;
                                    handleCellEdit(row.id, col.key, numValue);
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                              />
                            ) : ['emision_fact', 'vencimiento', 'fecha_pago', 'emision_prov', 'vencimiento_prov', 'fecha_pago_prov'].includes(col.key) ? (
                              // Campos de fecha con selector de rango
                              <div className="date-range-container" style={{ position: 'relative', width: '100%', zIndex: 999999 }}>
                                <input
                                  type="text"
                                  className="cell-input"
                                  id={`input-${cellId}`}
                                  value={row[col.key] || ''}
                                  placeholder="Clic para seleccionar rango..."
                                  ref={(input) => {
                                    // Auto-abrir el selector de rango al entrar en modo edición
                                    if (input && !inlineDateRange) {
                                      input.focus();
                                      setTimeout(() => {
                                        setInlineDateRange(`${row.id}-${col.key}`);
                                        const val = row[col.key] || '';
                                        if (val.includes(' - ')) {
                                          const [start, end] = val.split(' - ');
                                          setInlineTempStartDate(toInputDate(start.trim()));
                                          setInlineTempEndDate(toInputDate(end.trim()));
                                        } else if (val) {
                                          setInlineTempStartDate(toInputDate(val));
                                          setInlineTempEndDate('');
                                        } else {
                                          setInlineTempStartDate('');
                                          setInlineTempEndDate('');
                                        }
                                      }, 50);
                                    }
                                  }}
                                  onClick={() => {
                                    setInlineDateRange(`${row.id}-${col.key}`);
                                    const val = row[col.key] || '';
                                    if (val.includes(' - ')) {
                                      const [start, end] = val.split(' - ');
                                      setInlineTempStartDate(toInputDate(start.trim()));
                                      setInlineTempEndDate(toInputDate(end.trim()));
                                    } else if (val) {
                                      setInlineTempStartDate(toInputDate(val));
                                      setInlineTempEndDate('');
                                    } else {
                                      setInlineTempStartDate('');
                                      setInlineTempEndDate('');
                                    }
                                  }}
                                  readOnly
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      setInlineDateRange(null);
                                      setEditingCell(null);
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    cursor: 'pointer',
                                    backgroundColor: '#f9f9f9',
                                    padding: '4px 8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                  }}
                                  autoFocus
                                />

                                {inlineDateRange === `${row.id}-${col.key}` && (
                                    <div
                                      className="date-range-selector"
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      style={{
                                        position: 'absolute',
                                        backgroundColor: 'white',
                                        border: '2px solid var(--theme-secondary, #000000)',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                                        zIndex: 2147483647,
                                        minWidth: '320px',
                                        left: '0',
                                        top: '100%',
                                        marginTop: '2px'
                                      }}
                                  >
                                    <div style={{ marginBottom: '12px' }}>
                                      <label style={{
                                        display: 'block',
                                        marginBottom: '4px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: 'var(--theme-secondary, #000000)'
                                      }}>
                                        Fecha de Inicio / Start Date:
                                      </label>
                                      <input
                                        type="date"
                                        value={inlineTempStartDate || ''}
                                        onChange={(e) => {
                                          console.log('Fecha inicio seleccionada:', e.target.value);
                                          setInlineTempStartDate(e.target.value);
                                        }}
                                        style={{
                                          width: '100%',
                                          padding: '8px',
                                          border: '1px solid #e0e0e0',
                                          borderRadius: '4px',
                                          fontSize: '14px'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>

                                    <div style={{ marginBottom: '16px' }}>
                                      <label style={{
                                        display: 'block',
                                        marginBottom: '4px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: 'var(--theme-secondary, #000000)'
                                      }}>
                                        Fecha de Fin / End Date:
                                      </label>
                                      <input
                                        type="date"
                                        value={inlineTempEndDate || ''}
                                        onChange={(e) => {
                                          console.log('Fecha fin seleccionada:', e.target.value);
                                          setInlineTempEndDate(e.target.value);
                                        }}
                                        min={inlineTempStartDate}
                                        style={{
                                          width: '100%',
                                          padding: '8px',
                                          border: '1px solid #e0e0e0',
                                          borderRadius: '4px',
                                          fontSize: '14px'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (inlineTempStartDate && inlineTempEndDate) {
                                            const formattedStart = inlineTempStartDate.split('-').reverse().join('-');
                                            const formattedEnd = inlineTempEndDate.split('-').reverse().join('-');
                                            const rangeValue = `${formattedStart} - ${formattedEnd}`;
                                            handleCellEdit(row.id, col.key, rangeValue);
                                            setInlineDateRange(null);
                                            setEditingCell(null);
                                            setInlineTempStartDate('');
                                            setInlineTempEndDate('');
                                          } else if (inlineTempStartDate && !inlineTempEndDate) {
                                            const formattedStart = inlineTempStartDate.split('-').reverse().join('-');
                                            handleCellEdit(row.id, col.key, formattedStart);
                                            setInlineDateRange(null);
                                            setEditingCell(null);
                                            setInlineTempStartDate('');
                                            setInlineTempEndDate('');
                                          }
                                        }}
                                        disabled={!inlineTempStartDate}
                                        style={{
                                          flex: 1,
                                          padding: '8px',
                                          backgroundColor: inlineTempStartDate ? 'var(--theme-primary, #c4fffc)' : '#f0f0f0',
                                          border: '2px solid var(--theme-secondary, #000000)',
                                          borderRadius: '6px',
                                          fontWeight: '600',
                                          cursor: inlineTempStartDate ? 'pointer' : 'not-allowed',
                                          opacity: inlineTempStartDate ? 1 : 0.5
                                        }}
                                      >
                                        {inlineTempStartDate && !inlineTempEndDate ? 'Aplicar Fecha' : 'Aplicar Rango'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          // Usar solo fecha inicio como fecha única
                                          if (inlineTempStartDate) {
                                            const formattedStart = inlineTempStartDate.split('-').reverse().join('-');
                                            handleCellEdit(row.id, col.key, formattedStart);
                                            setInlineDateRange(null);
                                            setEditingCell(null);
                                            setInlineTempStartDate('');
                                            setInlineTempEndDate('');
                                          }
                                        }}
                                        disabled={!inlineTempStartDate}
                                        style={{
                                          flex: 1,
                                          padding: '8px',
                                          backgroundColor: inlineTempStartDate ? '#f0fdf4' : '#f0f0f0',
                                          border: `2px solid ${inlineTempStartDate ? '#16a34a' : '#e0e0e0'}`,
                                          borderRadius: '6px',
                                          fontWeight: '600',
                                          cursor: inlineTempStartDate ? 'pointer' : 'not-allowed',
                                          color: inlineTempStartDate ? '#16a34a' : '#999',
                                          fontSize: '12px'
                                        }}
                                      >
                                        Solo Fecha Inicio
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setInlineDateRange(null);
                                          setEditingCell(null);
                                          setInlineTempStartDate('');
                                          setInlineTempEndDate('');
                                        }}
                                        style={{
                                          flex: 1,
                                          padding: '8px',
                                          backgroundColor: 'white',
                                          border: '2px solid #e0e0e0',
                                          borderRadius: '6px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : col.key === 'cantidad' ? (
                              // Campo de cantidad con actualización de cálculos
                              <input
                                type="number"
                                className="cell-input"
                                defaultValue={(() => {
                                  const val = parseFloat(row[col.key]);
                                  return isNaN(val) ? '' : (val % 1 === 0 ? parseInt(val) : val);
                                })()}
                                placeholder="0"
                                autoFocus
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  handleCellEdit(row.id, col.key, value);

                                  // Actualizar cálculos automáticamente
                                  const updatedRow = { ...row, cantidad: value };
                                  updatedRow.total_neto = value * (parseFloat(updatedRow.pu_clp) || 0);
                                  updatedRow.total_proveedor = value * (parseFloat(updatedRow.pu_proveedor) || 0);
                                  updatedRow.u_del_negocio = calculateUtilidad(updatedRow.total_neto, updatedRow.total_proveedor);
                                  setData(data.map(r => r.id === row.id ? updatedRow : r));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const value = parseFloat(e.target.value) || 0;
                                    handleCellEdit(row.id, col.key, value);

                                    // Actualizar cálculos automáticamente
                                    const updatedRow = { ...row, cantidad: value };
                                    updatedRow.total_neto = value * (parseFloat(updatedRow.pu_clp) || 0);
                                    updatedRow.total_proveedor = value * (parseFloat(updatedRow.pu_proveedor) || 0);
                                    updatedRow.u_del_negocio = calculateUtilidad(updatedRow.total_neto, updatedRow.total_proveedor);
                                    setData(data.map(r => r.id === row.id ? updatedRow : r));
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                              />
                            ) : (FIELD_DOC_TYPE_MAP[col.key] && MULTI_DOC_TYPES.has(FIELD_DOC_TYPE_MAP[col.key])) ? (
                              // Multi-doc field: mostrar selector de documentos directo (sin input)
                              (() => {
                                // Disparar fetch al entrar en modo edición (siempre fresco)
                                if (!inlineMultiDocEdit || inlineMultiDocEdit.cellId !== cellId) {
                                  setTimeout(() => {
                                    setInlineMultiDocLoading(true);
                                    api.get(`/documents/item/${row.item}`).then(res => {
                                      const docs = (res.data || []).filter(d => d.doc_type === FIELD_DOC_TYPE_MAP[col.key]);
                                      setInlineMultiDocEdit({ cellId, docs, editingDocId: null });
                                    }).catch(() => {
                                      setInlineMultiDocEdit({ cellId, docs: [], editingDocId: null });
                                    }).finally(() => setInlineMultiDocLoading(false));
                                  }, 0);
                                }
                                const td = document.querySelector(`[data-cell-id="${row.item}-${col.key}"]`);
                                const rect = td?.getBoundingClientRect();
                                return (
                                  <div style={{ position: 'relative' }}>
                                    {/* Mostrar el valor actual de la celda normalmente */}
                                    <span>{formatValue(row[col.key], col.key, row)}</span>
                                    {/* Dropdown posicionado debajo de la celda */}
                                    {inlineMultiDocEdit && inlineMultiDocEdit.cellId === cellId && ReactDOM.createPortal(
                                      <div
                                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}
                                        onClick={() => { setEditingCell(null); setInlineMultiDocEdit(null); }}
                                      >
                                        <div
                                          onClick={(e) => e.stopPropagation()}
                                          style={{
                                            position: 'fixed',
                                            top: rect ? `${Math.min(rect.bottom + 2, window.innerHeight - 320)}px` : '100px',
                                            left: rect ? `${Math.min(rect.left, window.innerWidth - 300)}px` : '100px',
                                            width: '280px',
                                            maxHeight: '300px',
                                            backgroundColor: 'white',
                                            border: '2px solid var(--theme-secondary, #000)',
                                            borderRadius: '10px',
                                            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
                                            fontFamily: "'Space Grotesk', sans-serif",
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column'
                                          }}
                                        >
                                          {/* Header */}
                                          <div style={{
                                            padding: '8px 12px',
                                            background: 'var(--theme-secondary, #000)',
                                            color: 'var(--theme-primary, #c4fffc)',
                                            fontSize: '11px', fontWeight: 700,
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                          }}>
                                            <span>{DOC_TYPE_LABELS_SHORT[FIELD_DOC_TYPE_MAP[col.key]]} - Item #{row.item}</span>
                                            <button
                                              onClick={() => { setEditingCell(null); setInlineMultiDocEdit(null); }}
                                              style={{
                                                background: 'none', border: 'none', color: 'var(--theme-primary, #c4fffc)',
                                                cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 2px'
                                              }}
                                            >×</button>
                                          </div>
                                          {/* Content */}
                                          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
                                            {inlineMultiDocLoading ? (
                                              <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
                                                Cargando...
                                              </div>
                                            ) : inlineMultiDocEdit.docs.length === 0 ? (
                                              <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
                                                No hay documentos. Usa el clip para subir.
                                              </div>
                                            ) : (
                                              inlineMultiDocEdit.docs.map(doc => (
                                                <div key={doc.id} style={{
                                                  padding: '8px 12px',
                                                  borderBottom: '1px solid #f1f5f9'
                                                }}>
                                                  {inlineMultiDocEdit.editingDocId === doc.id ? (
                                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                      <input
                                                        type="text"
                                                        defaultValue={doc.doc_reference || ''}
                                                        autoFocus
                                                        placeholder="Referencia..."
                                                        style={{
                                                          flex: 1, padding: '5px 8px',
                                                          border: '2px solid var(--theme-secondary, #000)',
                                                          borderRadius: '5px', fontSize: '12px',
                                                          fontFamily: "'Space Grotesk', sans-serif",
                                                          outline: 'none'
                                                        }}
                                                        onKeyDown={(e) => {
                                                          e.stopPropagation();
                                                          if (e.key === 'Enter') {
                                                            const newRef = e.target.value;
                                                            api.patch(`/documents/${doc.id}`, { doc_reference: newRef }).then(() => {
                                                              const updatedDocs = inlineMultiDocEdit.docs.map(d => d.id === doc.id ? { ...d, doc_reference: newRef } : d);
                                                              setInlineMultiDocEdit(prev => ({
                                                                ...prev,
                                                                editingDocId: null,
                                                                docs: updatedDocs
                                                              }));
                                                              let displayValue = updatedDocs.length === 1
                                                                ? (updatedDocs[0].doc_reference || updatedDocs[0].original_name || '1 doc')
                                                                : `${updatedDocs.length} docs`;
                                                              setData(prev => prev.map(r => r.item === row.item ? { ...r, [col.key]: displayValue } : r));
                                                              api.patch('/documents/sync-maestro-field', {
                                                                item_number: row.item, field_key: col.key, value: displayValue
                                                              }).catch(() => {});
                                                            }).catch(() => alert('Error al guardar referencia'));
                                                          }
                                                          if (e.key === 'Escape') {
                                                            setInlineMultiDocEdit(prev => ({ ...prev, editingDocId: null }));
                                                          }
                                                        }}
                                                        onBlur={(e) => {
                                                          const newRef = e.target.value;
                                                          api.patch(`/documents/${doc.id}`, { doc_reference: newRef }).then(() => {
                                                            const updatedDocs = inlineMultiDocEdit.docs.map(d => d.id === doc.id ? { ...d, doc_reference: newRef } : d);
                                                            setInlineMultiDocEdit(prev => prev ? ({
                                                              ...prev, editingDocId: null, docs: updatedDocs
                                                            }) : null);
                                                            let displayValue = updatedDocs.length === 1
                                                              ? (updatedDocs[0].doc_reference || updatedDocs[0].original_name || '1 doc')
                                                              : `${updatedDocs.length} docs`;
                                                            setData(prev => prev.map(r => r.item === row.item ? { ...r, [col.key]: displayValue } : r));
                                                            api.patch('/documents/sync-maestro-field', {
                                                              item_number: row.item, field_key: col.key, value: displayValue
                                                            }).catch(() => {});
                                                          }).catch(() => {});
                                                        }}
                                                      />
                                                      <button
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => setInlineMultiDocEdit(prev => ({ ...prev, editingDocId: null }))}
                                                        style={{
                                                          padding: '4px 6px', borderRadius: '4px',
                                                          background: '#f3f4f6', border: '1px solid #d1d5db',
                                                          cursor: 'pointer', fontSize: '10px'
                                                        }}
                                                      >✕</button>
                                                    </div>
                                                  ) : (
                                                    <div
                                                      style={{
                                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                                                        padding: '4px 6px', borderRadius: '6px', transition: 'background 0.15s'
                                                      }}
                                                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                                                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                      onClick={() => setInlineMultiDocEdit(prev => ({ ...prev, editingDocId: doc.id }))}
                                                    >
                                                      <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{
                                                          fontSize: '12px', fontWeight: 600, color: '#1f2937',
                                                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                        }}>
                                                          {doc.doc_reference || '(sin referencia)'}
                                                        </div>
                                                        <div style={{ fontSize: '10px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                          {doc.original_name}
                                                        </div>
                                                      </div>
                                                      <Edit3 size={12} style={{ color: '#9ca3af', flexShrink: 0 }} />
                                                    </div>
                                                  )}
                                                </div>
                                              ))
                                            )}
                                          </div>
                                        </div>
                                      </div>,
                                      document.body
                                    )}
                                  </div>
                                );
                              })()
                            ) : (
                              <input
                                type="text"
                                className="cell-input"
                                defaultValue={row[col.key]}
                                autoFocus
                                onBlur={(e) => handleCellEdit(row.id, col.key, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellEdit(row.id, col.key, e.target.value);
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                              />
                            )
                          ) : (
                            // Mostrar valor normal o en modo historial
                            (() => {
                              // Verificar si estamos en modo historial y esta celda es la del cambio actual
                              if (isHistoryMode && currentHistoryChange &&
                                  row.item === currentHistoryChange.itemNumber &&
                                  col.key === currentHistoryChange.fieldKey) {
                                // Mostrar valor viejo tachado → nuevo
                                return (
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                    padding: '4px'
                                  }}>
                                    <div style={{
                                      textDecoration: 'line-through',
                                      color: '#666666',
                                      fontSize: '11px',
                                      fontWeight: '600'
                                    }}>
                                      {currentHistoryChange.oldValue ? formatValue(currentHistoryChange.oldValue, currentHistoryChange.fieldKey) : '(vacío)'}
                                    </div>
                                    <div style={{
                                      color: 'var(--theme-secondary, #000)',
                                      fontSize: '13px',
                                      fontWeight: '700'
                                    }}>
                                      ↓ {currentHistoryChange.newValue ? formatValue(currentHistoryChange.newValue, currentHistoryChange.fieldKey) : '(vacío)'}
                                    </div>
                                  </div>
                                );
                              }
                              // Valor normal - con botón de clip en esquina inferior derecha para campos de documento
                              if (FIELD_DOC_TYPE_MAP[col.key]) {
                                const docType = FIELD_DOC_TYPE_MAP[col.key];
                                const cellDocCount = docCountsByType[`${row.item}_${docType}`] || 0;
                                return (
                                  <>
                                    {formatValue(row[col.key], col.key, row)}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        openCellDocPopover(row.item, docType, {
                                          x: rect.right + 4,
                                          y: rect.top - 10
                                        }, row);
                                      }}
                                      title={`${DOC_TYPE_LABELS_SHORT[docType]}: ${cellDocCount} doc(s)`}
                                      style={{
                                        position: 'absolute',
                                        bottom: '2px', right: '2px',
                                        width: '18px', height: '18px',
                                        borderRadius: '4px', cursor: 'pointer',
                                        border: cellDocCount > 0
                                          ? '1.5px solid var(--theme-secondary, #000)'
                                          : '1px solid #d1d5db',
                                        background: cellDocCount > 0
                                          ? 'var(--theme-secondary, #000)'
                                          : 'white',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        padding: 0,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        transition: 'all 0.15s',
                                        zIndex: 2
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!cellDocCount) {
                                          e.currentTarget.style.background = 'var(--theme-secondary, #000)';
                                          e.currentTarget.style.borderColor = 'var(--theme-secondary, #000)';
                                          e.currentTarget.querySelector('svg').style.color = 'var(--theme-primary, #c4fffc)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!cellDocCount) {
                                          e.currentTarget.style.background = 'white';
                                          e.currentTarget.style.borderColor = '#d1d5db';
                                          e.currentTarget.querySelector('svg').style.color = '#9ca3af';
                                        }
                                      }}
                                    >
                                      <Paperclip size={10} style={{
                                        color: cellDocCount > 0
                                          ? 'var(--theme-primary, #c4fffc)'
                                          : '#9ca3af',
                                        transition: 'color 0.15s'
                                      }} />
                                    </button>
                                    {cellDocCount > 0 && (
                                      <span style={{
                                        position: 'absolute',
                                        bottom: '12px', right: '-2px',
                                        background: 'var(--theme-secondary, #000)',
                                        color: 'var(--theme-primary, #c4fffc)',
                                        borderRadius: '50%', width: '14px', height: '14px',
                                        fontSize: '8px', fontWeight: 800,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        lineHeight: 1, zIndex: 3,
                                        border: '1.5px solid white'
                                      }}>
                                        {cellDocCount}
                                      </span>
                                    )}
                                  </>
                                );
                              }
                              // Observaciones: mostrar badge de conteo
                              if (col.key === 'observaciones') {
                                const obsCount = obsCounts[row.item] || 0;
                                return (
                                  <div style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    justifyContent: 'center', width: '100%'
                                  }}>
                                    <MessageSquare size={14} style={{ color: obsCount > 0 ? 'var(--theme-secondary, #000)' : '#9ca3af', flexShrink: 0 }} />
                                    {obsCount > 0 ? (
                                      <span style={{
                                        background: 'var(--theme-secondary, #000)',
                                        color: 'var(--theme-primary, #c4fffc)',
                                        borderRadius: '10px', padding: '1px 7px',
                                        fontSize: '11px', fontWeight: 700
                                      }}>
                                        {obsCount}
                                      </span>
                                    ) : (
                                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>Agregar</span>
                                    )}
                                  </div>
                                );
                              }
                              return formatValue(row[col.key], col.key, row);
                            })()
                          )}
                        </td>
                      );
                    })
                  )}
                </tr>
              );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      ) : (
        /* Cards Container - Usando componente memoizado */
        <div className="matriz-cards-container" ref={cardsContainerRef}>
          {filteredData.map((row) => {
            const clienteNombre = getClienteNombre(row.cliente);
            const proveedorNombre = getProveedorNombre(row.proveedor);
            const isSelected = selectedRowsSet.has(row.id);
            const isNewRow = row.id === newRowId;

            return (
              <MatrizCard
                key={row.id}
                row={row}
                clienteNombre={clienteNombre}
                proveedorNombre={proveedorNombre}
                isSelected={isSelected}
                isNewRow={isNewRow}
                collapsedSections={collapsedSections}
                toggleSection={toggleSection}
                onSelectionChange={handleCardSelection}
              />
            );
          })}
        </div>
      )}

      {/* Zoom Controls */}
      <div className="zoom-controls">
        <button onClick={() => setZoomLevel(Math.max(10, zoomLevel - 10))}>
          <ZoomOut size={16} />
        </button>
        <input
          type="range"
          min="10"
          max="150"
          value={zoomLevel}
          onChange={(e) => setZoomLevel(parseInt(e.target.value))}
          className="zoom-slider"
          style={{
            '--slider-percent': `${((zoomLevel - 10) / (150 - 10)) * 100}%`
          }}
        />
        <span>{zoomLevel}%</span>
        <button onClick={() => setZoomLevel(Math.min(150, zoomLevel + 10))}>
          <ZoomIn size={16} />
        </button>
        <button onClick={() => setZoomLevel(100)} title="Restablecer zoom">
          <RotateCcw size={16} />
        </button>
      </div>

      {/* Mensaje cuando no hay datos o está cargando - COMPLETAMENTE FUERA del table-container */}
      {filteredData.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          margin: '20px',
          border: '1px solid #e5e7eb'
        }}>
          {loadingData ? (
            <>
              <div style={{
                width: '48px',
                height: '48px',
                margin: '0 auto 16px',
                border: '3px solid #e5e7eb',
                borderTop: '3px solid var(--theme-primary, #c4fffc)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Cargando datos...
              </h3>
              <p style={{
                color: '#6b7280',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                Por favor espere mientras se cargan los registros
              </p>
            </>
          ) : (
            <>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}>
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                <line x1="9" y1="12" x2="15" y2="12"></line>
                <line x1="9" y1="16" x2="13" y2="16"></line>
              </svg>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                No hay registros disponibles
              </h3>
              <p style={{
                color: '#6b7280',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                Comience agregando datos usando los botones de abajo
              </p>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={addNewRow}
                  className="btn btn-primary"
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Plus size={16} />
                  Nueva Fila
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-secondary"
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <Upload size={16} />
                  Importar Excel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal de Edición */}
      {showModal && modalData && ReactDOM.createPortal(
        <div className="matriz-modal-overlay">
          <div
            className="matriz-modal-content"
            onClick={(e) => {
              e.stopPropagation();
              // Cerrar dropdowns si se hace clic fuera de ellos
              if (!e.target.closest('.select-wrapper')) {
                setShowClientDropdown(false);
                setShowSupplierDropdown(false);
              }
            }}
          >
            <div className="matriz-modal-header">
              <h2>
                Editar Registro
                <span className="header-badge">#{data.findIndex(r => r.id === modalData.id) + 1}</span>
              </h2>
              <button
                className="matriz-modal-close"
                onClick={() => setShowModal(false)}
                title="Cerrar modal"
                aria-label="Cerrar modal"
              />
            </div>

            <div className="matriz-modal-tabs">
              {[
                { id: 'etapa1', label: 'Cliente', num: '1', color: '#E0FFF4', activeColor: '#065f46' },
                { id: 'etapa2', label: 'Facturación', num: '2', color: '#FFE4E1', activeColor: '#9f1239' },
                { id: 'etapa3', label: 'Proveedor', num: '3', color: '#FFEFD5', activeColor: '#92400e' },
                { id: 'etapa4', label: 'Observaciones', num: '4', color: '#F0E6FF', activeColor: '#5b21b6' },
              ].filter(tab => filteredEtapas.some(e => e.id === tab.id)).map(tab => (
                <button
                  key={tab.id}
                  className={`matriz-modal-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  data-etapa={tab.id}
                  style={activeTab === tab.id ? {
                    background: tab.color,
                    color: tab.activeColor,
                    fontWeight: 700,
                    boxShadow: `0 1px 3px rgba(0,0,0,0.1), inset 0 -2px 0 ${tab.activeColor}33`
                  } : {}}
                >
                  <span className="tab-badge" style={activeTab === tab.id ? {
                    background: tab.activeColor,
                    color: '#fff'
                  } : {}}>{tab.num}</span>
                  {tab.label}
                </button>
              ))}
              <button
                className={`matriz-modal-tab ${activeTab === 'documentos' ? 'active' : ''}`}
                onClick={() => setActiveTab('documentos')}
                role="tab"
                aria-selected={activeTab === 'documentos'}
                data-etapa="documentos"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Paperclip size={14} />
                Docs
                {documentCounts[modalData?.item] > 0 && (
                  <span style={{
                    background: '#ef4444', color: 'white', borderRadius: '50%',
                    width: '18px', height: '18px', fontSize: '10px', fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    marginLeft: '2px'
                  }}>
                    {documentCounts[modalData?.item]}
                  </span>
                )}
              </button>
            </div>

            <div className="matriz-modal-body">
              {activeTab === 'documentos' ? (
                <ModalDocumentsTab
                  itemNumber={modalData?.item}
                  itemData={modalData}
                  documentCounts={documentCounts}
                  onDocumentChange={() => {
                    loadDocumentCounts();
                    loadModalItemDocs(modalData?.item);
                  }}
                />
              ) : (
              <>
              {/* Título de sección - visible solo en móviles */}
              <h3 className="section-title-mobile">
                {activeTab === 'etapa1' && 'CLIENTE'}
                {activeTab === 'etapa2' && 'FACTURACIÓN'}
                {activeTab === 'etapa3' && 'PROVEEDOR'}
                {activeTab === 'etapa4' && 'OBSERVACIONES'}
              </h3>
              <div className="form-grid">
                {filteredEtapas
                  .find(e => e.id === activeTab)
                  ?.columnas.map(col => (
                    <div key={col.key} className={`form-field ${col.required ? 'required' : ''}`}>
                      <label htmlFor={`field-${col.key}`}>{col.label}</label>
                    {col.calculated ? (
                      <input
                        id={`field-${col.key}`}
                        type="text"
                        value={formatValue(modalData[col.key], col.key)}
                        readOnly
                        className="form-field-input"
                        aria-label={`${col.label} (calculado automáticamente)`}
                      />
                    ) : col.key === 'cliente' ? (
                      <div className="select-wrapper">
                        <div className="select-input-wrapper">
                          <input
                            id={`field-${col.key}`}
                            ref={clientDropdownRef}
                            type="text"
                            className="form-field-input select-input"
                            value={searchClient || modalData[col.key] || ''}
                            placeholder="Buscar o seleccionar cliente..."
                            onChange={(e) => {
                              setSearchClient(e.target.value);
                              setShowClientDropdown(true);
                              if (!e.target.value) {
                                setModalData({ ...modalData, cliente: '' });
                              }
                            }}
                            onClick={(e) => {
                              // Toggle dropdown al hacer clic
                              if (showClientDropdown) {
                                setShowClientDropdown(false);
                              } else {
                                setDropdownMaxHeight(calculateDropdownHeight(e.target));
                                setShowClientDropdown(true);
                              }
                            }}
                          />
                          <ChevronDown className="select-icon" size={20} />
                        </div>
                        {showClientDropdown && (
                          <div className="select-dropdown" style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: '4px',
                            maxHeight: `${dropdownMaxHeight}px`,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            backgroundColor: 'white',
                            border: '2px solid black',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 999999
                          }}>
                            {/* Botón para nuevo cliente */}
                            <div
                              className="select-option"
                              onClick={() => {
                                setShowClientDropdown(false);
                                // Navegar a la página de clientes con parámetro para abrir modal
                                navigate('/clientes?openModal=true');
                              }}
                              style={{
                                borderBottom: '2px solid #000',
                                background: '#c4fffc',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                cursor: 'pointer',
                                padding: '12px 16px'
                              }}
                            >
                              <div className="select-option-left">
                                <UserPlus size={18} style={{color: '#000000'}} />
                                <div className="select-option-info">
                                  <div style={{
                                    color: '#000000',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    display: 'block',
                                    fontFamily: "'Space Grotesk', sans-serif"
                                  }}>
                                    Agregar Nuevo Cliente
                                  </div>
                                  <div style={{
                                    color: '#666666',
                                    fontSize: '12px',
                                    display: 'block',
                                    marginTop: '2px',
                                    fontFamily: "'Space Grotesk', sans-serif"
                                  }}>
                                    Crea un nuevo registro de cliente
                                  </div>
                                </div>
                              </div>
                            </div>
                            {clients.length > 0 ? (
                              clients
                                .filter(client => {
                                  const searchTerm = searchClient.toLowerCase();
                                  const clientName = (client.name || '').toLowerCase();
                                  const clientRut = (client.rut || '').toLowerCase();
                                  // Si no hay búsqueda, mostrar todos
                                  if (!searchTerm) return true;
                                  return clientName.includes(searchTerm) || clientRut.includes(searchTerm);
                                })
                                .slice(0, 10)
                                .map(client => (
                                  <div
                                    key={client.id}
                                    className="select-option"
                                    onClick={() => {
                                      setModalData({ ...modalData, cliente: client.name || 'Sin nombre' });
                                      setSearchClient('');
                                      setShowClientDropdown(false);
                                    }}
                                  >
                                    <div className="select-option-left">
                                      <Building2 size={16} style={{color: '#000000'}} />
                                      <div className="select-option-info">
                                        <div className="select-option-name" style={{
                                          color: '#000000',
                                          fontWeight: '600',
                                          fontSize: '14px',
                                          display: 'block',
                                          fontFamily: "'Space Grotesk', sans-serif"
                                        }}>
                                          {client.name || 'Sin nombre'}
                                        </div>
                                        <div className="select-option-meta" style={{
                                          color: '#666666',
                                          fontSize: '12px',
                                          display: 'block',
                                          marginTop: '2px',
                                          fontFamily: "'Space Grotesk', sans-serif"
                                        }}>
                                          {client.rut} {client.industry && `• ${client.industry}`}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                            ) : (
                              <div className="select-option-empty">
                                Cargando clientes...
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : col.key === 'proveedor' ? (
                      <div className="select-wrapper">
                        <div className="select-input-wrapper">
                          <input
                            id={`field-${col.key}`}
                            ref={supplierDropdownRef}
                            type="text"
                            className="form-field-input select-input"
                            value={searchSupplier || modalData[col.key] || ''}
                            placeholder="Buscar o seleccionar proveedor..."
                            onChange={(e) => {
                              setSearchSupplier(e.target.value);
                              setShowSupplierDropdown(true);
                              if (!e.target.value) {
                                setModalData({ ...modalData, proveedor: '' });
                              }
                            }}
                            onClick={(e) => {
                              // Toggle dropdown al hacer clic
                              if (showSupplierDropdown) {
                                setShowSupplierDropdown(false);
                              } else {
                                setDropdownMaxHeight(calculateDropdownHeight(e.target));
                                setShowSupplierDropdown(true);
                              }
                            }}
                          />
                          <ChevronDown className="select-icon" size={20} />
                        </div>
                        {showSupplierDropdown && (
                          <div className="select-dropdown" style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: '4px',
                            maxHeight: `${dropdownMaxHeight}px`,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            backgroundColor: 'white',
                            border: '2px solid black',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 999999
                          }}>
                            {/* Botón para nuevo proveedor */}
                            <div
                              className="select-option"
                              onClick={() => {
                                setShowSupplierDropdown(false);
                                // Navegar a la página de proveedores con parámetro para abrir modal
                                navigate('/proveedores?openModal=true');
                              }}
                              style={{
                                borderBottom: '2px solid #000',
                                background: '#c4fffc',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                cursor: 'pointer',
                                padding: '12px 16px'
                              }}
                            >
                              <div className="select-option-left">
                                <UserPlus size={18} style={{color: '#000000'}} />
                                <div className="select-option-info">
                                  <div style={{
                                    color: '#000000',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    display: 'block',
                                    fontFamily: "'Space Grotesk', sans-serif"
                                  }}>
                                    Agregar Nuevo Proveedor
                                  </div>
                                  <div style={{
                                    color: '#666666',
                                    fontSize: '12px',
                                    display: 'block',
                                    marginTop: '2px',
                                    fontFamily: "'Space Grotesk', sans-serif"
                                  }}>
                                    Crea un nuevo registro de proveedor
                                  </div>
                                </div>
                              </div>
                            </div>
                            {suppliers
                              .filter(supplier => {
                                const searchTerm = searchSupplier.toLowerCase();
                                const supplierName = (supplier.name || '').toLowerCase();
                                const supplierRut = (supplier.rut || '').toLowerCase();
                                // Si no hay búsqueda, mostrar todos
                                if (!searchTerm) return true;
                                return supplierName.includes(searchTerm) || supplierRut.includes(searchTerm);
                              })
                              .slice(0, 10)
                              .map(supplier => (
                                <div
                                  key={supplier.id}
                                  className="select-option"
                                  onClick={() => {
                                    setModalData({ ...modalData, proveedor: supplier.name || 'Sin nombre' });
                                    setSearchSupplier('');
                                    setShowSupplierDropdown(false);
                                  }}
                                >
                                  <div className="select-option-left">
                                    <Users size={16} style={{color: '#000000'}} />
                                    <div className="select-option-info">
                                      <div className="select-option-name" style={{
                                        color: '#000000',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        display: 'block',
                                        fontFamily: "'Space Grotesk', sans-serif"
                                      }}>
                                        {supplier.name || 'Sin nombre'}
                                      </div>
                                      <div className="select-option-meta" style={{
                                        color: '#666666',
                                        fontSize: '12px',
                                        display: 'block',
                                        marginTop: '2px',
                                        fontFamily: "'Space Grotesk', sans-serif"
                                      }}>
                                        {supplier.rut} {supplier.industry && `• ${supplier.industry}`}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            }
                            {suppliers.filter(supplier => {
                              const searchTerm = (searchSupplier || '').toLowerCase();
                              const supplierName = (supplier.name || '').toLowerCase();
                              return supplierName.includes(searchTerm);
                            }).length === 0 && (
                              <div className="select-option-empty">
                                No se encontraron proveedores
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : ['delivery_date', 'real_delivery'].includes(col.key) ? (
                      // Selector de fecha simple
                      <div className="date-input-wrapper" style={{ position: 'relative' }}>
                        <input
                          id={`field-${col.key}`}
                          type="date"
                          className="form-field-input"
                          value={(() => {
                            const dateValue = modalData[col.key];
                            if (!dateValue) return '';
                            // Si ya está en formato yyyy-mm-dd, retornar tal cual
                            if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                              return dateValue;
                            }
                            // Si está en formato dd-mm-yyyy, convertir a yyyy-mm-dd
                            if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
                              const parts = dateValue.split('-');
                              return `${parts[2]}-${parts[1]}-${parts[0]}`;
                            }
                            return dateValue;
                          })()}
                          onChange={(e) => {
                            // Guardar en formato YYYY-MM-DD (igual que el backend)
                            const value = e.target.value;
                            setModalData({ ...modalData, [col.key]: value || '' });
                          }}
                          onClick={(e) => {
                            // Forzar apertura del calendario
                            e.target.showPicker && e.target.showPicker();
                          }}
                          style={{ paddingRight: '40px', cursor: 'pointer' }}
                        />
                        <Calendar
                          size={20}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none',
                            color: 'var(--theme-secondary, #000)'
                          }}
                        />
                      </div>
                    ) : ['emision_fact', 'vencimiento', 'fecha_pago', 'emision_prov', 'vencimiento_prov', 'fecha_pago_prov'].includes(col.key) ? (
                      // Selector de rango de fechas o fecha simple
                      <div className="date-input-wrapper" style={{ position: 'relative' }}>
                        <input
                          id={`field-${col.key}`}
                          type="text"
                          className="form-field-input date-picker-input"
                          value={modalData[col.key] || ''}
                          placeholder="Click para seleccionar fecha(s)"
                          readOnly
                          onClick={() => {
                            // Toggle behavior - cerrar si ya está abierto para el mismo campo
                            if (showDateRangePicker && activeDateField === col.key) {
                              setShowDateRangePicker(false);
                              setActiveDateField(null);
                              return;
                            }

                            setActiveDateField(col.key);
                            setShowDateRangePicker(true);
                            // Parsear fechas existentes si las hay y convertir al formato del input date
                            const value = modalData[col.key] || '';
                            const convertToInputDate = (dateStr) => {
                              if (!dateStr) return '';
                              const parts = dateStr.trim().split('-');
                              if (parts.length === 3) {
                                // Formato DD-MM-YYYY a YYYY-MM-DD
                                return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                              }
                              return '';
                            };

                            if (value.includes(' / ')) {
                              const [start, end] = value.split(' / ');
                              const startDate = convertToInputDate(start);
                              const endDate = convertToInputDate(end);
                              setDateRangeStart(startDate);
                              setDateRangeEnd(endDate);
                              setIsRangeMode(true);
                              // Guardar valores originales para comparación
                              setOriginalDateStart(startDate);
                              setOriginalDateEnd(endDate);
                              setOriginalIsRangeMode(true);
                            } else if (value) {
                              const startDate = convertToInputDate(value);
                              setDateRangeStart(startDate);
                              setDateRangeEnd('');
                              setIsRangeMode(false);
                              // Guardar valores originales para comparación
                              setOriginalDateStart(startDate);
                              setOriginalDateEnd('');
                              setOriginalIsRangeMode(false);
                            } else {
                              setDateRangeStart('');
                              setDateRangeEnd('');
                              setIsRangeMode(false);
                              // Guardar valores originales para comparación
                              setOriginalDateStart('');
                              setOriginalDateEnd('');
                              setOriginalIsRangeMode(false);
                            }
                          }}
                          style={{
                            paddingRight: '40px',
                            cursor: 'pointer',
                            backgroundColor: '#ffffff',
                            color: '#000000'
                          }}
                        />
                        <Calendar
                          size={20}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--theme-secondary, #000)',
                            pointerEvents: 'none'
                          }}
                        />

                        {/* Picker de rango de fechas */}
                        {showDateRangePicker && activeDateField === col.key && (
                          <>
                            {/* Backdrop */}
                            <div
                              style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0, 0, 0, 0.3)',
                                zIndex: 10000
                              }}
                              onClick={() => {
                                setShowDateRangePicker(false);
                                setActiveDateField(null);
                              }}
                            />

                            {/* Date picker modal */}
                            <div style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: '#ffffff',
                            border: '2px solid var(--theme-secondary, #000)',
                            borderRadius: '8px',
                            padding: '16px',
                            zIndex: 10001,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                            minWidth: '360px',
                            maxWidth: '400px',
                            maxHeight: '80vh',
                            overflowY: 'auto'
                          }}>
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '700',
                                marginBottom: '12px',
                                color: 'var(--theme-secondary, #000)',
                                fontFamily: "'Space Grotesk', sans-serif"
                              }}>
                                Seleccionar fecha(s) para {col.label}
                              </div>

                              {/* Toggle buttons para modo de selección */}
                              <div style={{
                                display: 'flex',
                                gap: '8px',
                                marginTop: '12px',
                                padding: '4px',
                                background: '#f3f4f6',
                                borderRadius: '8px'
                              }}>
                                <button
                                  onClick={() => {
                                    setIsRangeMode(false);
                                    setDateRangeEnd('');
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    background: !isRangeMode ? 'var(--theme-secondary, #000)' : 'transparent',
                                    color: !isRangeMode ? 'var(--theme-primary, #c4fffc)' : '#6b7280',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    fontFamily: "'Space Grotesk', sans-serif"
                                  }}
                                >
                                  Fecha única
                                </button>
                                <button
                                  onClick={() => setIsRangeMode(true)}
                                  style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    background: isRangeMode ? 'var(--theme-secondary, #000)' : 'transparent',
                                    color: isRangeMode ? 'var(--theme-primary, #c4fffc)' : '#6b7280',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    fontFamily: "'Space Grotesk', sans-serif"
                                  }}
                                >
                                  Rango de fechas
                                </button>
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                              <div style={{ flex: 1 }}>
                                <label style={{
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  display: 'block',
                                  marginBottom: '6px',
                                  color: 'var(--theme-secondary, #000)',
                                  fontFamily: "'Space Grotesk', sans-serif",
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  {isRangeMode ? 'Fecha inicio:' : 'Fecha única:'}
                                </label>
                                <input
                                  type="date"
                                  value={dateRangeStart}
                                  onChange={(e) => setDateRangeStart(e.target.value)}
                                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    background: '#ffffff'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = 'var(--theme-secondary, #000)';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(var(--theme-primary-rgb, 196, 255, 252), 0.3)';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#e5e7eb';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                />
                              </div>

                              {isRangeMode && (
                                <>
                                  <span style={{
                                    marginTop: '28px',
                                    fontSize: '20px',
                                    color: 'var(--theme-secondary, #000)'
                                  }}>→</span>
                                  <div style={{ flex: 1 }}>
                                    <label style={{
                                      fontSize: '12px',
                                      fontWeight: '700',
                                      display: 'block',
                                      marginBottom: '6px',
                                      color: 'var(--theme-secondary, #000)',
                                      fontFamily: "'Space Grotesk', sans-serif",
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      Fecha fin:
                                    </label>
                                    <input
                                      type="date"
                                      value={dateRangeEnd}
                                      onChange={(e) => setDateRangeEnd(e.target.value)}
                                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                      min={dateRangeStart}
                                      style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontFamily: "'Space Grotesk', sans-serif",
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        background: '#ffffff'
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--theme-secondary, #000)';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(var(--theme-primary-rgb, 196, 255, 252), 0.3)';
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                    />
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Mensaje de validación */}
                            {isRangeMode && (!dateRangeStart || !dateRangeEnd) && (
                              <div style={{
                                marginTop: '12px',
                                padding: '8px 12px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#dc2626',
                                fontFamily: "'Space Grotesk', sans-serif"
                              }}>
                                ⚠️ Debes seleccionar ambas fechas para el rango
                              </div>
                            )}

                            {!isRangeMode && !dateRangeStart && (
                              <div style={{
                                marginTop: '12px',
                                padding: '8px 12px',
                                background: 'rgba(59, 130, 246, 0.1)',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#2563eb',
                                fontFamily: "'Space Grotesk', sans-serif"
                              }}>
                                ℹ️ Selecciona una fecha para continuar
                              </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => {
                                  setShowDateRangePicker(false);
                                  setActiveDateField(null);
                                }}
                                style={{
                                  padding: '8px 16px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  background: 'white',
                                  cursor: 'pointer'
                                }}
                              >
                                Cancelar
                              </button>
                              <button
                                onClick={() => {
                                  // Formatear las fechas evitando problemas de timezone
                                  const formatDate = (dateStr) => {
                                    if (!dateStr) return '';
                                    // Dividir la fecha YYYY-MM-DD para evitar timezone issues
                                    const [year, month, day] = dateStr.split('-');
                                    return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
                                  };

                                  let value = '';
                                  if (isRangeMode && dateRangeStart && dateRangeEnd) {
                                    value = `${formatDate(dateRangeStart)} / ${formatDate(dateRangeEnd)}`;
                                  } else if (!isRangeMode && dateRangeStart) {
                                    value = formatDate(dateRangeStart);
                                  }

                                  setModalData({ ...modalData, [col.key]: value });
                                  setShowDateRangePicker(false);
                                  setActiveDateField(null);
                                }}
                                disabled={(() => {
                                  // Deshabilitar si no hay fechas seleccionadas
                                  if (isRangeMode ? (!dateRangeStart || !dateRangeEnd) : !dateRangeStart) {
                                    return true;
                                  }
                                  // Deshabilitar si las fechas no han cambiado
                                  const hasChanged =
                                    dateRangeStart !== originalDateStart ||
                                    dateRangeEnd !== originalDateEnd ||
                                    isRangeMode !== originalIsRangeMode;
                                  return !hasChanged;
                                })()}
                                style={{
                                  padding: '8px 16px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  background: (() => {
                                    const isDisabled =
                                      (isRangeMode ? (!dateRangeStart || !dateRangeEnd) : !dateRangeStart) ||
                                      (dateRangeStart === originalDateStart &&
                                       dateRangeEnd === originalDateEnd &&
                                       isRangeMode === originalIsRangeMode);
                                    return isDisabled ? '#e5e7eb' : 'var(--theme-secondary, #000)';
                                  })(),
                                  color: (() => {
                                    const isDisabled =
                                      (isRangeMode ? (!dateRangeStart || !dateRangeEnd) : !dateRangeStart) ||
                                      (dateRangeStart === originalDateStart &&
                                       dateRangeEnd === originalDateEnd &&
                                       isRangeMode === originalIsRangeMode);
                                    return isDisabled ? '#9ca3af' : 'var(--theme-primary, #c4fffc)';
                                  })(),
                                  cursor: (() => {
                                    const isDisabled =
                                      (isRangeMode ? (!dateRangeStart || !dateRangeEnd) : !dateRangeStart) ||
                                      (dateRangeStart === originalDateStart &&
                                       dateRangeEnd === originalDateEnd &&
                                       isRangeMode === originalIsRangeMode);
                                    return isDisabled ? 'not-allowed' : 'pointer';
                                  })(),
                                  fontWeight: '600',
                                  opacity: (() => {
                                    const isDisabled =
                                      (isRangeMode ? (!dateRangeStart || !dateRangeEnd) : !dateRangeStart) ||
                                      (dateRangeStart === originalDateStart &&
                                       dateRangeEnd === originalDateEnd &&
                                       isRangeMode === originalIsRangeMode);
                                    return isDisabled ? 0.5 : 1;
                                  })(),
                                  transition: 'all 0.2s'
                                }}
                              >
                                Aplicar
                              </button>
                            </div>
                          </div>
                          </>
                        )}
                      </div>
                    ) : ['pu_clp', 'pu_proveedor'].includes(col.key) ? (
                      // Campo de precio con formato de moneda
                      <input
                        id={`field-${col.key}`}
                        type="text"
                        className="form-field-input"
                        value={modalData[col.key] ? `$${Number(modalData[col.key]).toLocaleString('es-CL')}` : ''}
                        placeholder="$0"
                        onChange={(e) => {
                          // Remover formato para guardar solo el número
                          const value = e.target.value.replace(/[^\d]/g, '');
                          const numValue = parseInt(value) || 0;
                          const updatedRow = { ...modalData, [col.key]: numValue };

                          // Aplicar cálculos automáticos
                          if (col.key === 'pu_clp') {
                            updatedRow.total_neto = (parseFloat(updatedRow.cantidad) || 0) * numValue;
                          }
                          if (col.key === 'pu_proveedor') {
                            updatedRow.total_proveedor = (parseFloat(updatedRow.cantidad) || 0) * numValue;
                          }
                          updatedRow.u_del_negocio = calculateUtilidad(updatedRow.total_neto, updatedRow.total_proveedor);

                          setModalData(updatedRow);
                        }}
                      />
                    ) : col.key === 'unidad' ? (
                      // Selector de unidades con búsqueda
                      <div className="select-wrapper" ref={unitDropdownRef} style={{ position: 'relative' }}>
                        <div className="select-input-wrapper">
                          <input
                            id={`field-${col.key}`}
                            type="text"
                            className="form-field-input"
                            value={showUnitDropdown ? searchUnit : (modalData[col.key] || '')}
                            placeholder="Seleccionar o buscar unidad..."
                            onFocus={() => {
                              setShowUnitDropdown(true);
                              setSearchUnit(''); // Clear search on focus to show all options
                            }}
                            onChange={(e) => {
                              setSearchUnit(e.target.value.toUpperCase());
                              setShowUnitDropdown(true);
                            }}
                            style={{ paddingRight: '40px' }}
                          />
                          <ChevronDown
                            size={18}
                            className="select-icon"
                            onClick={() => {
                              if (!showUnitDropdown) {
                                setSearchUnit(''); // Clear search when opening
                              }
                              setShowUnitDropdown(!showUnitDropdown);
                            }}
                            style={{ cursor: 'pointer', pointerEvents: 'all' }}
                          />
                        </div>

                        {showUnitDropdown && (
                          <div
                            className="select-dropdown"
                            style={{
                              maxHeight: `${dropdownMaxHeight}px`,
                              overflowY: 'auto'
                            }}
                          >
                            {/* Botón para agregar nueva unidad */}
                            <div
                              className="select-option"
                              onClick={() => {
                                setShowAddUnitModal(true);
                                setShowUnitDropdown(false);
                              }}
                              style={{
                                background: 'var(--theme-primary, #c4fffc)',
                                borderBottom: '2px solid var(--theme-secondary, #000)',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                              }}
                            >
                              <div className="select-option-left">
                                <Plus size={18} style={{ color: 'var(--theme-secondary, #000)' }} />
                                <div className="select-option-info">
                                  <span className="select-option-name" style={{ color: 'var(--theme-secondary, #000)', fontWeight: 700 }}>
                                    Agregar Nueva Unidad
                                  </span>
                                  <span className="select-option-meta" style={{ color: 'var(--theme-secondary, #000)' }}>
                                    Crea una nueva unidad de medida
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Lista de unidades filtradas */}
                            {availableUnits
                              .filter(unit => unit.toLowerCase().includes((searchUnit || '').toLowerCase()))
                              .map(unit => (
                                <div
                                  key={unit}
                                  className="select-option"
                                  onClick={() => {
                                    setModalData({ ...modalData, unidad: unit });
                                    setSearchUnit('');
                                    setShowUnitDropdown(false);
                                  }}
                                >
                                  <div className="select-option-left" style={{ paddingLeft: '10px' }}>
                                    <div className="select-option-info">
                                      <span className="select-option-name" style={{ color: 'black', fontSize: '14px', fontWeight: 600 }}>
                                        {unit}
                                      </span>
                                      <span className="select-option-meta" style={{ color: '#666', fontSize: '12px' }}>
                                        {unit === 'CU' && 'Cada Unidad'}
                                        {unit === 'ROLLO' && 'Unidad de rollo'}
                                        {unit === 'MTS' && 'Metros'}
                                        {unit === 'UN' && 'Unidad'}
                                        {unit === 'PZA' && 'Pieza'}
                                        {unit === 'KG' && 'Kilogramos'}
                                        {unit === 'GR' && 'Gramos'}
                                        {unit === 'TON' && 'Toneladas'}
                                        {unit === 'LT' && 'Litros'}
                                        {unit === 'ML' && 'Mililitros'}
                                        {unit === 'GAL' && 'Galones'}
                                        {unit === 'M2' && 'Metros cuadrados'}
                                        {unit === 'M3' && 'Metros cúbicos'}
                                        {unit === 'CM' && 'Centímetros'}
                                        {unit === 'MM' && 'Milímetros'}
                                        {unit === 'CAJA' && 'Caja'}
                                        {unit === 'PAQUETE' && 'Paquete'}
                                        {unit === 'SET' && 'Conjunto'}
                                        {unit === 'PAR' && 'Par'}
                                        {unit === 'BOLSA' && 'Bolsa'}
                                        {unit === 'LATA' && 'Lata'}
                                        {unit === 'BOTELLA' && 'Botella'}
                                        {unit === 'BALDE' && 'Balde'}
                                        {unit === 'TAMBOR' && 'Tambor'}
                                        {unit === 'HR' && 'Horas'}
                                        {unit === 'DIA' && 'Días'}
                                        {unit === 'MES' && 'Meses'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {availableUnits.filter(unit =>
                              unit.toLowerCase().includes((searchUnit || '').toLowerCase())
                            ).length === 0 && (
                              <div className="select-option-empty">
                                No se encontraron unidades
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : ['part_number', 'cin'].includes(col.key) ? (
                      // Campo con opción "No Aplica"
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          id={`field-${col.key}`}
                          type="text"
                          className="form-field-input"
                          value={modalData[col.key] || ''}
                          placeholder={modalData[col.key] === 'No Aplica' ? '' : "Escribir valor..."}
                          onChange={(e) => setModalData({ ...modalData, [col.key]: e.target.value })}
                          disabled={modalData[col.key] === 'No Aplica'}
                          style={{
                            flex: 1,
                            backgroundColor: modalData[col.key] === 'No Aplica' ? '#f5f5f5' : '#ffffff',
                            cursor: modalData[col.key] === 'No Aplica' ? 'not-allowed' : 'text',
                            opacity: modalData[col.key] === 'No Aplica' ? 0.7 : 1
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (modalData[col.key] === 'No Aplica') {
                              // Si ya está en "No Aplica", limpiar el campo y habilitarlo
                              setModalData({ ...modalData, [col.key]: '' });
                            } else {
                              // Si no está en "No Aplica", establecerlo
                              setModalData({ ...modalData, [col.key]: 'No Aplica' });
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            background: modalData[col.key] === 'No Aplica'
                              ? 'var(--theme-primary, #c4fffc)'
                              : 'var(--theme-secondary, #000)',
                            color: modalData[col.key] === 'No Aplica'
                              ? 'var(--theme-secondary, #000)'
                              : 'var(--theme-primary, #c4fffc)',
                            border: '2px solid var(--theme-secondary, #000)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontWeight: '600',
                            transition: 'all 0.2s',
                            minWidth: '90px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {modalData[col.key] === 'No Aplica' ? '✓ No Aplica' : 'No Aplica'}
                        </button>
                      </div>
                    ) : col.key === 'item2' ? (
                      // Selector para ITEM2
                      <div className="select-wrapper" ref={item2DropdownRef} style={{ position: 'relative' }}>
                        <div className="select-input-wrapper">
                          <input
                            id="field-item2"
                            type="text"
                            className="form-field-input"
                            value={modalData[col.key] || ''}
                            placeholder="Escribe o selecciona..."
                            onChange={(e) => {
                              // Permitir solo números
                              const value = e.target.value.replace(/[^\d]/g, '');
                              setModalData({ ...modalData, item2: value });
                            }}
                            onClick={() => setShowItem2Dropdown(true)}
                            style={{ paddingRight: '40px' }}
                          />
                          <ChevronDown
                            size={18}
                            className="select-icon"
                            onClick={() => setShowItem2Dropdown(!showItem2Dropdown)}
                            style={{ cursor: 'pointer', pointerEvents: 'all' }}
                          />
                        </div>

                        {showItem2Dropdown && (
                          <div
                            className="select-dropdown"
                            style={{
                              maxHeight: '250px',
                              overflowY: 'auto'
                            }}
                          >
                            {/* Mensaje principal al inicio */}
                            <div style={{
                              padding: '12px 16px',
                              background: 'var(--theme-secondary, #000000)',
                              color: '#ffffff',
                              fontSize: '14px',
                              fontWeight: '600',
                              textAlign: 'center',
                              borderBottom: '2px solid var(--theme-primary, #c4fffc)',
                              fontFamily: "'Space Grotesk', sans-serif",
                              position: 'sticky',
                              top: 0,
                              zIndex: 10,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              💡 Puedes escribir manualmente o seleccionar
                            </div>

                            {/* Lista de valores predefinidos con diseño mejorado */}
                            {item2Options.map(value => (
                              <div
                                key={value}
                                className="select-option"
                                onClick={() => {
                                  setModalData({ ...modalData, item2: value.toString() });
                                  setShowItem2Dropdown(false);
                                }}
                                style={{
                                  padding: '10px 16px',
                                  borderBottom: '1px solid #eee',
                                  cursor: 'pointer',
                                  textAlign: 'center',
                                  fontSize: '15px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <span style={{
                                  color: '#000',
                                  fontFamily: "'Space Grotesk', sans-serif"
                                }}>
                                  {value}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (FIELD_DOC_TYPE_MAP[col.key] && MULTI_DOC_TYPES.has(FIELD_DOC_TYPE_MAP[col.key])) ? (
                      /* Multi-doc: display references list + clip para subir */
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                        <div
                          className="form-field-input"
                          style={{
                            flex: 1, display: 'flex', flexDirection: 'column', gap: '4px',
                            color: '#6b7280', cursor: 'default', minHeight: '36px', padding: '8px 12px'
                          }}
                        >
                          {(() => {
                            const docs = modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]] || [];
                            if (docs.length === 0) return <span style={{ color: '#94a3b8', fontSize: '13px' }}>Sin documentos</span>;
                            return docs.map((doc, idx) => (
                              <div key={doc.id} style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                fontSize: '13px', color: '#1e293b', fontWeight: 500,
                                padding: idx > 0 ? '3px 0 0 0' : '0',
                                borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none'
                              }}>
                                <span style={{
                                  width: '18px', height: '18px', borderRadius: '4px',
                                  background: '#f1f5f9', color: '#64748b',
                                  fontSize: '10px', fontWeight: 700,
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>{idx + 1}</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {doc.doc_reference || doc.original_name}
                                </span>
                              </div>
                            ));
                          })()}
                        </div>
                        <button
                          onClick={() => {

                            setFieldUploadDocType(FIELD_DOC_TYPE_MAP[col.key]);
                            setFieldUploadContext({ itemNumber: modalData.item, row: modalData });
                            setShowFieldUpload(true);
                          }}
                          title={`Adjuntar ${DOC_TYPE_LABELS_SHORT[FIELD_DOC_TYPE_MAP[col.key]] || 'documento'}`}
                          style={{
                            position: 'relative',
                            width: '36px', height: '36px', flexShrink: 0,
                            borderRadius: '8px', cursor: 'pointer',
                            border: modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0
                              ? '2px solid var(--theme-secondary, #000)'
                              : '2px dashed #d1d5db',
                            background: modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0
                              ? 'var(--theme-secondary, #000)'
                              : '#fafafa',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Paperclip size={15} style={{
                            color: modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0
                              ? 'var(--theme-primary, #c4fffc)'
                              : '#9ca3af'
                          }} />
                          {modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0 && (
                            <span style={{
                              position: 'absolute', top: '-6px', right: '-6px',
                              background: '#ef4444', color: 'white',
                              fontSize: '9px', fontWeight: 800, lineHeight: '16px',
                              width: '16px', height: '16px', borderRadius: '50%',
                              textAlign: 'center'
                            }}>
                              {modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]].length}
                            </span>
                          )}
                        </button>
                      </div>
                    ) : FIELD_DOC_TYPE_MAP[col.key] ? (
                      /* Single-doc: input editable + clip (replace existing) */
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          id={`field-${col.key}`}
                          type="text"
                          className="form-field-input"
                          style={{ flex: 1 }}
                          value={modalData[col.key] || ''}
                          placeholder={`Ingrese ${col.label.toLowerCase()}`}
                          onChange={(e) => {
                            setModalData({ ...modalData, [col.key]: e.target.value });
                          }}
                        />
                        <button
                          onClick={() => {

                            const existingDocs = modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]] || [];
                            setFieldUploadDocType(FIELD_DOC_TYPE_MAP[col.key]);
                            setFieldUploadContext({
                              itemNumber: modalData.item,
                              row: modalData,
                              replaceDocIds: existingDocs.map(d => d.id)
                            });
                            setShowFieldUpload(true);
                          }}
                          title={modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0
                            ? `Reemplazar ${DOC_TYPE_LABELS_SHORT[FIELD_DOC_TYPE_MAP[col.key]] || 'documento'}`
                            : `Adjuntar ${DOC_TYPE_LABELS_SHORT[FIELD_DOC_TYPE_MAP[col.key]] || 'documento'}`}
                          style={{
                            position: 'relative',
                            width: '36px', height: '36px', flexShrink: 0,
                            borderRadius: '8px', cursor: 'pointer',
                            border: modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0
                              ? '2px solid var(--theme-secondary, #000)'
                              : '2px dashed #d1d5db',
                            background: modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0
                              ? 'var(--theme-secondary, #000)'
                              : '#fafafa',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Paperclip size={15} style={{
                            color: modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0
                              ? 'var(--theme-primary, #c4fffc)'
                              : '#9ca3af'
                          }} />
                          {modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]]?.length > 0 && (
                            <span style={{
                              position: 'absolute', top: '-6px', right: '-6px',
                              background: '#ef4444', color: 'white',
                              fontSize: '9px', fontWeight: 800, lineHeight: '16px',
                              width: '16px', height: '16px', borderRadius: '50%',
                              textAlign: 'center'
                            }}>
                              {modalItemDocs[FIELD_DOC_TYPE_MAP[col.key]].length}
                            </span>
                          )}
                        </button>
                      </div>
                    ) : col.key === 'observaciones' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setObsPanel({
                            itemNumber: modalData.item,
                            itemData: {
                              cliente: modalData.cliente,
                              descripcion: modalData.descripcion
                            }
                          });
                        }}
                        style={{
                          width: '100%', padding: '10px 16px', borderRadius: '8px',
                          border: '2px solid var(--theme-secondary, #000)',
                          background: 'var(--theme-secondary, #000)',
                          color: 'var(--theme-primary, #c4fffc)',
                          cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                          fontFamily: "'Space Grotesk', sans-serif",
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                        }}
                      >
                        <MessageSquare size={16} />
                        Ver Observaciones ({obsCounts[modalData.item] || 0})
                      </button>
                    ) : (
                      <input
                        id={`field-${col.key}`}
                        type="text"
                        className="form-field-input"
                        value={(() => {
                          const val = modalData[col.key] || '';
                          // Para campos numéricos, eliminar decimales innecesarios
                          if (['cantidad', 'utilidad_factor'].includes(col.key) && val !== '') {
                            const num = parseFloat(val);
                            return num % 1 === 0 ? parseInt(num).toString() : val;
                          }
                          return val;
                        })()}
                        placeholder={`Ingrese ${col.label.toLowerCase()}`}
                        onChange={(e) => {
                          const updatedRow = { ...modalData, [col.key]: e.target.value };
                          // Aplicar cálculos automáticos
                          if (col.key === 'cantidad') {
                            updatedRow.total_neto = (parseFloat(e.target.value) || 0) * (parseFloat(updatedRow.pu_clp) || 0);
                            updatedRow.total_proveedor = (parseFloat(e.target.value) || 0) * (parseFloat(updatedRow.pu_proveedor) || 0);
                            updatedRow.u_del_negocio = calculateUtilidad(updatedRow.total_neto, updatedRow.total_proveedor);
                          }
                          setModalData(updatedRow);
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              </>
              )}
            </div>

            <div className="matriz-modal-footer">
              <div className="footer-info">
                <span>Cliente: <strong>{modalData.cliente || 'Sin cliente'}</strong></span>
                <div className="status-indicator">Editando</div>
              </div>
              <div className="footer-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  aria-label="Cancelar edición"
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const updatedData = data.map(row =>
                      row.id === modalData.id ? modalData : row
                    );
                    setData(updatedData);
                    setHasChanges(true);
                    setShowModal(false);
                    // Recargar colaboradores para mostrar badge inmediatamente
                    setTimeout(() => {
                      loadCollaborators();
                    }, 1000);
                  }}
                  aria-label="Guardar cambios"
                >
                  <Save size={14} />
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>

          {/* Upload modal para adjuntar documento desde campo del formulario */}
          <DocumentUploadModal
            isOpen={showFieldUpload}
            onClose={() => setShowFieldUpload(false)}
            itemNumber={modalData?.item}
            itemData={modalData}
            onUploadSuccess={() => {
              loadDocumentCounts();
              loadModalItemDocs(modalData?.item);
            }}
            defaultDocType={fieldUploadDocType}
          />
        </div>,
        document.body
      )}

      {/* Modal para agregar nueva unidad */}
      {showAddUnitModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="matriz-modal-header">
              <h2>Agregar Nueva Unidad</h2>
              <button
                className="matriz-modal-close"
                onClick={() => {
                  setShowAddUnitModal(false);
                  setNewUnitName('');
                }}
                type="button"
              >
                <X size={20} />
              </button>
            </div>

            <div className="matriz-modal-body">
              <div className="form-field">
                <label htmlFor="new-unit" className="form-field-label">
                  Nombre de la unidad
                </label>
                <input
                  id="new-unit"
                  type="text"
                  className="form-field-input"
                  value={newUnitName}
                  onChange={(e) => setNewUnitName(e.target.value.toUpperCase())}
                  placeholder="Ej: M2, GAL, TON..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newUnitName.trim()) {
                      const trimmedUnit = newUnitName.trim();
                      if (!availableUnits.includes(trimmedUnit)) {
                        setAvailableUnits([...availableUnits, trimmedUnit]);
                        setModalData({ ...modalData, unidad: trimmedUnit });
                      }
                      setShowAddUnitModal(false);
                      setNewUnitName('');
                    }
                  }}
                />
              </div>
            </div>

            <div className="matriz-modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddUnitModal(false);
                  setNewUnitName('');
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!newUnitName.trim() || availableUnits.includes(newUnitName.trim())}
                onClick={() => {
                  const trimmedUnit = newUnitName.trim();
                  if (trimmedUnit && !availableUnits.includes(trimmedUnit)) {
                    setAvailableUnits([...availableUnits, trimmedUnit]);
                    setModalData({ ...modalData, unidad: trimmedUnit });
                  }
                  setShowAddUnitModal(false);
                  setNewUnitName('');
                }}
              >
                <Plus size={14} />
                Agregar Unidad
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Importación */}
      {showImportModal && (
        <div className="matriz-modal-overlay" style={{ zIndex: 10000 }}>
          <div className="matriz-modal-content" style={{
            maxWidth: '900px',
            backgroundColor: '#ffffff'
          }}>
            <div className="matriz-modal-header">
              <h2>
                📊 Importación de Datos Excel
              </h2>
              <button
                className="matriz-modal-close"
                onClick={() => {
                  setShowImportModal(false);
                  setImportResults(null);
                  setShowPreview(false);
                  setDuplicateItems([]);
                  setDatosAnteriores([]);
                  setNewClientsMap({});
                  setNewSuppliersMap({});
                  setPendingImportData(null);
                  setImportStep('uploading');
                }}
              />
            </div>

            <div className="matriz-modal-body">
              {/* Detección de duplicados */}
              {duplicateItems.length > 0 && importStep === 'preview' && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#fff3cd',
                  border: '3px solid #dc3545',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(220, 53, 69, 0.3)'
                }}>
                  <h3 style={{
                    marginBottom: '12px',
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#000'
                  }}>
                    ⚠️ Items Duplicados Detectados
                  </h3>
                  <p style={{
                    marginBottom: '12px',
                    fontSize: '14px',
                    color: '#333'
                  }}>
                    Se encontraron {duplicateItems.length} items que ya existen en la base de datos:
                  </p>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginBottom: '16px'
                  }}>
                    {duplicateItems.slice(0, 10).map(item => (
                      <span key={item} style={{
                        padding: '4px 12px',
                        backgroundColor: '#000',
                        color: '#fff3cd',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        Item #{item}
                      </span>
                    ))}
                    {duplicateItems.length > 10 && (
                      <span style={{
                        padding: '4px 12px',
                        backgroundColor: '#666',
                        color: '#fff',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        +{duplicateItems.length - 10} más
                      </span>
                    )}
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px'
                  }}>
                    <p style={{
                      marginBottom: '12px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      ¿Qué desea hacer con los registros duplicados?
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: overwriteMode === 'overwrite' ? '#e7f5ff' : 'transparent',
                        border: overwriteMode === 'overwrite' ? '2px solid #1c7ed6' : '2px solid transparent'
                      }}>
                        <input
                          type="radio"
                          value="overwrite"
                          checked={overwriteMode === 'overwrite'}
                          onChange={(e) => setOverwriteMode(e.target.value)}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <div>
                          <strong style={{ fontSize: '14px' }}>🔄 Sobrescribir</strong>
                          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            Actualizar los registros existentes con los nuevos datos
                          </p>
                        </div>
                      </label>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: overwriteMode === 'skip' ? '#fff5f5' : 'transparent',
                        border: overwriteMode === 'skip' ? '2px solid #c92a2a' : '2px solid transparent'
                      }}>
                        <input
                          type="radio"
                          value="skip"
                          checked={overwriteMode === 'skip'}
                          onChange={(e) => setOverwriteMode(e.target.value)}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <div>
                          <strong style={{ fontSize: '14px' }}>⏭️ Omitir</strong>
                          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            Mantener los registros existentes sin cambios
                          </p>
                        </div>
                      </label>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: overwriteMode === 'ask' ? '#f3f0ff' : 'transparent',
                        border: overwriteMode === 'ask' ? '2px solid #7048e8' : '2px solid transparent'
                      }}>
                        <input
                          type="radio"
                          value="ask"
                          checked={overwriteMode === 'ask'}
                          onChange={(e) => setOverwriteMode(e.target.value)}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <div>
                          <strong style={{ fontSize: '14px' }}>❓ Preguntar por cada uno</strong>
                          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            Decidir caso por caso durante la importación
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Barra de progreso */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '600' }}>
                    {importStatus}
                  </span>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {importProgress}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${importProgress}%`,
                    height: '100%',
                    backgroundColor: importProgress === 100 ? '#10b981' : 'var(--theme-primary, #c4fffc)',
                    transition: 'width 0.3s ease',
                    borderRadius: '4px'
                  }} />
                </div>
              </div>

              {/* Preview de datos */}
              {showPreview && previewData.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{
                    marginBottom: '12px',
                    fontSize: '18px',
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontWeight: '600',
                    color: '#000'
                  }}>
                    📋 Vista previa de los primeros registros:
                  </h3>
                  <div style={{
                    overflowX: 'auto',
                    border: '2px solid #000',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}>
                    <table style={{
                      width: '100%',
                      fontSize: '13px',
                      borderCollapse: 'collapse',
                      fontFamily: '"Space Grotesk", sans-serif'
                    }}>
                      <thead>
                        <tr style={{
                          backgroundColor: '#000',
                          color: 'var(--theme-primary, #c4fffc)'
                        }}>
                          <th style={{
                            padding: '10px',
                            textAlign: 'center',
                            borderRight: '1px solid var(--theme-primary, #c4fffc)',
                            fontWeight: '700'
                          }}>Item</th>
                          <th style={{
                            padding: '10px',
                            textAlign: 'left',
                            borderRight: '1px solid var(--theme-primary, #c4fffc)',
                            fontWeight: '700'
                          }}>Cliente</th>
                          <th style={{
                            padding: '10px',
                            textAlign: 'left',
                            borderRight: '1px solid var(--theme-primary, #c4fffc)',
                            fontWeight: '700'
                          }}>Descripción</th>
                          <th style={{
                            padding: '10px',
                            textAlign: 'right',
                            borderRight: '1px solid var(--theme-primary, #c4fffc)',
                            fontWeight: '700'
                          }}>Cantidad</th>
                          <th style={{
                            padding: '10px',
                            textAlign: 'right',
                            borderRight: '1px solid var(--theme-primary, #c4fffc)',
                            fontWeight: '700'
                          }}>P.U. CLP</th>
                          <th style={{
                            padding: '10px',
                            textAlign: 'right',
                            fontWeight: '700'
                          }}>Total Neto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.map((row, idx) => {
                          const isDuplicate = duplicateItems.includes(row.item);
                          return (
                            <tr key={idx} style={{
                              backgroundColor: isDuplicate ? '#fff3cd' : (idx % 2 === 0 ? '#ffffff' : '#f8f9fa')
                            }}>
                              <td style={{
                                padding: '10px',
                                borderBottom: '1px solid #dee2e6',
                                borderRight: '1px solid #dee2e6',
                                textAlign: 'center',
                                fontWeight: '600'
                              }}>
                                {row.item}
                                {isDuplicate && (
                                  <span style={{
                                    marginLeft: '6px',
                                    padding: '2px 6px',
                                    backgroundColor: '#856404',
                                    color: '#fff',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    fontWeight: '700'
                                  }}>
                                    DUP
                                  </span>
                                )}
                              </td>
                              <td style={{
                                padding: '10px',
                                borderBottom: '1px solid #dee2e6',
                                borderRight: '1px solid #dee2e6'
                              }}>
                                {row.cliente}
                              </td>
                              <td style={{
                                padding: '10px',
                                borderBottom: '1px solid #dee2e6',
                                borderRight: '1px solid #dee2e6',
                                fontSize: '12px'
                              }}>
                                {row.descripcion}
                              </td>
                              <td style={{
                                padding: '10px',
                                textAlign: 'right',
                                borderBottom: '1px solid #dee2e6',
                                borderRight: '1px solid #dee2e6',
                                fontWeight: '600'
                              }}>
                                {row.cantidad}
                              </td>
                              <td style={{
                                padding: '10px',
                                textAlign: 'right',
                                borderBottom: '1px solid #dee2e6',
                                borderRight: '1px solid #dee2e6',
                                fontWeight: '600',
                                color: '#0066cc'
                              }}>
                                ${row.pu_clp?.toLocaleString('es-CL')}
                              </td>
                              <td style={{
                                padding: '10px',
                                textAlign: 'right',
                                borderBottom: '1px solid #dee2e6',
                                fontWeight: '700',
                                color: '#28a745',
                                fontSize: '14px'
                              }}>
                                ${(row.cantidad * row.pu_clp)?.toLocaleString('es-CL')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length === 10 && (
                    <p style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#666',
                      fontStyle: 'italic'
                    }}>
                      * Mostrando solo los primeros 10 registros de la importación
                    </p>
                  )}
                </div>
              )}

              {/* Resultados de la importación */}
              {importResults && (
                <div>
                  {importResults.error ? (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#fee2e2',
                      border: '1px solid #ef4444',
                      borderRadius: '8px',
                      color: '#991b1b'
                    }}>
                      <strong>Error:</strong> {importResults.error}
                    </div>
                  ) : (
                    <>
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#dcfce7',
                        border: '1px solid #10b981',
                        borderRadius: '8px',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{ marginBottom: '8px', color: '#166534' }}>
                          ✅ Importación completada
                        </h3>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                          <li>{importResults.summary?.totalRegistros} registros procesados</li>
                          <li>{importResults.summary?.registrosNuevos} registros nuevos</li>
                          <li>{importResults.summary?.registrosActualizados || 0} registros actualizados</li>
                          {importResults.summary?.nuevosClientes?.length > 0 && (
                            <li>🆕 {importResults.summary.nuevosClientes.length} clientes nuevos detectados</li>
                          )}
                          {importResults.summary?.nuevosProveedores?.length > 0 && (
                            <li>🆕 {importResults.summary.nuevosProveedores.length} proveedores nuevos detectados</li>
                          )}
                        </ul>
                      </div>

                      {/* Debug: mostrar clientes y proveedores disponibles */}
                      {console.log('🔍 Clientes disponibles:', clients)}
                      {console.log('🔍 Proveedores disponibles:', suppliers)}
                      {console.log('🔍 newClientsMap:', newClientsMap)}
                      {console.log('🔍 newSuppliersMap:', newSuppliersMap)}

                      {/* Nuevos clientes detectados */}
                      {importResults.summary?.nuevosClientes?.length > 0 && (
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          marginBottom: '20px'
                        }}>
                          <h4 style={{
                            marginBottom: '12px',
                            color: '#212529',
                            fontSize: '16px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '20px' }}>👥</span>
                            Nuevos clientes detectados
                          </h4>
                          <p style={{ fontSize: '13px', marginBottom: '12px', color: '#6c757d' }}>
                            Estos clientes no están en el catálogo. Seleccione una acción para cada uno:
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {importResults.summary.nuevosClientes.map(client => {
                              // Por defecto, crear nuevo (null significa crear nuevo)
                              const isCreatingNew = newClientsMap[client] === null || newClientsMap[client] === undefined;
                              return (
                                <div key={client} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '12px',
                                  backgroundColor: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6'
                                }}>
                                  <span style={{
                                    fontSize: '16px'
                                  }}>👤</span>
                                  <span style={{
                                    flex: 1,
                                    fontWeight: '500',
                                    fontSize: '14px',
                                    color: '#212529'
                                  }}>{client}</span>

                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: isCreatingNew ? '#28a745' : 'white',
                                        color: isCreatingNew ? 'white' : '#28a745',
                                        border: '1px solid #28a745',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                      onClick={() => {
                                        console.log(`🆕 Marcando cliente "${client}" como CREAR NUEVO`);
                                        const updatedMap = {
                                          ...newClientsMap,
                                          [client]: null
                                        };
                                        console.log('📋 newClientsMap actualizado:', updatedMap);
                                        setNewClientsMap(updatedMap);
                                      }}
                                    >
                                      <span style={{ fontSize: '14px' }}>➕</span>
                                      Crear nuevo
                                    </button>

                                    <span style={{ color: '#6c757d', fontSize: '12px' }}>o</span>

                                    <select
                                      value={newClientsMap[client] || ''}
                                      style={{
                                        padding: '6px 10px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        backgroundColor: newClientsMap[client] && newClientsMap[client] !== null ? '#d4edda' : 'white',
                                        cursor: 'pointer',
                                        minWidth: '180px',
                                        color: '#495057',
                                        fontWeight: '400'
                                      }}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        console.log(`🔗 Mapeando cliente "${client}" a:`, value || 'CREAR NUEVO');
                                        const updatedMap = {
                                          ...newClientsMap,
                                          [client]: value || null // Si no hay valor, volver a crear nuevo
                                        };
                                        console.log('📋 newClientsMap actualizado:', updatedMap);
                                        setNewClientsMap(updatedMap);
                                      }}
                                    >
                                      <option value="">Seleccionar cliente existente...</option>
                                      {clients && clients.length > 0 ? (
                                        clients.map(c => (
                                          <option key={`client-${c.id}`} value={c.id}>
                                            {c.name || 'Sin nombre'}
                                          </option>
                                        ))
                                      ) : (
                                        <option value="" disabled>No hay clientes disponibles</option>
                                      )}
                                    </select>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Nuevos proveedores detectados */}
                      {importResults.summary?.nuevosProveedores?.length > 0 && (
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          marginBottom: '16px',
                          border: '1px solid #dee2e6'
                        }}>
                          <h4 style={{
                            marginBottom: '12px',
                            color: '#212529',
                            fontSize: '16px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '20px' }}>📦</span>
                            Nuevos proveedores detectados
                          </h4>
                          <p style={{ fontSize: '13px', marginBottom: '12px', color: '#6c757d' }}>
                            Estos proveedores no están en el catálogo. Seleccione una acción para cada uno:
                          </p>

                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px'
                          }}>
                            {importResults.summary.nuevosProveedores.map(supplier => {
                              // Por defecto, crear nuevo (null significa crear nuevo)
                              const isCreatingNew = newSuppliersMap[supplier] === null || newSuppliersMap[supplier] === undefined;
                              return (
                                <div key={supplier} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '12px',
                                  backgroundColor: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6'
                                }}>
                                  <span style={{
                                    fontSize: '16px'
                                  }}>🏭</span>
                                  <span style={{
                                    flex: 1,
                                    fontWeight: '500',
                                    fontSize: '14px',
                                    color: '#212529'
                                  }}>{supplier}</span>

                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: isCreatingNew ? '#007bff' : 'white',
                                        color: isCreatingNew ? 'white' : '#007bff',
                                        border: '1px solid #007bff',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                      onClick={() => {
                                        console.log(`🆕 Marcando proveedor "${supplier}" como CREAR NUEVO`);
                                        const updatedMap = {
                                          ...newSuppliersMap,
                                          [supplier]: null
                                        };
                                        console.log('📋 newSuppliersMap actualizado:', updatedMap);
                                        setNewSuppliersMap(updatedMap);
                                      }}
                                    >
                                      <span style={{ fontSize: '14px' }}>➕</span>
                                      Crear nuevo
                                    </button>

                                    <span style={{ color: '#6c757d', fontSize: '12px' }}>o</span>

                                    <select
                                      value={newSuppliersMap[supplier] || ''}
                                      style={{
                                        padding: '6px 10px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        backgroundColor: newSuppliersMap[supplier] && newSuppliersMap[supplier] !== null ? '#cce5ff' : 'white',
                                        cursor: 'pointer',
                                        minWidth: '180px',
                                        color: '#495057',
                                        fontWeight: '400'
                                      }}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        console.log(`🔗 Mapeando proveedor "${supplier}" a:`, value || 'CREAR NUEVO');
                                        const updatedMap = {
                                          ...newSuppliersMap,
                                          [supplier]: value || null // Si no hay valor, volver a crear nuevo
                                        };
                                        console.log('📋 newSuppliersMap actualizado:', updatedMap);
                                        setNewSuppliersMap(updatedMap);
                                      }}
                                    >
                                      <option value="">Seleccionar proveedor existente...</option>
                                      {suppliers && suppliers.length > 0 ? (
                                        suppliers.map(s => (
                                          <option key={`supplier-${s.id}`} value={s.id}>
                                            {s.name || 'Sin nombre'}
                                          </option>
                                        ))
                                      ) : (
                                        <option value="" disabled>No hay proveedores disponibles</option>
                                      )}
                                    </select>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Cambios detectados */}
                      {importResults.cambios?.length > 0 && (
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#f0f9ff',
                          border: '1px solid var(--theme-primary, #c4fffc)',
                          borderRadius: '8px'
                        }}>
                          <h4 style={{ marginBottom: '8px', color: '#1e3a8a' }}>
                            🔄 Registros actualizados
                          </h4>
                          <div style={{ fontSize: '12px' }}>
                            {importResults.cambios.slice(0, 5).map((change, idx) => (
                              <div key={idx} style={{
                                padding: '6px',
                                marginBottom: '4px',
                                backgroundColor: 'white',
                                borderRadius: '4px'
                              }}>
                                <strong>Item {change.item}:</strong>
                                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                  {change.changes.map((c, i) => (
                                    <li key={i}>{c}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                            {importResults.cambios.length > 5 && (
                              <p style={{ color: '#6b7280', marginTop: '8px' }}>
                                ... y {importResults.cambios.length - 5} cambios más
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Mostrar datos que serán sobrescritos */}
              {datosAnteriores && datosAnteriores.length > 0 && importStep === 'preview' && (
                <div style={{
                  marginTop: '20px',
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#fff3cd',
                  border: '2px solid #000',
                  borderRadius: '8px'
                }}>
                  <h3 style={{
                    marginBottom: '12px',
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#000'
                  }}>
                    ⚠️ Datos que serán sobrescritos
                  </h3>
                  <p style={{
                    marginBottom: '12px',
                    fontSize: '14px',
                    color: '#333'
                  }}>
                    Los siguientes items ya existen y serán actualizados con los nuevos valores:
                  </p>
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '12px'
                  }}>
                    {datosAnteriores.map((item, idx) => (
                      <div key={idx} style={{
                        marginBottom: '16px',
                        paddingBottom: '16px',
                        borderBottom: idx < datosAnteriores.length - 1 ? '1px solid #e9ecef' : 'none'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '8px',
                          gap: '8px'
                        }}>
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: '#000',
                            color: '#fff',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '700'
                          }}>
                            Item #{item.item}
                          </span>
                          <span style={{
                            fontSize: '13px',
                            color: '#666'
                          }}>
                            {item.anterior.descripcion?.substring(0, 50)}...
                          </span>
                        </div>
                        <div className="overwrite-comparison-grid" style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                          gap: '12px',
                          fontSize: '13px'
                        }}>
                          <div style={{
                            padding: '8px',
                            backgroundColor: '#fee2e2',
                            borderRadius: '4px',
                            border: '1px solid #dc3545'
                          }}>
                            <strong style={{ color: '#dc3545' }}>Valores actuales:</strong>
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                              <li>Cliente: <strong>{item.anterior.cliente || 'N/A'}</strong></li>
                              <li>Cantidad: <strong>{parseFloat(item.anterior.cantidad || 0) % 1 === 0 ? parseInt(item.anterior.cantidad || 0) : parseFloat(item.anterior.cantidad || 0)}</strong></li>
                              <li>P.U. CLP: <strong>${parseFloat(item.anterior.pu_clp || 0).toLocaleString('es-CL')}</strong></li>
                              <li>Total: <strong>${parseFloat(item.anterior.total_neto || 0).toLocaleString('es-CL')}</strong></li>
                              <li>Proveedor: <strong>{item.anterior.proveedor || 'N/A'}</strong></li>
                            </ul>
                          </div>
                          <div style={{
                            padding: '8px',
                            backgroundColor: '#d1fae5',
                            borderRadius: '4px',
                            border: '1px solid #10b981'
                          }}>
                            <strong style={{ color: '#065f46' }}>Nuevos valores:</strong>
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                              <li>Cliente: <strong>{item.nuevo.cliente || 'N/A'}</strong></li>
                              <li>Cantidad: <strong>{item.nuevo.cantidad}</strong></li>
                              <li>P.U. CLP: <strong>${item.nuevo.pu_clp?.toLocaleString('es-CL')}</strong></li>
                              <li>Total: <strong>${item.nuevo.total_neto?.toLocaleString('es-CL')}</strong></li>
                              <li>Proveedor: <strong>{item.nuevo.proveedor || 'N/A'}</strong></li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                    {datosAnteriores.length > 5 && (
                      <p style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#666',
                        fontStyle: 'italic',
                        textAlign: 'center'
                      }}>
                        * Mostrando solo los primeros 5 items que serán modificados
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer del modal */}
            {importStep === 'preview' && importProgress === 100 && (
              <div className="matriz-modal-footer">
                <div className="footer-info">
                  {duplicateItems.length > 0 && (
                    <span className="status-indicator" style={{
                      backgroundColor: '#fff3cd',
                      border: '1px solid #856404',
                      color: '#856404'
                    }}>
                      ⚠️ {duplicateItems.length} duplicados - Modo: {
                        overwriteMode === 'overwrite' ? 'Sobrescribir' :
                        overwriteMode === 'skip' ? 'Omitir' : 'Preguntar'
                      }
                    </span>
                  )}
                </div>

                <div className="footer-actions">
                  {/* Botón de cancelar */}
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setShowImportModal(false);
                      setImportResults(null);
                      setShowPreview(false);
                      setImportProgress(0);
                      setNewClientsMap({});
                      setNewSuppliersMap({});
                      setDuplicateItems([]);
                      setPendingImportData(null);
                      setImportStep('uploading');
                      setDatosAnteriores([]);
                    }}
                  >
                    ❌ Cancelar
                  </button>

                  {/* Botón de confirmar importación */}
                  <button
                    className="btn-primary"
                    onClick={() => {
                      console.log('🔵 BOTÓN CONFIRMAR IMPORTACIÓN PRESIONADO');
                      console.log('importResults:', importResults);
                      console.log('newClientsMap:', newClientsMap);
                      console.log('newSuppliersMap:', newSuppliersMap);

                      // Validar que todas las entidades tengan una decisión
                      const clientesNoDecididos = importResults?.summary?.nuevosClientes?.filter(
                        client => !newClientsMap.hasOwnProperty(client)
                      );
                      const proveedoresNoDecididos = importResults?.summary?.nuevosProveedores?.filter(
                        supplier => !newSuppliersMap.hasOwnProperty(supplier)
                      );

                      console.log('Clientes no decididos:', clientesNoDecididos);
                      console.log('Proveedores no decididos:', proveedoresNoDecididos);

                      if (clientesNoDecididos?.length > 0) {
                        console.log('⚠️ HAY CLIENTES SIN DECIDIR');
                        alert(`⚠️ Debe seleccionar una acción para los siguientes clientes:\n${clientesNoDecididos.join('\n')}`);
                        return;
                      }

                      if (proveedoresNoDecididos?.length > 0) {
                        console.log('⚠️ HAY PROVEEDORES SIN DECIDIR');
                        alert(`⚠️ Debe seleccionar una acción para los siguientes proveedores:\n${proveedoresNoDecididos.join('\n')}`);
                        return;
                      }

                      console.log('✅ VALIDACIÓN PASADA - LLAMANDO A confirmImport()');
                      // Si todo está ok, confirmar
                      confirmImport();
                    }}
                    disabled={
                      (duplicateItems.length > 0 && !overwriteMode) ||
                      (importResults?.summary?.nuevosClientes?.some(client => !newClientsMap.hasOwnProperty(client))) ||
                      (importResults?.summary?.nuevosProveedores?.some(supplier => !newSuppliersMap.hasOwnProperty(supplier)))
                    }
                    style={{
                      opacity: (
                        (duplicateItems.length > 0 && !overwriteMode) ||
                        importResults?.summary?.nuevosClientes?.some(client => !newClientsMap.hasOwnProperty(client)) ||
                        importResults?.summary?.nuevosProveedores?.some(supplier => !newSuppliersMap.hasOwnProperty(supplier))
                      ) ? 0.5 : 1
                    }}
                    title={
                      duplicateItems.length > 0 && !overwriteMode
                        ? 'Debe elegir qué hacer con los duplicados'
                        : importResults?.summary?.nuevosClientes?.some(client => !newClientsMap.hasOwnProperty(client))
                        ? 'Debe seleccionar una acción para todos los clientes'
                        : importResults?.summary?.nuevosProveedores?.some(supplier => !newSuppliersMap.hasOwnProperty(supplier))
                        ? 'Debe seleccionar una acción para todos los proveedores'
                        : 'Confirmar importación'
                    }
                  >
                    ✅ Confirmar Importación
                  </button>
                </div>
              </div>
            )}

            {/* Footer cuando se completa */}
            {importStep === 'completed' && (
              <div className="matriz-modal-footer" style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '20px',
                borderTop: '2px solid #000',
                backgroundColor: '#d4edda'
              }}>
                <div style={{
                  textAlign: 'center'
                }}>
                  <h3 style={{
                    color: '#155724',
                    fontFamily: '"Space Grotesk", sans-serif',
                    marginBottom: '10px'
                  }}>
                    ✅ Importación Completada Exitosamente
                  </h3>
                  <p style={{ color: '#155724', fontSize: '14px' }}>
                    Cerrando automáticamente...
                  </p>
                </div>
              </div>
            )}

            {/* Footer antiguo para mapeos (oculto por ahora) */}
            {false && importProgress === 100 && (
              <div className="matriz-modal-footer" style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end'
              }}>
                {/* Botón de guardar si hay mapeos pendientes */}
                {(Object.keys(newClientsMap).length > 0 || Object.keys(newSuppliersMap).length > 0) && (
                  <button
                    onClick={async () => {
                      // Aplicar mapeos de clientes y proveedores
                      try {
                        const token = localStorage.getItem('token');
                        let empresaData = localStorage.getItem('empresaActiva');
                        let empresaId = '3';

                        if (empresaData) {
                          try {
                            const parsed = JSON.parse(empresaData);
                            empresaId = String(parsed.id || parsed.empresaId || '3');
                          } catch {
                            if (!isNaN(empresaData)) {
                              empresaId = String(empresaData);
                            }
                          }
                        }

                        const headers = {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                          'x-empresa-id': empresaId,
                          'x-company-id': empresaId
                        };

                        // Separar clientes nuevos de mapeos
                        const newClients = [];
                        const clientMappings = {};

                        for (const [clientName, mappedId] of Object.entries(newClientsMap)) {
                          if (mappedId === null) {
                            newClients.push(clientName);
                          } else {
                            clientMappings[clientName] = mappedId;
                          }
                        }

                        // Separar proveedores nuevos de mapeos
                        const newSuppliers = [];
                        const supplierMappings = {};

                        for (const [supplierName, mappedId] of Object.entries(newSuppliersMap)) {
                          if (mappedId === null) {
                            newSuppliers.push(supplierName);
                          } else {
                            supplierMappings[supplierName] = mappedId;
                          }
                        }

                        // Crear nuevos clientes si hay
                        if (newClients.length > 0) {
                          const clientResponse = await axios.post(
                            '/api/entities/clients/bulk',
                            { clients: newClients },
                            { headers }
                          );
                          console.log('Clientes creados:', clientResponse.data);
                        }

                        // Crear nuevos proveedores si hay
                        if (newSuppliers.length > 0) {
                          const supplierResponse = await axios.post(
                            '/api/entities/suppliers/bulk',
                            { suppliers: newSuppliers },
                            { headers }
                          );
                          console.log('Proveedores creados:', supplierResponse.data);
                        }

                        // Actualizar mapeos si hay
                        if (Object.keys(clientMappings).length > 0 || Object.keys(supplierMappings).length > 0) {
                          const mappingResponse = await axios.put(
                            '/api/entities/mappings/update',
                            {
                              clientMappings,
                              supplierMappings
                            },
                            { headers }
                          );
                          console.log('Mapeos actualizados:', mappingResponse.data);
                        }

                        alert('✅ Entidades creadas y mapeos aplicados correctamente');

                        // Limpiar y cerrar
                        setNewClientsMap({});
                        setNewSuppliersMap({});
                        setShowImportModal(false);
                        setImportResults(null);
                        setShowPreview(false);
                        setImportProgress(0);

                        // Recargar datos y actualizar listas de clientes/proveedores
                        await Promise.all([
                          loadOrdersFromBackend(),
                          loadClientsAndSuppliers()
                        ]);

                      } catch (error) {
                        console.error('Error aplicando mapeos:', error);
                        const errorMsg = error.response?.data?.message || error.message || 'Error desconocido';
                        alert(`❌ Error al aplicar los mapeos: ${errorMsg}`);
                      }
                    }}
                    className="btn btn-success"
                    style={{
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      padding: '8px 20px',
                      borderRadius: '4px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    💾 Guardar y Aplicar Mapeos
                  </button>
                )}

                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportResults(null);
                    setShowPreview(false);
                    setImportProgress(0);
                    setNewClientsMap({});
                    setNewSuppliersMap({});
                  }}
                  className="btn btn-primary"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <Trash2 size={32} color="#ef4444" />
              </div>

              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                margin: '0 0 12px 0',
                color: '#111827'
              }}>
                ¿Eliminar {selectedRows.length} registro(s)?
              </h3>

              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '0 0 24px 0',
                lineHeight: '1.6'
              }}>
                Esta acción <strong>eliminará permanentemente</strong> los registros seleccionados,
                incluyendo todos los <strong>documentos</strong> y <strong>observaciones</strong> asociados a cada fila.
                <br />
                <strong style={{ color: '#ef4444' }}>Esta acción no se puede deshacer.</strong>
              </p>

              <div style={{
                display: 'flex',
                gap: '12px',
                width: '100%'
              }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>

                <button
                  onClick={confirmDelete}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Éxito */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: 'var(--theme-primary, #c4fffc)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '32px',
              color: 'var(--theme-secondary, #000)'
            }}>
              ✓
            </div>

            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--theme-secondary, #000)',
              marginBottom: '12px',
              fontFamily: "'Space Grotesk', sans-serif"
            }}>
              {successMessage}
            </h3>

            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '24px'
            }}>
              Los cambios se han guardado correctamente
            </p>

            <button
              onClick={() => setShowSuccessModal(false)}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: 'var(--theme-secondary, #000)',
                color: 'var(--theme-primary, #c4fffc)',
                border: '2px solid var(--theme-secondary, #000)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: "'Space Grotesk', sans-serif"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--theme-primary, #c4fffc)';
                e.target.style.color = 'var(--theme-secondary, #000)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--theme-secondary, #000)';
                e.target.style.color = 'var(--theme-primary, #c4fffc)';
              }}
            >
              Aceptar
            </button>
          </div>
        </div>
      )}

      {/* Popup estilo cómic para badges de colaboradores */}
      {badgePopup && (
        <>
          {/* Overlay para cerrar al hacer clic fuera */}
          <div
            onClick={() => setBadgePopup(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998
            }}
          />

          {/* Popup */}
          <div
            ref={(el) => {
              if (el) {
                const rect = el.getBoundingClientRect();
                const vw = window.innerWidth;
                // Ajustar si se sale por la izquierda o derecha
                if (rect.left < 8) {
                  el.style.left = `${8 + rect.width / 2}px`;
                } else if (rect.right > vw - 8) {
                  el.style.left = `${vw - 8 - rect.width / 2}px`;
                }
              }
            }}
            style={{
              position: 'fixed',
              left: `${badgePopup.position.x}px`,
              top: `${badgePopup.position.y}px`,
              transform: 'translateX(-50%)',
              background: 'white',
              border: '3px solid var(--theme-secondary, #000)',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              zIndex: 9999,
              minWidth: '280px',
              maxWidth: '320px',
              fontFamily: "'Space Grotesk', sans-serif"
            }}
          >
            {/* Flecha apuntando arriba */}
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '12px solid transparent',
              borderRight: '12px solid transparent',
              borderBottom: '12px solid var(--theme-secondary, #000)'
            }} />
            <div style={{
              position: 'absolute',
              top: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderBottom: '10px solid white'
            }} />

            {/* Contenido del popup */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              {/* Avatar del usuario */}
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: badgePopup.isEditor ? 'var(--theme-secondary, #000)' : 'var(--theme-primary, #c4fffc)',
                border: badgePopup.isEditor ? '2px solid white' : '2px solid var(--theme-secondary, #000)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '700',
                color: badgePopup.isEditor ? 'var(--theme-primary, #c4fffc)' : 'var(--theme-secondary, #000)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                {badgePopup.user.initials}
              </div>

              {/* Nombre y tipo */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: 'var(--theme-secondary, #000)',
                  marginBottom: '4px'
                }}>
                  {badgePopup.user.name}
                </div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: badgePopup.isEditor ? '#10b981' : '#3b82f6',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {badgePopup.isEditor ? '✏️ Editor' : '📥 Importador'}
                </div>
              </div>
            </div>

            {/* Información adicional si es editor */}
            {badgePopup.isEditor && badgePopup.editorData && (
              <div style={{
                background: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  marginBottom: '8px',
                  letterSpacing: '0.5px'
                }}>
                  Última edición
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#374151',
                  marginBottom: '8px',
                  lineHeight: '1.5'
                }}>
                  <strong>{badgePopup.editorData.oldValue || '(vacío)'}</strong>
                  <span style={{ margin: '0 6px', color: '#10b981' }}>→</span>
                  <strong>{badgePopup.editorData.newValue || '(vacío)'}</strong>
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#9ca3af'
                }}>
                  {new Date(badgePopup.editorData.lastChange).toLocaleString('es-CL', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            )}

            {/* Información si es importador */}
            {!badgePopup.isEditor && (
              <div style={{
                background: '#f0f9ff',
                border: '2px solid #bfdbfe',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#374151',
                lineHeight: '1.5'
              }}>
                Este usuario importó el Item {badgePopup.itemNumber} a la matriz.
              </div>
            )}

            {/* Botón para ir a Equipo */}
            <button
              onClick={() => {
                window.location.href = '/equipo';
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'var(--theme-secondary, #000)',
                color: 'var(--theme-primary, #c4fffc)',
                border: '2px solid var(--theme-secondary, #000)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: "'Space Grotesk', sans-serif",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--theme-primary, #c4fffc)';
                e.target.style.color = 'var(--theme-secondary, #000)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'var(--theme-secondary, #000)';
                e.target.style.color = 'var(--theme-primary, #c4fffc)';
              }}
            >
              <span>👥</span>
              Ver en Equipo
            </button>
          </div>
        </>
      )}

      {/* Document Panel */}
      <DocumentPanel
        isOpen={docPanelItem !== null}
        onClose={() => setDocPanelItem(null)}
        itemNumber={docPanelItem}
        itemData={docPanelItem ? (() => {
          const row = data.find(r => r.item === docPanelItem);
          if (!row) return null;
          return {
            ...row,
            cliente: getClienteNombre(row.cliente),
            proveedor: getProveedorNombre(row.proveedor)
          };
        })() : null}
        onDocumentChange={loadDocumentCounts}
      />

      {/* Popover de documentos en celda del maestro */}
      {cellDocPopover && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 10000
          }}
          onClick={() => setCellDocPopover(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: `${Math.min(cellDocPopover.position.y, window.innerHeight - 320)}px`,
              left: `${Math.min(cellDocPopover.position.x, window.innerWidth - 320)}px`,
              width: '300px',
              maxHeight: '300px',
              background: 'white',
              borderRadius: '12px',
              border: '2px solid var(--theme-secondary, #000)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
              fontFamily: "'Space Grotesk', sans-serif",
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '10px 14px',
              background: 'var(--theme-secondary, #000)',
              color: 'var(--theme-primary, #c4fffc)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ fontSize: '12px', fontWeight: 700 }}>
                {DOC_TYPE_LABELS_SHORT[cellDocPopover.docType]} - Item #{cellDocPopover.itemNumber}
              </span>
              <button
                onClick={() => setCellDocPopover(null)}
                style={{
                  background: 'none', border: 'none', color: 'var(--theme-primary, #c4fffc)',
                  cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '0 2px'
                }}
              >×</button>
            </div>

            {/* Descripción del ítem */}
            {cellDocPopover.row?.descripcion && (
              <div style={{
                padding: '6px 14px', fontSize: '11px', color: '#6b7280',
                borderBottom: '1px solid #f1f5f9', background: '#fafafa'
              }}>
                {cellDocPopover.row.descripcion}
                {cellDocPopover.row.cliente && <span style={{ marginLeft: '6px', fontWeight: 600 }}>· {getClienteNombre(cellDocPopover.row.cliente)}</span>}
              </div>
            )}

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
              {cellDocLoading ? (
                <div style={{ textAlign: 'center', padding: '16px', color: '#9ca3af', fontSize: '12px' }}>
                  Cargando...
                </div>
              ) : cellDocPopover.docs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '16px', color: '#9ca3af', fontSize: '12px' }}>
                  No hay documentos adjuntos
                </div>
              ) : (
                cellDocPopover.docs.map(doc => (
                  <div key={doc.id} style={{
                    padding: '8px', borderRadius: '8px', marginBottom: '6px',
                    background: '#f9fafb', border: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      fontSize: '11px', fontWeight: 600, color: '#1f2937',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      marginBottom: '2px'
                    }}>
                      {doc.original_name}
                    </div>
                    <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '6px' }}>
                      {doc.file_size ? (doc.file_size < 1024*1024 ? `${(doc.file_size/1024).toFixed(0)} KB` : `${(doc.file_size/(1024*1024)).toFixed(1)} MB`) : ''}
                      {doc.doc_reference ? ` · Ref: ${doc.doc_reference}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          flex: 1, padding: '4px 0',
                          background: '#eff6ff', border: '1px solid #bfdbfe',
                          borderRadius: '5px', textDecoration: 'none',
                          fontSize: '10px', fontWeight: 700, color: '#2563eb',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                          fontFamily: "'Space Grotesk', sans-serif"
                        }}
                      >↗ Ver</a>
                      {/* Reemplazar solo para tipos single-doc */}
                      {!MULTI_DOC_TYPES.has(cellDocPopover.docType) && (
                        <button
                          onClick={() => {

                            setFieldUploadDocType(cellDocPopover.docType);
                            setFieldUploadContext({
                              itemNumber: cellDocPopover.itemNumber,
                              row: cellDocPopover.row,
                              replaceDocIds: cellDocPopover.docs.map(d => d.id)
                            });
                            setShowFieldUpload(true);
                            setCellDocPopover(null);
                          }}
                          style={{
                            flex: 1, padding: '4px 0', borderRadius: '5px',
                            background: '#fef9c3', border: '1px solid #fde68a',
                            cursor: 'pointer', fontSize: '10px', fontWeight: 700,
                            color: '#92400e', fontFamily: "'Space Grotesk', sans-serif",
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px'
                          }}
                        >✎ Reemplazar</button>
                      )}
                      <button
                        onClick={() => handleCellDocDelete(doc.id)}
                        style={{
                          padding: '4px 8px', borderRadius: '5px',
                          background: '#fef2f2', border: '1px solid #fecaca',
                          cursor: 'pointer', fontSize: '10px', fontWeight: 700,
                          color: '#dc2626', fontFamily: "'Space Grotesk', sans-serif"
                        }}
                      >✕</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '8px', borderTop: '1px solid #e5e7eb' }}>
              {cellDocPopover.docs.length > 0 ? (
                <div style={{ display: 'flex', gap: '4px' }}>
                  {/* Para multi-doc: botón "Agregar otro" */}
                  {MULTI_DOC_TYPES.has(cellDocPopover.docType) && (
                    <button
                      onClick={() => {
    
                        setFieldUploadDocType(cellDocPopover.docType);
                        setFieldUploadContext({ itemNumber: cellDocPopover.itemNumber, row: cellDocPopover.row });
                        setShowFieldUpload(true);
                        setCellDocPopover(null);
                      }}
                      style={{
                        flex: 1, padding: '7px',
                        background: 'var(--theme-secondary, #000)',
                        color: 'var(--theme-primary, #c4fffc)',
                        border: '1.5px solid var(--theme-secondary, #000)',
                        borderRadius: '6px',
                        fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                        fontFamily: "'Space Grotesk', sans-serif",
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                      }}
                    >
                      <Upload size={10} /> Agregar otro
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setCellDocPopover(null);
                      navigate('/documentos');
                    }}
                    style={{
                      flex: 1, padding: '7px',
                      background: 'var(--theme-primary, #c4fffc)',
                      color: 'var(--theme-secondary, #000)',
                      border: '1.5px solid var(--theme-secondary, #000)',
                      borderRadius: '6px',
                      fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                      fontFamily: "'Space Grotesk', sans-serif",
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                    }}
                  >
                    Ver en Documentos
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {

                    setFieldUploadDocType(cellDocPopover.docType);
                    setFieldUploadContext({ itemNumber: cellDocPopover.itemNumber, row: cellDocPopover.row });
                    setShowFieldUpload(true);
                    setCellDocPopover(null);
                  }}
                  style={{
                    width: '100%', padding: '7px',
                    background: 'var(--theme-secondary, #000)',
                    color: 'var(--theme-primary, #c4fffc)',
                    border: 'none', borderRadius: '6px',
                    fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'Space Grotesk', sans-serif",
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'
                  }}
                >
                  <Upload size={12} /> Subir documento
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Upload modal desde celda del maestro o desde el modal de edición */}
      {showFieldUpload && fieldUploadContext && (
        <DocumentUploadModal
          isOpen={showFieldUpload}
          onClose={() => { setShowFieldUpload(false); setFieldUploadContext(null); }}
          itemNumber={fieldUploadContext.itemNumber}
          itemData={{
            ...fieldUploadContext.row,
            cliente: getClienteNombre(fieldUploadContext.row?.cliente),
            proveedor: getProveedorNombre(fieldUploadContext.row?.proveedor)
          }}
          onUploadSuccess={(result) => {
            handleCellDocUploadSuccess(result);
            // Si el modal está abierto, recargar docs del modal
            if (showModal && fieldUploadContext?.itemNumber) {
              loadModalItemDocs(fieldUploadContext.itemNumber);
            }
          }}
          defaultDocType={fieldUploadDocType}
        />
      )}

      {/* Panel de observaciones */}
      <ObservationsPanel
        isOpen={!!obsPanel}
        onClose={() => { setObsPanel(null); loadObservationCounts(); }}
        itemNumber={obsPanel?.itemNumber}
        itemData={obsPanel?.itemData}
      />

      {/* Modal de confirmación para cambios sin guardar */}
      {pendingNavigation && (() => {
        const tp = localStorage.getItem('theme-primary-color') || '#c4fffc';
        const ts = localStorage.getItem('theme-secondary-color') || '#000000';
        return ReactDOM.createPortal(
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 99999
          }}>
            <div style={{
              background: ts, borderRadius: '16px', padding: '32px',
              maxWidth: '440px', width: '90%', color: tp,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)', textAlign: 'center',
              border: `3px solid ${tp}`
            }}>
              <div style={{ marginBottom: '16px' }}><AlertTriangle size={48} color={tp} /></div>
              <h3 style={{ margin: '0 0 12px', fontSize: '20px', fontWeight: 600, color: tp }}>
                Cambios sin guardar
              </h3>
              <p style={{ margin: '0 0 24px', color: tp, opacity: 0.7, fontSize: '14px', lineHeight: '1.5' }}>
                Tienes cambios que no han sido guardados. Si sales ahora, perderás toda la información ingresada.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={() => setPendingNavigation(null)}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: `2px solid ${tp}`,
                    background: 'transparent', color: tp, cursor: 'pointer',
                    fontSize: '14px', fontWeight: 600
                  }}
                >
                  Seguir editando
                </button>
                <button
                  onClick={async () => {
                    // Clean up documents uploaded during this session that weren't saved
                    if (pendingDocUploadsRef.current.length > 0) {
                      try {
                        await api.post('/documents/bulk-delete', { ids: pendingDocUploadsRef.current });
                      } catch (e) {
                        console.warn('Error cleaning pending docs:', e);
                      }
                      pendingDocUploadsRef.current = [];
                    }
                    const dest = pendingNavigation;
                    setPendingNavigation(null);
                    if (dest === '__back__') {
                      window.history.back();
                    } else {
                      navigate(dest);
                    }
                  }}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: `2px solid ${tp}`,
                    background: tp, color: ts, cursor: 'pointer',
                    fontSize: '14px', fontWeight: 600
                  }}
                >
                  Salir sin guardar
                </button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default MatrizExcelPaperBoatFinal;